import { listLegalEntities } from '@/services/payrollSettingsService';
// Performance Management — horizontal-tab module (Overview / Goals / Reviews /
// Achievements / Team Reviews / Analytics). Role-aware: Team Reviews for managers+,
// Analytics for HR/exec. Talks to performance-service via the gateway.
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Target, Loader2, Plus, Trophy, Sparkles, Trash2, ChevronUp, ChevronDown, Archive, ArchiveRestore } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { DataState } from '@/features/shared/components/ui/DataState';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { DateTimeField } from '@/features/shared/components/DateTimeField';
import { Badge } from '@/features/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/features/shared/components/ui/tabs';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/features/shared/components/ui/dialog';
import { Label } from '@/features/shared/components/ui/label';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { useAppSelector } from '@/app/hooks';
// Import directly from the slice (not the '@/features/auth' barrel, which pulls a
// broken utils re-export into the build graph).
import { selectTenant } from '@/features/auth/state/tenant-state/tenantSlice';
import { openDana } from '@/lib/dana';
import { getDepartments } from '@/lib/hrApi';
import { getCompensation } from '@/lib/compensationApi';
import { formatMoney } from '@/lib/money';
import * as api from '@/services/performanceService';
import RecognitionFeed from './RecognitionFeed';
import * as workforce from '@/services/workforceService';
import { getApiErrorMessage } from '@/lib/apiError';
import { useHasPermission } from '@/rbac/useRbac';
import { listCourses, listSessions } from '@/services/learningService';
import type { Course, Session as LearningSession } from '@/services/learningService';

// Format a numeric goal value, as currency when the goal measures money.
const goalValue = (g: api.Goal, v?: number | null): string => {
  if (v == null) return '—';
  return g.measurement_type === 'currency' ? formatMoney(v) : String(v);
};

const STATUS: Record<string, string> = {
  not_started: 'bg-slate-100 text-slate-600 dark:bg-slate-500/20 dark:text-slate-300',
  on_track: 'bg-success-soft text-success-strong',
  at_risk: 'bg-warning-soft text-warning-strong',
  completed: 'bg-info-soft text-info-strong',
  paused: 'bg-slate-100 text-slate-500 dark:bg-slate-500/20 dark:text-slate-400',
  active: 'bg-success-soft text-success-strong',
  finalized: 'bg-info-soft text-info-strong',
  submitted: 'bg-success-soft text-success-strong',
};
const chip = (s?: string) => STATUS[s || ''] || 'bg-slate-100 text-slate-600 dark:bg-slate-500/20 dark:text-slate-300';
const human = (s?: string) => (s || '').replace(/_/g, ' ');
const fmtDate = (d?: string | null) => (d ? new Date(d + (d.length <= 10 ? 'T00:00:00' : '')).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

const Progress: React.FC<{ value: number }> = ({ value }) => (
  <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
    <div className="h-full rounded-full bg-orange-500 transition-all" style={{ width: `${Math.min(100, Math.max(0, value))}%` }} />
  </div>
);

// OverachievementBar: a progress bar means "progress toward done", so the bar FILLS to
// full when the target is met (turns emerald when met/exceeded, orange while in
// progress) — meeting target always looks complete. Overachievement is never lost:
// it's shown as a distinct "+28%" above-target badge rather than by shrinking the base.
const OverachievementBar: React.FC<{ goal: api.Goal }> = ({ goal }) => {
  const raw = Math.max(0, goal.raw_attainment ?? goal.progress ?? 0);
  const met = raw >= 100;
  const over = Math.max(0, Math.round(raw - 100));
  return (
    <div className="flex items-center gap-2">
      <div className="relative h-2.5 flex-1 rounded-full bg-muted overflow-hidden" role="progressbar"
        aria-valuenow={Math.round(raw)} aria-valuemin={0} aria-valuemax={100} aria-label="Goal progress toward target">
        <div className={`absolute inset-y-0 left-0 rounded-full transition-all ${met ? 'bg-success' : 'bg-orange-500'}`} style={{ width: `${Math.min(100, raw)}%` }} />
      </div>
      {over > 0 && (
        <span className="shrink-0 text-[10px] font-semibold text-success tabular-nums" aria-label={`${over}% above target`}>+{over}%</span>
      )}
    </div>
  );
};

// attainmentLabel renders the human overachievement string ("135% achieved · +35% above target").
const attainmentLabel = (g: api.Goal, t: (k: string, d?: string) => string): string => {
  const raw = g.raw_attainment ?? g.progress ?? 0;
  // Money goals show the currency-formatted actual/target alongside the %.
  const money = g.measurement_type === 'currency' && g.actual != null
    ? `${goalValue(g, g.actual)}${g.target != null ? ` / ${goalValue(g, g.target)}` : ''} · `
    : '';
  if (raw > 100) return `${money}${raw}% ${t('achieved', 'achieved')} · +${raw - 100}% ${t('above_target', 'above target')}`;
  return `${money}${raw}% ${t('complete', 'complete')}`;
};

const AskDana: React.FC<{ q: string }> = ({ q }) => (
  <button type="button" onClick={() => openDana(q)} className="inline-flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700">
    <Sparkles className="w-3.5 h-3.5" /> Ask Dana
  </button>
);

// persona resolves the caller's primary lens + a plain-language summary of what they
// can do here, so the header can always answer "who am I in this module?".
const usePersona = () => {
  const { isManager, isHrManager, isTenantOwner, isFinance, isEmployee } = useRoleAccess();
  const isHR = isHrManager || isTenantOwner;
  const canManage = isManager || isHR || isFinance;
  let label = 'Employee', can = 'Set goals, run your self-review, and give feedback.';
  if (isTenantOwner) { label = 'Company Owner'; can = 'Full access — your team, cycles, calibration, and compensation.'; }
  else if (isHrManager) { label = 'HR Admin'; can = 'Run review cycles, calibrate, manage compensation and improvement plans.'; }
  else if (isFinance) { label = 'Finance'; can = 'Review compensation recommendations and company-wide analytics.'; }
  else if (isManager) { label = 'Manager'; can = 'Review your team, plus your own goals and reviews.'; }
  return { label, can, isHR, isManager, isFinance, isEmployee, canManage, showComp: isHR || isFinance, showAnalytics: isHR || isFinance };
};

const useLegacyOverview = () => {
  try { return typeof window !== 'undefined' && window.localStorage.getItem('kp_perf_legacy') === '1'; } catch { return false; }
};

export const PerformancePage: React.FC = () => {
  const { t } = useTranslation('performance');
  const p = usePersona();
  const legacy = useLegacyOverview();
  const [zone, setZone] = useState<'me' | 'manage'>('me');
  const [myTab, setMyTab] = useState('overview');
  const [manageTab, setManageTab] = useState(p.isManager ? 'team' : 'compensation');
  // Deep-link callback the overview uses to route its CTAs to the right place.
  const nav: Nav = (z, tab) => { setZone(z); if (z === 'me') setMyTab(tab); else setManageTab(tab); if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' }); };
  // Open a specific "me" tab from a deep link, e.g. /app/performance?tab=recognition.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) { setZone('me'); setMyTab(tab); }
  }, [searchParams]);

  return (
    <div className="space-y-5">
      {/* Role-context header: who you are + what you can do here */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-orange-100 dark:bg-orange-500/20 p-2"><Target className="w-5 h-5 text-orange-600" /></div>
          <div>
            <h1 className="text-2xl font-bold text-charcoal flex items-center gap-2 flex-wrap">
              {t('title', 'Performance')}
              <span className="text-[11px] font-semibold rounded-full bg-charcoal/90 text-white px-2 py-0.5">{t('viewing_as', 'Viewing as')}: {t(`persona_${p.label.replace(/ /g, '_').toLowerCase()}`, p.label)}</span>
            </h1>
            <p className="text-muted-foreground text-sm mt-0.5">{p.can}</p>
          </div>
        </div>
        {p.canManage && (
          <div className="inline-flex rounded-lg border border-border bg-background p-0.5 text-sm" role="tablist" aria-label={t('zone_switch', 'Performance area')}>
            <button role="tab" aria-selected={zone === 'me'} onClick={() => setZone('me')} className={`px-3 py-1.5 rounded-md font-medium transition ${zone === 'me' ? 'bg-orange-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}>{t('zone_me', 'My performance')}</button>
            <button role="tab" aria-selected={zone === 'manage'} onClick={() => setZone('manage')} className={`px-3 py-1.5 rounded-md font-medium transition ${zone === 'manage' ? 'bg-orange-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}>{t('zone_manage', 'Manage')}</button>
          </div>
        )}
      </div>

      {/* MY PERFORMANCE — personal self-service */}
      {zone === 'me' && (
        <Tabs value={myTab} onValueChange={setMyTab}>
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="overview">{t('tab_overview', 'Overview')}</TabsTrigger>
            <TabsTrigger value="goals">{t('tab_goals', 'Goals')}</TabsTrigger>
            <TabsTrigger value="reviews">{t('tab_reviews', 'Reviews')}</TabsTrigger>
            <TabsTrigger value="oneonones">{t('tab_1on1', '1:1s')}</TabsTrigger>
            <TabsTrigger value="feedback">{t('tab_feedback', 'Feedback')}</TabsTrigger>
            <TabsTrigger value="achievements">{t('tab_achievements', 'Achievements')}</TabsTrigger>
            <TabsTrigger value="recognition">{t('tab_recognition', 'Recognition')}</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-4">{legacy ? <LegacyOverviewTab /> : <OverviewTab nav={nav} persona={p} />}</TabsContent>
          <TabsContent value="goals" className="mt-4"><GoalsTab /></TabsContent>
          <TabsContent value="reviews" className="mt-4"><ReviewsTab /></TabsContent>
          <TabsContent value="oneonones" className="mt-4"><OneOnOnesTab /></TabsContent>
          <TabsContent value="feedback" className="mt-4"><FeedbackTab /></TabsContent>
          <TabsContent value="achievements" className="mt-4"><AchievementsTab /></TabsContent>
          <TabsContent value="recognition" className="mt-4"><RecognitionTab /></TabsContent>
        </Tabs>
      )}

      {/* MANAGE — team + company administration (role-gated) */}
      {p.canManage && zone === 'manage' && (
        <Tabs value={manageTab} onValueChange={setManageTab}>
          <TabsList className="flex-wrap h-auto">
            {p.isManager && <TabsTrigger value="team">{t('tab_team', 'Team Reviews')}</TabsTrigger>}
            {p.showComp && <TabsTrigger value="compensation">{t('tab_compensation', 'Compensation')}</TabsTrigger>}
            {p.showAnalytics && <TabsTrigger value="analytics">{t('tab_analytics', 'Analytics')}</TabsTrigger>}
            {p.isHR && <TabsTrigger value="admin">{t('tab_admin', 'Admin')}</TabsTrigger>}
          </TabsList>
          {p.isManager && <TabsContent value="team" className="mt-4"><TeamReviewsTab isHR={p.isHR} /></TabsContent>}
          {p.showComp && <TabsContent value="compensation" className="mt-4"><CompensationTab /></TabsContent>}
          {p.showAnalytics && <TabsContent value="analytics" className="mt-4"><AnalyticsTab /></TabsContent>}
          {p.isHR && <TabsContent value="admin" className="mt-4"><AdminTab /></TabsContent>}
        </Tabs>
      )}
    </div>
  );
};

// ---- Overview ---------------------------------------------------------------
const StatCard: React.FC<{ label: string; value: React.ReactNode; sub?: string }> = ({ label, value, sub }) => (
  <Card><CardContent className="pt-5">
    <div className="text-2xl font-bold text-foreground">{value}</div>
    <div className="text-xs text-muted-foreground mt-0.5">{label}{sub ? ` · ${sub}` : ''}</div>
  </CardContent></Card>
);

const LegacyOverviewTab: React.FC = () => {
  const { t } = useTranslation('performance');
  const [goals, setGoals] = useState<api.Goal[]>([]);
  const [ach, setAch] = useState<api.Achievement[]>([]);
  const [cycles, setCycles] = useState<api.Cycle[]>([]);
  const [reviews, setReviews] = useState<api.Review[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.listGoals().catch(() => []), api.listAchievements().catch(() => []),
      api.listCycles().catch(() => []), api.listMyReviews().catch(() => []),
    ]).then(([g, a, c, r]) => { setGoals(g); setAch(a); setCycles(c); setReviews(r); }).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;
  const active = goals.filter((g) => g.status !== 'completed' && g.status !== 'archived');
  const avg = goals.length ? Math.round(goals.reduce((s, g) => s + g.progress, 0) / goals.length) : 0;
  const cycle = cycles.find((c) => c.status === 'active');
  const myReview = reviews[0];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label={t('ov_current_cycle', 'Current cycle')} value={cycle ? cycle.name : '—'} />
        <StatCard label={t('ov_goal_progress', 'Goal progress')} value={`${avg}%`} sub={`${active.length} active`} />
        <StatCard label={t('ov_achievements', 'Achievements logged')} value={ach.length} />
        <StatCard label={t('ov_review_status', 'Review status')} value={<span className="capitalize text-base">{myReview ? human(myReview.status) : '—'}</span>} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Card>
          <CardHeader><CardTitle className="text-base">{t('ov_active_goals', 'My active goals')}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {active.length === 0 ? <Empty text={t('ov_no_goals', 'No active goals yet.')} /> : active.slice(0, 5).map((g) => (
              <div key={g.id}>
                <div className="flex items-center justify-between text-sm"><span className="font-medium truncate">{g.title}</span><span className="text-muted-foreground">{g.progress}%</span></div>
                <Progress value={g.progress} />
              </div>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">{t('ov_recent_achievements', 'Recent achievements')}</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {ach.length === 0 ? <Empty text={t('ov_no_achievements', 'No achievements logged yet.')} /> : ach.slice(0, 5).map((a) => (
              <div key={a.id} className="text-sm"><span className="font-medium">{a.title}</span>{a.impact ? <span className="text-muted-foreground"> — {a.impact}</span> : null}</div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ============================================================================
// Redesigned, role-aware Performance homepage. Answers "what cycle am I in, what
// stage, what's next, what's due, how are my goals, what needs me?" at a glance.
// Everything below maps to REAL backend state — nothing is fabricated.
// ============================================================================

type Nav = (zone: 'me' | 'manage', tab: string) => void;

const daysUntil = (d?: string | null): number | null => {
  if (!d) return null;
  const ms = new Date(d + (d.length <= 10 ? 'T00:00:00' : '')).getTime() - Date.now();
  return Math.ceil(ms / 86400000);
};

// goalBucket classifies a goal for prioritisation + filtering (real fields only).
type Bucket = 'overdue' | 'at_risk' | 'exceeded' | 'completed' | 'on_track' | 'not_started';
const goalBucket = (g: api.Goal): Bucket => {
  const raw = g.raw_attainment ?? g.progress ?? 0;
  const du = daysUntil(g.due_date);
  if (g.status === 'completed') return raw > 100 ? 'exceeded' : 'completed';
  if (raw > 100) return 'exceeded';
  if (g.status === 'at_risk') return 'at_risk';
  if (du != null && du < 0) return 'overdue';
  if (g.status === 'not_started' || raw === 0) return 'not_started';
  return 'on_track';
};
const BUCKET_META: Record<Bucket, { label: string; dot: string; chip: string }> = {
  overdue: { label: 'Overdue', dot: 'bg-danger', chip: 'bg-danger-soft text-danger-strong' },
  at_risk: { label: 'At risk', dot: 'bg-warning', chip: 'bg-warning-soft text-warning-strong' },
  exceeded: { label: 'Exceeded', dot: 'bg-success', chip: 'bg-success-soft text-success-strong' },
  completed: { label: 'Achieved', dot: 'bg-info', chip: 'bg-info-soft text-info-strong' },
  on_track: { label: 'On track', dot: 'bg-orange-500', chip: 'bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300' },
  not_started: { label: 'Not started', dot: 'bg-slate-400', chip: 'bg-slate-100 text-slate-600 dark:bg-slate-500/20 dark:text-slate-300' },
};
const isStale = (g: api.Goal) => { const d = daysUntil(g.updated_at); return d != null && d < -14; };

// --- workflow view-model -----------------------------------------------------
interface HomeData {
  loading: boolean;
  cycle?: api.Cycle;
  myReview?: api.Review;
  goals: api.Goal[];
  activeGoals: api.Goal[];
  weights?: api.GoalWeightSummary;
  achievements: api.Achievement[];
  feedbackInbox: api.FeedbackRequest[];
  aboutMe?: api.FeedbackAboutMe;
  teamReviews: api.Review[];
  trend: api.CycleTrend[];
  oneOnOnes: api.OneOnOne[];
  reload: () => void;
}

const useHomeData = (persona: ReturnType<typeof usePersona>): HomeData => {
  const [d, setD] = useState<Omit<HomeData, 'loading' | 'reload'>>({ goals: [], activeGoals: [], achievements: [], feedbackInbox: [], teamReviews: [], trend: [], oneOnOnes: [] });
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    Promise.all([
      api.listGoals().catch(() => []),
      api.listMyReviews().catch(() => []),
      api.listCycles().catch(() => []),
      api.listAchievements().catch(() => []),
      api.getFeedbackInbox().catch(() => []),
      api.getFeedbackAboutMe().catch(() => undefined),
      api.getGoalWeights().catch(() => undefined),
      persona.isManager ? api.listTeamReviews().catch(() => []) : Promise.resolve([]),
      persona.showAnalytics ? api.getPerformanceOverview().catch(() => []) : Promise.resolve([]),
      api.listOneOnOnes().catch(() => []),
    ]).then(([goals, reviews, cycles, ach, inbox, about, weights, team, trend, ones]) => {
      const cycle = (cycles as api.Cycle[]).find((c) => c.status === 'active') || (cycles as api.Cycle[])[0];
      const activeGoals = (goals as api.Goal[]).filter((g) => g.status !== 'completed' && g.status !== 'archived');
      setD({
        cycle, myReview: (reviews as api.Review[])[0], goals: goals as api.Goal[], activeGoals,
        weights: weights as any, achievements: ach as api.Achievement[], feedbackInbox: (inbox as api.FeedbackRequest[]).filter((r) => r.status === 'pending'),
        aboutMe: about as any, teamReviews: team as api.Review[], trend: trend as api.CycleTrend[], oneOnOnes: ones as api.OneOnOne[],
      });
    }).finally(() => setLoading(false));
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps
  return { ...d, loading, reload: load };
};

// Journey stages, derived from real review + goal state.
type StageState = 'complete' | 'active' | 'upcoming' | 'overdue' | 'locked';
interface Step { key: string; label: string; state: StageState }
const deriveJourney = (d: HomeData): Step[] => {
  const r = d.myReview;
  const hasGoals = d.goals.length > 0;
  const weightsOk = d.weights?.valid ?? false;
  const selfDone = r?.self_review_status === 'submitted';
  const mgrDone = r?.manager_review_status === 'submitted';
  const finalized = r?.status === 'finalized';
  const released = !!r?.released_at;
  const selfDue = daysUntil(d.cycle?.self_review_due_date);
  const st = (b: boolean, active: boolean, overdue = false): StageState => b ? 'complete' : overdue ? 'overdue' : active ? 'active' : 'upcoming';
  return [
    { key: 'goals', label: 'Goal setting', state: st(hasGoals && weightsOk, hasGoals ? false : true) },
    { key: 'checkins', label: 'Check-ins', state: !selfDone && hasGoals ? 'active' : selfDone ? 'complete' : 'upcoming' },
    { key: 'self', label: 'Self review', state: st(selfDone, !selfDone && (hasGoals), !selfDone && selfDue != null && selfDue < 0) },
    { key: 'manager', label: 'Manager review', state: mgrDone ? 'complete' : selfDone ? 'active' : 'locked' },
    { key: 'calibration', label: 'Calibration', state: finalized ? 'complete' : mgrDone ? 'active' : 'locked' },
    { key: 'outcome', label: 'Final outcome', state: released ? 'complete' : finalized ? 'active' : 'locked' },
  ];
};

interface NextAction { title: string; detail?: string; requirements?: string[]; cta?: { label: string; go: () => void }; tone: 'urgent' | 'todo' | 'done' }
const deriveNextAction = (d: HomeData, persona: ReturnType<typeof usePersona>, nav: Nav): NextAction => {
  const r = d.myReview;
  const selfDue = daysUntil(d.cycle?.self_review_due_date);
  // Manager: team assessments awaiting you take priority.
  if (persona.isManager) {
    const awaiting = d.teamReviews.filter((x) => x.self_review_status === 'submitted' && x.manager_review_status !== 'submitted').length;
    if (awaiting > 0) return { title: `${awaiting} team review${awaiting > 1 ? 's' : ''} awaiting your assessment`, detail: 'Complete the manager review for your direct reports.', tone: 'urgent', cta: { label: 'Review your team', go: () => nav('manage', 'team') } };
  }
  if (r?.released_at && !r?.acknowledged_at) return { title: 'Acknowledge your final review', detail: 'Your manager has released your review. Read it and confirm.', tone: 'urgent', cta: { label: 'Acknowledge review', go: () => nav('me', 'reviews') } };
  if (d.cycle?.status === 'active' && r?.self_review_status !== 'submitted') {
    const overdue = selfDue != null && selfDue < 0;
    return { title: 'Continue your self-review', detail: overdue ? `Overdue by ${Math.abs(selfDue!)} day${Math.abs(selfDue!) > 1 ? 's' : ''}.` : selfDue != null ? `Due in ${selfDue} day${selfDue > 1 ? 's' : ''}.` : 'Reflect on your goals and achievements.', tone: overdue ? 'urgent' : 'todo', cta: { label: 'Continue self-review', go: () => nav('me', 'reviews') } };
  }
  if (d.goals.length === 0) return { title: 'Set your goals for this cycle', detail: 'Create goals aligned to your team and company objectives.', tone: 'todo', cta: { label: 'Set goals', go: () => nav('me', 'goals') } };
  if (d.weights && !d.weights.valid) return { title: 'Balance your goal weights to 100%', detail: `Your goals currently total ${d.weights.total_weight}%.`, tone: 'todo', cta: { label: 'Adjust weights', go: () => nav('me', 'goals') } };
  const stale = d.activeGoals.filter(isStale);
  if (stale.length > 0) return { title: `Update progress on ${stale.length} goal${stale.length > 1 ? 's' : ''}`, detail: 'These goals haven’t been updated in a while.', tone: 'todo', cta: { label: 'Update goals', go: () => nav('me', 'goals') } };
  if (d.feedbackInbox.length > 0) return { title: `${d.feedbackInbox.length} feedback request${d.feedbackInbox.length > 1 ? 's' : ''} waiting for you`, detail: 'A colleague asked for your input.', tone: 'todo', cta: { label: 'Give feedback', go: () => nav('me', 'feedback') } };
  if (r?.self_review_status === 'submitted' && r?.manager_review_status !== 'submitted') return { title: 'You’re up to date', detail: 'Your manager review is scheduled next.', tone: 'done' };
  return { title: 'You’re all set for this cycle', detail: 'Keep logging progress and achievements as you go.', tone: 'done' };
};

// --- presentational pieces ---------------------------------------------------

const Donut: React.FC<{ value: number; label: string; sub?: string; tone?: string }> = ({ value, label, sub, tone = 'text-orange-500' }) => {
  const pct = Math.max(0, Math.min(100, value));
  const r = 26, c = 2 * Math.PI * r;
  return (
    <div className="flex items-center gap-3">
      <svg width="64" height="64" viewBox="0 0 64 64" className="shrink-0" role="img" aria-label={`${label}: ${Math.round(value)}%`}>
        <circle cx="32" cy="32" r={r} className="stroke-muted" strokeWidth="7" fill="none" />
        <circle cx="32" cy="32" r={r} className={`${tone} transition-all`} stroke="currentColor" strokeWidth="7" fill="none"
          strokeDasharray={c} strokeDashoffset={c - (pct / 100) * c} strokeLinecap="round" transform="rotate(-90 32 32)" />
        <text x="32" y="36" textAnchor="middle" className="fill-foreground text-[15px] font-bold">{Math.round(value)}%</text>
      </svg>
      <div><div className="text-sm font-medium text-foreground">{label}</div>{sub && <div className="text-xs text-muted-foreground">{sub}</div>}</div>
    </div>
  );
};

const Metric: React.FC<{ label: string; value: React.ReactNode; sub?: string; onClick?: () => void; accent?: string }> = ({ label, value, sub, onClick, accent }) => {
  const inner = (
    <>
      <div className={`text-xl font-bold ${accent || 'text-foreground'}`}>{value}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{label}{sub ? <span className="block">{sub}</span> : null}</div>
    </>
  );
  return onClick
    ? <button onClick={onClick} className="text-left rounded-lg border border-border bg-card hover:bg-muted/50 hover:border-orange-300 transition p-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400">{inner}</button>
    : <div className="rounded-lg border border-border bg-card p-3">{inner}</div>;
};

const SectionEmpty: React.FC<{ text: string; cta?: { label: string; onClick: () => void } }> = ({ text, cta }) => (
  <div className="text-center py-6 px-3">
    <p className="text-sm text-muted-foreground">{text}</p>
    {cta && <Button size="sm" variant="outline" className="mt-2" onClick={cta.onClick}>{cta.label}</Button>}
  </div>
);

const CycleHeader: React.FC<{ d: HomeData; next: NextAction; journey: Step[]; nav: Nav }> = ({ d, next, journey, nav }) => {
  const { t } = useTranslation('performance');
  const activeStep = journey.find((s) => s.state === 'active' || s.state === 'overdue');
  const nearest = [d.cycle?.self_review_due_date, d.cycle?.manager_review_due_date, d.cycle?.end_date]
    .map((x) => ({ x, du: daysUntil(x) })).filter((o) => o.x && o.du != null && o.du >= -30).sort((a, b) => (a.du! - b.du!))[0];
  const done = journey.filter((s) => s.state === 'complete').length;
  const applicable = journey.filter((s) => s.state !== 'locked').length || journey.length;
  const completion = Math.round((done / journey.length) * 100);
  if (!d.cycle) return (
    <Card><CardContent className="pt-6"><SectionEmpty text={t('no_cycle', 'No active performance cycle right now. Your goals and reviews will appear here once a cycle opens.')} /></CardContent></Card>
  );
  return (
    <div className="rounded-2xl border border-border bg-gradient-to-br from-orange-50 via-card to-card dark:from-orange-500/10 dark:via-card p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-bold text-charcoal truncate">{d.cycle.name}</h2>
            {activeStep && <span className="text-[11px] font-semibold rounded-full bg-orange-600 text-white px-2 py-0.5">{t('current_stage', 'Current stage')}: {activeStep.label}</span>}
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            {d.cycle.start_date && d.cycle.end_date ? `${fmtDate(d.cycle.start_date)} – ${fmtDate(d.cycle.end_date)}` : ''}
            {nearest && <> · <span className={nearest.du! < 0 ? 'text-danger font-medium' : nearest.du! <= 3 ? 'text-warning font-medium' : ''}>{nearest.du! < 0 ? t('overdue_by', 'overdue by {{n}} days', { n: Math.abs(nearest.du!) }) : t('due_in', 'due in {{n}} days', { n: nearest.du! })}</span></>}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-2xl font-bold text-foreground">{completion}%</div>
            <div className="text-[11px] text-muted-foreground">{t('cycle_complete', 'cycle complete')} · {done}/{applicable}</div>
          </div>
          {next.cta && <Button onClick={next.cta.go} className="shadow-sm">{next.cta.label}</Button>}
        </div>
      </div>
    </div>
  );
};

const JourneyStepper: React.FC<{ steps: Step[] }> = ({ steps }) => {
  const { t } = useTranslation('performance');
  const stateStyle: Record<StageState, { ring: string; text: string; icon: string }> = {
    complete: { ring: 'bg-success border-success text-white', text: 'text-foreground', icon: '✓' },
    active: { ring: 'bg-orange-600 border-orange-600 text-white animate-none', text: 'text-orange-700 dark:text-orange-300 font-semibold', icon: '●' },
    overdue: { ring: 'bg-danger border-danger text-white', text: 'text-danger font-semibold', icon: '!' },
    upcoming: { ring: 'bg-card border-border text-muted-foreground', text: 'text-muted-foreground', icon: '' },
    locked: { ring: 'bg-muted border-border text-muted-foreground/50', text: 'text-muted-foreground/50', icon: '🔒' },
  };
  return (
    <div className="rounded-xl border border-border bg-card p-3 overflow-x-auto">
      <ol className="flex items-center gap-1 min-w-max" aria-label={t('cycle_journey', 'Performance cycle journey')}>
        {steps.map((s, i) => {
          const st = stateStyle[s.state];
          return (
            <li key={s.key} className="flex items-center gap-1">
              <div className="flex flex-col items-center gap-1 px-2">
                <div className={`h-7 w-7 rounded-full border-2 flex items-center justify-center text-xs ${st.ring}`} aria-label={`${s.label}: ${s.state}`}>{st.icon || (i + 1)}</div>
                <span className={`text-[11px] whitespace-nowrap ${st.text}`}>{s.label}</span>
              </div>
              {i < steps.length - 1 && <div className={`h-0.5 w-6 md:w-10 ${steps[i].state === 'complete' ? 'bg-success' : 'bg-border'}`} />}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

const NextActionCard: React.FC<{ next: NextAction }> = ({ next }) => {
  const { t } = useTranslation('performance');
  const tone = next.tone === 'urgent'
    ? 'border-danger bg-danger-soft'
    : next.tone === 'done' ? 'border-success bg-success-soft' : 'border-orange-200 bg-orange-50 dark:bg-orange-500/10';
  return (
    <div className={`rounded-xl border ${tone} p-4`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground">{next.tone === 'done' ? t('you_are_up_to_date', 'Up to date') : t('needs_your_attention', 'Needs your attention')}</div>
          <div className="text-base font-semibold text-foreground mt-0.5">{next.title}</div>
          {next.detail && <div className="text-sm text-muted-foreground mt-0.5">{next.detail}</div>}
          {next.requirements && next.requirements.length > 0 && (
            <ul className="mt-2 space-y-0.5">{next.requirements.map((r) => <li key={r} className="text-xs text-muted-foreground flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50" /> {r}</li>)}</ul>
          )}
        </div>
        {next.cta && <Button onClick={next.cta.go} className="shrink-0">{next.cta.label}</Button>}
      </div>
    </div>
  );
};

// Rich goal card with the correct overachievement viz + status chip + inline action.
const RichGoalCard: React.FC<{ goal: api.Goal; onUpdate: () => void }> = ({ goal, onUpdate }) => {
  const { t } = useTranslation('performance');
  const b = goalBucket(goal);
  const meta = BUCKET_META[b];
  const raw = goal.raw_attainment ?? goal.progress ?? 0;
  const du = daysUntil(goal.due_date);
  return (
    <div className="rounded-xl border border-border bg-card p-3.5 hover:border-orange-300 transition space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-foreground truncate">{goal.title}</div>
          <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
            <span>{t('weight', 'Weight')} {goal.weight}%</span>
            {goal.due_date && <span className={du != null && du < 0 ? 'text-danger' : ''}>· {t('due', 'Due')} {fmtDate(goal.due_date)}</span>}
            {isStale(goal) && <span className="text-warning">· {t('needs_update', 'needs update')}</span>}
          </div>
        </div>
        <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-medium rounded-full px-2 py-0.5 ${meta.chip}`}><span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />{t(`bucket_${b}`, meta.label)}</span>
      </div>
      <OverachievementBar goal={goal} />
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{attainmentLabel(goal, t)}</span>
        <Button size="sm" variant="outline" className="h-7" onClick={onUpdate}>{t('update', 'Update')}</Button>
      </div>
    </div>
  );
};

const GoalsSummaryBar: React.FC<{ goals: api.Goal[]; weights?: api.GoalWeightSummary; filter: Bucket | 'all' | 'attention'; setFilter: (f: any) => void }> = ({ goals, weights, filter, setFilter }) => {
  const { t } = useTranslation('performance');
  const counts = useMemo(() => {
    const c: Record<string, number> = { overdue: 0, at_risk: 0, on_track: 0, exceeded: 0, completed: 0, not_started: 0 };
    goals.forEach((g) => { c[goalBucket(g)]++; });
    return c;
  }, [goals]);
  const attention = counts.overdue + counts.at_risk;
  const chips: { key: any; label: string; n?: number; tone?: string }[] = [
    { key: 'all', label: t('filter_all', 'All'), n: goals.length },
    { key: 'attention', label: t('filter_attention', 'Needs attention'), n: attention, tone: attention ? 'text-danger' : '' },
    { key: 'on_track', label: t('bucket_on_track', 'On track'), n: counts.on_track },
    { key: 'exceeded', label: t('bucket_exceeded', 'Exceeded'), n: counts.exceeded },
    { key: 'completed', label: t('bucket_completed', 'Achieved'), n: counts.completed },
  ];
  return (
    <div className="space-y-2">
      {weights && (
        <div className="flex items-center gap-2 text-xs">
          <span className={`rounded-full px-2 py-0.5 ${weights.valid ? 'bg-success-soft text-success-strong' : 'bg-warning-soft text-warning-strong'}`}>{t('total_weight', 'Total weight')}: {weights.total_weight}% {weights.valid ? '✓' : `· ${t('must_total_100', 'must total 100%')}`}</span>
        </div>
      )}
      <div className="flex flex-wrap gap-1.5">
        {chips.map((c) => (
          <button key={c.key} onClick={() => setFilter(c.key)} className={`text-xs rounded-full border px-2.5 py-1 transition ${filter === c.key ? 'bg-orange-600 text-white border-orange-600' : 'border-border text-foreground hover:bg-muted'}`}>
            {c.label}{c.n != null && <span className={`ml-1 ${filter === c.key ? '' : c.tone || 'text-muted-foreground'}`}>{c.n}</span>}
          </button>
        ))}
      </div>
    </div>
  );
};

// Merged activity feed (achievements + feedback received/pending), newest first.
const ActivityFeed: React.FC<{ d: HomeData; nav: Nav }> = ({ d, nav }) => {
  const { t } = useTranslation('performance');
  type Item = { id: string; kind: 'achievement' | 'feedback' | 'request' | 'meeting'; title: string; sub?: string; date?: string; action?: () => void };
  const items: Item[] = [];
  const nextMeeting = d.oneOnOnes.filter((o) => o.status === 'scheduled' && o.scheduled_at && new Date(o.scheduled_at).getTime() >= Date.now() - 3600e3).sort((a, b) => new Date(a.scheduled_at!).getTime() - new Date(b.scheduled_at!).getTime())[0];
  if (nextMeeting) items.push({ id: 'oo-' + nextMeeting.id, kind: 'meeting', title: t('next_1on1', 'Next 1:1 with {{name}}', { name: nextMeeting.counterpart_name || t('a_colleague', 'a colleague') }), sub: fmtDateTime(nextMeeting.scheduled_at), date: nextMeeting.scheduled_at, action: () => nav('me', 'oneonones') });
  d.feedbackInbox.forEach((r) => items.push({ id: 'req-' + r.id, kind: 'request', title: t('feedback_requested_you', 'Feedback requested from you'), sub: r.subject_name ? `${t('feedback_on', 'on')} ${r.subject_name}` : undefined, date: r.created_at, action: () => nav('me', 'feedback') }));
  (d.aboutMe?.items || []).forEach((it, i) => items.push({ id: 'fb-' + i, kind: 'feedback', title: t('feedback_received', 'Feedback received'), sub: it.anonymous ? t('anonymous', 'Anonymous') : it.provider_name, date: it.created_at }));
  d.achievements.forEach((a) => items.push({ id: 'ach-' + a.id, kind: 'achievement', title: a.title, sub: a.impact, date: a.entry_date }));
  items.sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime());
  const icon: Record<Item['kind'], { e: string; c: string }> = {
    achievement: { e: '🏆', c: 'bg-orange-100 dark:bg-orange-500/20' },
    feedback: { e: '💬', c: 'bg-blue-100 dark:bg-blue-500/20' },
    request: { e: '📨', c: 'bg-amber-100 dark:bg-amber-500/20' },
    meeting: { e: '🗓️', c: 'bg-violet-100 dark:bg-violet-500/20' },
  };
  if (items.length === 0) return <SectionEmpty text={t('no_activity', 'No recent activity. Log an achievement or request feedback to get started.')} cta={{ label: t('add_achievement', 'Add achievement'), onClick: () => nav('me', 'achievements') }} />;
  return (
    <ul className="space-y-2.5">
      {items.slice(0, 6).map((it) => (
        <li key={it.id} className="flex items-start gap-2.5">
          <span className={`h-7 w-7 rounded-full flex items-center justify-center text-sm shrink-0 ${icon[it.kind].c}`}>{icon[it.kind].e}</span>
          <div className="min-w-0 flex-1">
            <div className="text-sm text-foreground leading-tight">{it.title}</div>
            {it.sub && <div className="text-xs text-muted-foreground truncate">{it.sub}</div>}
            <div className="text-[11px] text-muted-foreground/70">{it.date ? fmtDate(it.date) : ''}</div>
          </div>
          {it.action && <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={it.action}>{t('respond', 'Respond')}</Button>}
        </li>
      ))}
    </ul>
  );
};

// Manager strip — team tasks lead the manager's homepage.
const ManagerStrip: React.FC<{ d: HomeData; nav: Nav }> = ({ d, nav }) => {
  const { t } = useTranslation('performance');
  const awaiting = d.teamReviews.filter((x) => x.self_review_status === 'submitted' && x.manager_review_status !== 'submitted').length;
  const toRelease = d.teamReviews.filter((x) => x.status === 'finalized' && !x.released_at).length;
  const done = d.teamReviews.filter((x) => x.status === 'finalized').length;
  return (
    <Card className="border-orange-200 dark:border-orange-500/30">
      <CardContent className="pt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold text-foreground">{t('your_team', 'Your team')}</div>
          <Button size="sm" variant="outline" onClick={() => nav('manage', 'team')}>{t('open_team', 'Open team reviews')}</Button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Metric label={t('awaiting_you', 'Awaiting your review')} value={awaiting} accent={awaiting ? 'text-danger' : undefined} onClick={() => nav('manage', 'team')} />
          <Metric label={t('to_release', 'Ready to release')} value={toRelease} onClick={() => nav('manage', 'team')} />
          <Metric label={t('team_done', 'Finalized')} value={`${done}/${d.teamReviews.length}`} onClick={() => nav('manage', 'team')} />
        </div>
      </CardContent>
    </Card>
  );
};

// HR strip — administration + bottlenecks lead the HR homepage.
const HRStrip: React.FC<{ d: HomeData; nav: Nav }> = ({ d, nav }) => {
  const { t } = useTranslation('performance');
  const active = d.trend.filter((c) => c.status === 'active').length;
  const latest = d.trend[0];
  return (
    <Card className="border-orange-200 dark:border-orange-500/30">
      <CardContent className="pt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold text-foreground">{t('administration', 'Administration')}</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => nav('manage', 'analytics')}>{t('view_insights', 'Insights')}</Button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Metric label={t('active_cycles', 'Active cycles')} value={active} />
          <Metric label={t('latest_completion', 'Latest completion')} value={latest ? `${latest.completion_pct}%` : '—'} sub={latest?.cycle_name} onClick={() => nav('manage', 'analytics')} />
          <Metric label={t('latest_avg', 'Avg rating')} value={latest?.avg_final_rating != null ? latest.avg_final_rating.toFixed(1) : '—'} onClick={() => nav('manage', 'analytics')} />
          <Metric label={t('open_reports', 'Reports')} value="→" onClick={() => nav('manage', 'analytics')} />
        </div>
      </CardContent>
    </Card>
  );
};

// The new role-aware overview homepage.
const OverviewTab: React.FC<{ nav: Nav; persona: ReturnType<typeof usePersona> }> = ({ nav, persona }) => {
  const { t } = useTranslation('performance');
  const d = useHomeData(persona);
  const [filter, setFilter] = useState<Bucket | 'all' | 'attention'>('all');

  if (d.loading) return <OverviewSkeleton />;
  const journey = deriveJourney(d);
  const next = deriveNextAction(d, persona, nav);
  const filtered = d.activeGoals.filter((g) => {
    if (filter === 'all') return true;
    if (filter === 'attention') return ['overdue', 'at_risk'].includes(goalBucket(g));
    return goalBucket(g) === filter;
  }).sort((a, b) => {
    const order: Bucket[] = ['overdue', 'at_risk', 'not_started', 'on_track', 'exceeded', 'completed'];
    return order.indexOf(goalBucket(a)) - order.indexOf(goalBucket(b));
  });
  const avgAttain = d.goals.length ? Math.round(d.goals.reduce((s, g) => s + (g.raw_attainment ?? g.progress ?? 0), 0) / d.goals.length) : 0;
  const finalScore = d.myReview?.final_score;
  const showComp = persona.showComp; // released outcome for employees handled inside Reviews; here HR/Fin get the shortcut

  return (
    <div className="space-y-4">
      <CycleHeader d={d} next={next} journey={journey} nav={nav} />
      {d.cycle && <JourneyStepper steps={journey} />}

      {persona.isHR && <HRStrip d={d} nav={nav} />}
      {persona.isManager && !persona.isHR && <ManagerStrip d={d} nav={nav} />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-4">
          <NextActionCard next={next} />

          <Card>
            <CardHeader className="flex-row items-center justify-between pb-2">
              <CardTitle className="text-base">{t('active_goals', 'Active goals')}</CardTitle>
              <Button size="sm" variant="ghost" onClick={() => nav('me', 'goals')}>{t('view_all', 'View all')} →</Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {d.goals.length === 0
                ? <SectionEmpty text={t('empty_goals', 'No goals for this cycle yet. Create goals aligned to your team and company objectives.')} cta={{ label: t('create_goal', 'Create goal'), onClick: () => nav('me', 'goals') }} />
                : <>
                  <GoalsSummaryBar goals={d.goals} weights={d.weights} filter={filter} setFilter={setFilter} />
                  {filtered.length === 0 ? <p className="text-sm text-muted-foreground py-3 text-center">{t('no_goals_filter', 'No goals in this view.')}</p>
                    : filtered.slice(0, 5).map((g) => <RichGoalCard key={g.id} goal={g} onUpdate={() => nav('me', 'goals')} />)}
                </>}
            </CardContent>
          </Card>
        </div>

        {/* Supporting column */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">{t('snapshot', 'Performance snapshot')}</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Donut value={finalScore != null ? (finalScore / 5) * 100 : avgAttain} label={finalScore != null ? t('weighted_score', 'Weighted score') : t('goal_attainment', 'Goal attainment')} sub={finalScore != null ? `${finalScore} / 5` : `${d.activeGoals.length} ${t('active', 'active')}`} />
              <div className="grid grid-cols-2 gap-2">
                <Metric label={t('review_status', 'Review')} value={<span className="text-sm capitalize">{d.myReview ? human(d.myReview.status) : '—'}</span>} onClick={() => nav('me', 'reviews')} />
                <Metric label={t('feedback_received', 'Feedback')} value={d.aboutMe?.submitted_count ?? 0} onClick={() => nav('me', 'feedback')} />
                <Metric label={t('achievements', 'Achievements')} value={d.achievements.length} onClick={() => nav('me', 'achievements')} />
                <Metric label={t('rating', 'Rating')} value={d.myReview?.released_at ? (d.myReview.final_rating ?? '—') : '—'} sub={d.myReview?.released_at ? undefined : t('after_release', 'after release')} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">{t('activity', 'Recent activity')}</CardTitle></CardHeader>
            <CardContent><ActivityFeed d={d} nav={nav} /></CardContent>
          </Card>

          <DevelopmentCard nav={nav} />

          {showComp && (
            <Card className="border-dashed">
              <CardContent className="pt-4 flex items-center justify-between gap-2">
                <div className="text-sm"><div className="font-medium text-foreground">{t('tab_compensation', 'Compensation')}</div><div className="text-xs text-muted-foreground">{t('comp_shortcut', 'Review and approve merit & bonuses.')}</div></div>
                <Button size="sm" variant="outline" onClick={() => nav('manage', 'compensation')}>{t('open', 'Open')}</Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

// Development panel (collapsible; own fetch so the overview stays light).
const DevelopmentCard: React.FC<{ nav: Nav }> = ({ nav }) => {
  const { t } = useTranslation('performance');
  const [plans, setPlans] = useState<api.DevPlan[] | null>(null);
  useEffect(() => { api.listDevPlans().then(setPlans).catch(() => setPlans([])); }, []);
  const active = (plans || []).filter((p) => p.status === 'active');
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between pb-2">
        <CardTitle className="text-base">{t('development', 'Development')}</CardTitle>
        <Button size="sm" variant="ghost" onClick={() => nav('me', 'reviews')}>{t('view_all', 'View all')} →</Button>
      </CardHeader>
      <CardContent>
        {plans === null ? <div className="h-10 rounded bg-muted animate-pulse" />
          : active.length === 0 ? <SectionEmpty text={t('empty_dev', 'No development plan yet. Set growth goals to work toward.')} cta={{ label: t('create_dev_plan', 'Create plan'), onClick: () => nav('me', 'reviews') }} />
            : <ul className="space-y-2">{active.slice(0, 3).map((p) => {
              const total = p.actions.length, done = p.actions.filter((a) => a.done).length;
              return <li key={p.id} className="text-sm"><div className="flex items-center justify-between"><span className="font-medium text-foreground truncate">{p.title}</span><span className="text-xs text-muted-foreground">{done}/{total}</span></div><Progress value={total ? (done / total) * 100 : 0} /></li>;
            })}</ul>}
      </CardContent>
    </Card>
  );
};

const OverviewSkeleton: React.FC = () => (
  <div className="space-y-4">
    <div className="h-24 rounded-2xl bg-muted animate-pulse" />
    <div className="h-16 rounded-xl bg-muted animate-pulse" />
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 space-y-4"><div className="h-24 rounded-xl bg-muted animate-pulse" /><div className="h-64 rounded-xl bg-muted animate-pulse" /></div>
      <div className="space-y-4"><div className="h-48 rounded-xl bg-muted animate-pulse" /><div className="h-40 rounded-xl bg-muted animate-pulse" /></div>
    </div>
  </div>
);

// ---- Goals ------------------------------------------------------------------
const GoalsTab: React.FC = () => {
  const { t } = useTranslation('performance');
  const [rows, setRows] = useState<api.Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState<api.Goal | null>(null);
  const [checkIn, setCheckIn] = useState<api.Goal | null>(null);
  const load = () => api.listGoals().then(setRows).catch(() => setRows([])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  // Live weight-sum validator (the reference products hide this; we surface it).
  const totalWeight = useMemo(() => rows.reduce((s, g) => s + (g.weight || 0), 0), [rows]);
  const weightValid = totalWeight === 100;

  const archive = async (g: api.Goal) => {
    try { await api.archiveGoal(g.id); toast.success(t('goal_archived', 'Goal archived')); load(); } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); }
  };

  if (loading) return <Loading />;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <AskDana q="Suggest a few SMART goals for me this cycle" />
        <div className="flex items-center gap-3">
          {rows.length > 0 && (
            <span className={`text-xs rounded-full px-2.5 py-1 ${weightValid ? 'bg-success-soft text-success-strong' : 'bg-warning-soft text-warning-strong'}`}>
              {t('total_weight', 'Total weight')}: {totalWeight}% {weightValid ? '✓' : `· ${t('must_total_100', 'must total 100%')}`}
            </span>
          )}
          <Button size="sm" onClick={() => { setEdit(null); setOpen(true); }}><Plus className="w-4 h-4 mr-1" /> {t('new_goal', 'New goal')}</Button>
        </div>
      </div>
      {rows.length === 0 ? <Empty text={t('no_goals', 'No goals yet — create one or ask Dana for SMART suggestions.')} /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {rows.map((g) => (
            <Card key={g.id}>
              <CardContent className="pt-5 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-foreground">{g.title}</div>
                    {g.description && <div className="text-sm text-muted-foreground line-clamp-2">{g.description}</div>}
                  </div>
                  <Badge className={`${chip(g.status)} border-0 capitalize shrink-0`}>{human(g.status)}</Badge>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{t('weight', 'Weight')}: {g.weight}%</span>
                  {g.due_date && <span>· {t('due', 'Due')} {fmtDate(g.due_date)}</span>}
                  {(g.raw_attainment ?? 0) > 100 && g.score_contribution < g.raw_attainment && (
                    <span>· {t('scored', 'scored')} {g.score_contribution}% ({t('capped', 'capped')})</span>
                  )}
                </div>
                <OverachievementBar goal={g} />
                <div className="text-xs text-muted-foreground">{attainmentLabel(g, t)}</div>
                {g.connected_work && (
                  <div className="text-[11px] text-muted-foreground">
                    Asana · {g.connected_work.remote_name || g.connected_work.remote_id} · {g.connected_work.tasks_done}/{g.connected_work.tasks_total} {t('tasks', 'tasks')}
                    {g.connected_work.overdue_count > 0 && ` · ${g.connected_work.overdue_count} ${t('overdue', 'overdue')}`}
                    {g.progress_source === 'connected' && ` · ${t('progress_from_asana', 'progress from Asana')}`}
                    {g.connected_work.external_status !== 'open' && ` · ${t('source_unavailable', 'source work unavailable')}`}
                    {g.connected_work.remote_url && <> · <a href={g.connected_work.remote_url} target="_blank" rel="noreferrer" className="text-orange-600 underline">{t('view', 'View')}</a></>}
                  </div>
                )}
                <div className="flex gap-2 pt-1">
                  <Button size="sm" variant="outline" onClick={() => setCheckIn(g)}>{t('check_in', 'Check in')}</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setEdit(g); setOpen(true); }}>{t('edit_goal', 'Edit')}</Button>
                  <Button size="sm" variant="ghost" onClick={() => archive(g)}>{t('archive', 'Archive')}</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {checkIn && <GoalCheckInDialog goal={checkIn} onClose={() => setCheckIn(null)} onSaved={() => { setCheckIn(null); load(); }} />}
      <GoalDialog open={open} goal={edit} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />
    </div>
  );
};

// Continuous performance: log a check-in (progress + note + evidence) that appends to
// a timeline, and manage milestones — so a goal has a running history, not just a
// current number.
const GoalCheckInDialog: React.FC<{ goal: api.Goal; onClose: () => void; onSaved: () => void }> = ({ goal, onClose, onSaved }) => {
  const { t } = useTranslation('performance');
  const numeric = goal.measurement_type === 'number' || goal.measurement_type === 'currency';
  const [updates, setUpdates] = useState<api.ProgressUpdate[]>([]);
  const [milestones, setMilestones] = useState<api.Milestone[]>([]);
  const [names, setNames] = useState<Record<string, string>>({}); // US-24: actor user_id → name
  const [f, setF] = useState<{ progress: string; actual: string; note: string; evidence_url: string }>({ progress: String(goal.progress ?? ''), actual: goal.actual != null ? String(goal.actual) : '', note: '', evidence_url: '' });
  const [msTitle, setMsTitle] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = () => {
    api.getGoalUpdates(goal.id).then(setUpdates).catch(() => setUpdates([]));
    api.getMilestones(goal.id).then(setMilestones).catch(() => setMilestones([]));
  };
  useEffect(() => {
    api.getFeedbackPeople().then((ppl) => {
      const m: Record<string, string> = {};
      ppl.forEach((p) => { m[p.user_id] = p.name; });
      setNames(m);
    }).catch(() => {});
  }, []);
  useEffect(reload, [goal.id]);

  const submit = async () => {
    setBusy(true);
    try {
      const body: api.CheckInInput = { note: f.note || undefined, evidence_url: f.evidence_url || undefined };
      if (numeric) { if (f.actual !== '') body.actual = Number(f.actual); }
      else if (f.progress !== '') body.progress = Number(f.progress);
      await api.addGoalUpdate(goal.id, body);
      toast.success(t('checked_in', 'Check-in saved'));
      setF({ ...f, note: '', evidence_url: '' });
      reload(); onSaved();
    } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } finally { setBusy(false); }
  };

  const addMs = async () => {
    if (!msTitle.trim()) return;
    try { await api.addMilestone(goal.id, { title: msTitle.trim() }); setMsTitle(''); reload(); }
    catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); }
  };
  const toggleMs = async (m: api.Milestone) => {
    try { await api.updateMilestone(goal.id, m.id, { done: !m.done }); reload(); }
    catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); }
  };
  const delMs = async (m: api.Milestone) => {
    try { await api.deleteMilestone(goal.id, m.id); reload(); } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{t('check_in', 'Check in')} · {goal.title}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-1 max-h-[64vh] overflow-y-auto">
          {/* Log a check-in */}
          <div className="space-y-2">
            {numeric ? (
              <div><Label className="text-xs">{t('actual', 'Actual')}</Label>
                <Input type="number" step="any" value={f.actual} onChange={(e) => setF({ ...f, actual: e.target.value })} /></div>
            ) : (
              <div><Label className="text-xs">{t('progress_pct', 'Progress %')}</Label>
                <Input type="number" min={0} value={f.progress} onChange={(e) => setF({ ...f, progress: e.target.value })} />
                <p className="text-[11px] text-muted-foreground mt-0.5">{t('over_100_ok', 'You can enter above 100% — overachievement is preserved.')}</p></div>
            )}
            <div><Label className="text-xs">{t('note', 'Note')}</Label>
              <textarea value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className="w-full min-h-[60px] rounded-md border border-border bg-background px-2.5 py-2 text-sm" placeholder={t('note_ph', 'What changed since last time?')} /></div>
            <div><Label className="text-xs">{t('evidence', 'Evidence link')} <span className="text-muted-foreground">({t('optional', 'optional')})</span></Label>
              <Input type="url" value={f.evidence_url} onChange={(e) => setF({ ...f, evidence_url: e.target.value })} placeholder="https://…" /></div>
            <Button size="sm" onClick={submit} disabled={busy}>{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{t('save_checkin', 'Save check-in')}</Button>
          </div>

          {/* Milestones */}
          <div className="border-t border-border pt-3 space-y-2">
            <div className="text-xs font-semibold text-muted-foreground">{t('milestones', 'Milestones')}</div>
            {milestones.map((m) => (
              <div key={m.id} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={m.done} onChange={() => toggleMs(m)} className="h-4 w-4 accent-orange-500" />
                <span className={`flex-1 ${m.done ? 'line-through text-muted-foreground' : 'text-foreground'}`}>{m.title}</span>
                <button onClick={() => delMs(m)} className="text-muted-foreground hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            <div className="flex gap-2">
              <Input value={msTitle} onChange={(e) => setMsTitle(e.target.value)} placeholder={t('add_milestone', 'Add a milestone')} onKeyDown={(e) => e.key === 'Enter' && addMs()} className="h-8" />
              <Button size="sm" variant="outline" onClick={addMs}><Plus className="h-4 w-4" /></Button>
            </div>
          </div>

          {/* Timeline */}
          <div className="border-t border-border pt-3 space-y-2">
            <div className="text-xs font-semibold text-muted-foreground">{t('timeline', 'Check-in history')}</div>
            {updates.length === 0 ? <p className="text-sm text-muted-foreground">{t('no_updates', 'No check-ins yet.')}</p> :
              updates.map((u) => (
                <div key={u.id} className="text-sm border-l-2 border-border pl-2.5">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                    <span>{fmtDateTime(u.created_at)}</span>
                    {u.created_by && names[u.created_by] && <span>· {names[u.created_by]}</span>}
                    {u.progress != null && <span>· {u.progress}%</span>}
                    {u.actual != null && <span>· {t('actual', 'Actual')} {goalValue(goal, u.actual)}</span>}
                  </div>
                  {u.note && <div className="text-foreground">{u.note}</div>}
                  {u.evidence_url && <a href={u.evidence_url} target="_blank" rel="noreferrer" className="text-xs text-orange-600 underline break-all">{u.evidence_url}</a>}
                </div>
              ))}
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>{t('close', 'Close')}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const MEASUREMENTS: api.MeasurementType[] = ['percentage', 'number', 'currency', 'boolean', 'milestones', 'rating', 'formula', 'manual'];

// Connected work (M4): link the goal to an Asana project — evidence-only or
// progress-calculated. Authoring goes through migration-service; this component
// never touches Asana directly.
const ConnectedWorkSection: React.FC<{ goal: api.Goal; onChanged: () => void }> = ({ goal, onChanged }) => {
  const { t } = useTranslation('performance');
  const cw = goal.connected_work;
  const [projects, setProjects] = useState<api.AsanaGoalProject[] | null>(null);
  const [gid, setGid] = useState('');
  const [mode, setMode] = useState<'evidence' | 'calculated'>('evidence');
  const [busy, setBusy] = useState(false);
  // US-26: distinguish loading / ready / quiet (not connected) / error (recoverable)
  // instead of a single boolean that silently hid the picker on ANY failure.
  const [pstatus, setPstatus] = useState<'loading' | 'ready' | 'quiet' | 'error'>('loading');
  const loadProjects = React.useCallback(() => {
    setPstatus('loading');
    api.listGoalProjects()
      .then((ps) => { setProjects(ps); setPstatus('ready'); })
      .catch((e: any) => {
        const code = e?.response?.status;
        // Not connected / not authorized / no such surface → stay quiet (unchanged UX).
        setPstatus(code === 401 || code === 403 || code === 404 ? 'quiet' : 'error');
      });
  }, []);
  useEffect(() => { if (!cw) loadProjects(); }, [cw, loadProjects]);
// Connected work can come from more than one place. performance-service
// records the provider on every link; the UI must not assume one.
//
//   native_kazipeople — work items in KaziPeople Work carrying this goal's id
//                       (work.work_item.goal_id). internal/workevidence keeps
//                       the counts live from work.completed/cancelled/reopened.
//   asana             — an Asana project linked through migration-service.
// Product names, not translatable copy — so no t() and no TFunction plumbing.
function providerLabel(provider: string): string {
  if (provider === 'native_kazipeople') return 'KaziPeople Work';
  if (provider === 'asana') return 'Asana';
  return provider || 'Connected';
}

// How to connect KaziPeople Work — which is done from the WORK side, not here.
// A work item names the goal it contributes to, so one goal can gather many
// items; the goal then reflects them. That is the right direction (work is
// where people work) but it is invisible from this dialog, so say it.
const WORK_LINK_HINT =
  'To track this goal with KaziPeople Work, open a work item and set the goal it contributes to. Progress here then follows those items.';

  const sel = 'w-full h-9 rounded-md border border-border bg-background px-2 text-sm';
  if (cw) {
    return (
      <div className="rounded-md border border-border p-2 space-y-1">
        {/* ⚠️ LABEL THE PROVIDER WE ACTUALLY GOT. This said "Asana"
            unconditionally, while performance-service has recorded
            provider="native_kazipeople" for KaziPeople Work all along
            (internal/workevidence consumes work.completed and pulls
            authoritative counts). Work was already driving goal progress; the
            screen just called it Asana. */}
        <div className="text-xs font-medium text-foreground">
          {t('connected_work', 'Connected work')} · {providerLabel(cw.provider)}
        </div>
        <div className="text-[11px] text-muted-foreground">
          {cw.remote_name || cw.remote_id} · {cw.tasks_done}/{cw.tasks_total} {t('tasks', 'tasks')}
          {cw.milestones_total > 0 && ` · ${cw.milestones_done}/${cw.milestones_total} ${t('milestones', 'milestones')}`}
          {cw.overdue_count > 0 && ` · ${cw.overdue_count} ${t('overdue', 'overdue')}`}
          {` · ${cw.progress_mode === 'calculated' ? t('drives_progress', 'drives progress') : t('evidence_only', 'evidence only')}`}
        </div>
        <div className="flex items-center gap-2">
          {cw.remote_url && (
            <a href={cw.remote_url} target="_blank" rel="noreferrer" className="text-xs text-orange-600 underline">
              {t('view_in_provider', 'View in {{provider}}', { provider: providerLabel(cw.provider) })}
            </a>
          )}
          <Button size="sm" variant="ghost" disabled={busy} onClick={async () => {
            setBusy(true);
            try { await api.unlinkGoal(goal.id); toast.success(t('unlinked', 'Disconnected')); onChanged(); }
            catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); }
            finally { setBusy(false); }
          }}>{t('disconnect', 'Disconnect')}</Button>
        </div>
      </div>
    );
  }
  // Not connected / connected-but-no-projects → stay quiet (unchanged). A transient
  // failure is NOT hidden any more — it shows a recoverable error with Retry (US-26).
  // ⚠️ Previously this returned null, so a goal with no Asana showed NOTHING
  // about connected work — even though linking KaziPeople Work to it is
  // supported and drives progress. Silence is why "link with Work" looked
  // like a missing feature rather than an undiscoverable one.
  if (pstatus === 'quiet' || (pstatus === 'ready' && (projects?.length ?? 0) === 0)) {
    return (
      <div className="rounded-md border border-border p-2">
        <div className="text-xs font-medium text-foreground">{t('connected_work', 'Connected work')}</div>
        <p className="text-[11px] text-muted-foreground">{t('work_link_hint', WORK_LINK_HINT)}</p>
      </div>
    );
  }
  if (pstatus === 'error') {
    // ⚠️ THIS IS AN ASANA PROBLEM, NOT A CONNECTED-WORK PROBLEM, and the old
    // copy made it read as the latter — a red error on a goal whose progress
    // KaziPeople Work can already drive. Say which integration failed, and
    // point at the path that works without it.
    return (
      <div className="rounded-md border border-border p-2 space-y-1">
        <DataState variant="error" compact onRetry={loadProjects}
          message={t('asana_load_failed', 'Couldn’t load Asana projects.') as string}
          retryLabel={t('retry', 'Retry') as string} />
        <p className="text-[11px] text-muted-foreground">{t('work_link_hint', WORK_LINK_HINT)}</p>
      </div>
    );
  }
  return (
    <div className="rounded-md border border-border p-2 space-y-2">
      <div className="text-xs font-medium text-foreground">{t('connect_work', 'Connect Asana work')} <span className="text-muted-foreground">({t('optional', 'optional')})</span></div>
      <div className="grid grid-cols-2 gap-2">
        <select className={sel} value={gid} onChange={(e) => setGid(e.target.value)}>
          <option value="">{projects === null ? t('loading', 'Loading…') : t('pick_project', 'Pick a project…')}</option>
          {(projects ?? []).map((p) => <option key={p.gid} value={p.gid}>{p.name}</option>)}
        </select>
        <select className={sel} value={mode} onChange={(e) => setMode(e.target.value as any)}>
          <option value="evidence">{t('mode_evidence', 'Evidence only')}</option>
          <option value="calculated">{t('mode_calculated', 'Calculate progress from tasks')}</option>
        </select>
      </div>
      <Button size="sm" variant="outline" disabled={!gid || busy} onClick={async () => {
        setBusy(true);
        try { await api.linkGoal({ goal_id: goal.id, project_gid: gid, mode }); toast.success(t('linked', 'Connected')); onChanged(); }
        catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); }
        finally { setBusy(false); }
      }}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('connect', 'Connect')}</Button>
    </div>
  );
};

const GoalDialog: React.FC<{ open: boolean; goal: api.Goal | null; onClose: () => void; onSaved: () => void }> = ({ open, goal, onClose, onSaved }) => {
  const { t } = useTranslation('performance');
  const { isManager, isHrManager, isTenantOwner } = useRoleAccess();
  const isHR = isHrManager || isTenantOwner;
  const editing = !!goal;
  const [f, setF] = useState<any>({});
  const [busy, setBusy] = useState(false);
  const [versions, setVersions] = useState<api.GoalVersion[]>([]);
  const [orgGoals, setOrgGoals] = useState<api.Goal[]>([]);
  const [categories, setCategories] = useState<api.GoalCategory[]>([]);
  const [dialogDepts, setDialogDepts] = useState<Array<{ id: string; name: string }>>([]);
  // US-15 — named-assignee picker (managers/HR, individual goals). US-19 — the
  // Due date defaults to today so the native date field never renders as 0/0/0.
  // ⚠️ userId is the AUTH user id, which is what performance keys goals on
  // (the service reads mw.UserID). The directory hit's `id` is the employee
  // NUMBER (e.g. EMP2D6C499F) — sending that made uuid.Parse fail server-side
  // and the goal silently landed on the CREATOR instead of the assignee, with
  // no error and no email. See the comment on EmployeeHit.
  const [assignee, setAssignee] = useState<{ userId: string; name: string } | null>(null);
  const [empQuery, setEmpQuery] = useState('');
  const [empHits, setEmpHits] = useState<workforce.EmployeeHit[]>([]);
  useEffect(() => {
    setF(goal
      ? { measurement_type: goal.measurement_type, progress: goal.progress, actual: goal.actual ?? '', target: goal.target ?? '', weight: goal.weight, scoring_cap: goal.scoring_cap ?? '', status: goal.status, weight_locked: !!goal.weight_locked_at, goal_level: goal.goal_level, parent_goal_id: goal.parent_goal_id ?? '', category_id: goal.category_id ?? '', org_unit_id: goal.org_unit_id ?? '' }
      : { title: '', weight: 20, measurement_type: 'percentage', goal_level: 'individual', parent_goal_id: '', category_id: '', org_unit_id: '', due_date: isoToday() });
    setAssignee(null); setEmpQuery(''); setEmpHits([]);
    setVersions([]);
    if (goal) api.getGoalVersions(goal.id).then(setVersions).catch(() => setVersions([]));
    // Parent candidates: everything above the goal's level (dept goals align to
    // company; individual goals align to either).
    if (open) {
      Promise.all([api.listGoals(undefined, 'company').catch(() => []), api.listGoals(undefined, 'department').catch(() => [])])
        .then(([c, d]) => setOrgGoals([...c, ...d]));
      api.listGoalCategories().then(setCategories).catch(() => setCategories([]));
      getDepartments().then((r: any) => setDialogDepts((r?.items || []).map((d: any) => ({ id: d.id, name: d.name })))).catch(() => {});
    }
  }, [goal, open]);

  const numeric = f.measurement_type === 'number' || f.measurement_type === 'currency';

  // Debounced directory search for the assignee picker.
  useEffect(() => {
    const q = empQuery.trim();
    if (assignee || q.length < 1) { setEmpHits([]); return; }
    const h = setTimeout(() => {
      workforce.searchEmployees(q).then((r) => setEmpHits(r.slice(0, 6))).catch(() => setEmpHits([]));
    }, 250);
    return () => clearTimeout(h);
  }, [empQuery, assignee]);

  const save = async () => {
    setBusy(true);
    try {
      const body: api.GoalInput = {
        title: f.title, description: f.description,
        measurement_type: f.measurement_type,
        weight: f.weight === '' || f.weight == null ? undefined : Number(f.weight),
        due_date: f.due_date || undefined,
        target: f.target === '' || f.target == null ? undefined : Number(f.target),
        actual: f.actual === '' || f.actual == null ? undefined : Number(f.actual),
        scoring_cap: f.scoring_cap === '' || f.scoring_cap == null ? undefined : Number(f.scoring_cap),
        progress: f.progress === '' || f.progress == null ? undefined : Number(f.progress),
        status: f.status,
        change_reason: f.change_reason || undefined,
        goal_level: editing ? undefined : f.goal_level || undefined,
        parent_goal_id: f.parent_goal_id || undefined,
        category_id: f.category_id || undefined,
        org_unit_id: f.org_unit_id || undefined,
        clear_parent: editing && !f.parent_goal_id && !!goal?.parent_goal_id ? true : undefined,
      };
      // Only individual goals carry a named owner; department/company goals are
      // scoped to the org unit, so never forward an assignee for them.
      const assigneeId = (!editing && f.goal_level === 'individual') ? (assignee?.userId || undefined) : undefined;
      if (editing) await api.updateGoal(goal!.id, body);
      else await api.createGoal(body, assigneeId);
      toast.success(editing ? t('goal_updated', 'Goal updated') : t('goal_created', 'Goal created'));
      onSaved();
    } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); }
    finally { setBusy(false); }
  };
  const sel = 'w-full h-9 rounded-md border border-border bg-background px-2 text-sm';
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? t('edit_goal', 'Edit goal') : t('new_goal', 'New goal')}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          {!editing && (
            <>
              <div className="space-y-1"><Label className="text-xs">{t('goal_title', 'Title')}</Label><Input value={f.title || ''} onChange={(e) => setF({ ...f, title: e.target.value })} /></div>
              <div className="space-y-1"><Label className="text-xs">{t('goal_desc', 'Description')}</Label><Input value={f.description || ''} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
            </>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">{t('measurement', 'Measured by')}</Label>
              <select className={sel} value={f.measurement_type} onChange={(e) => setF({ ...f, measurement_type: e.target.value })}>
                {MEASUREMENTS.map((m) => <option key={m} value={m}>{human(m)}</option>)}
              </select>
            </div>
            <div className="space-y-1"><Label className="text-xs">{t('weight', 'Weight')} (%)</Label><Input type="number" min={0} max={100} value={f.weight ?? ''} onChange={(e) => setF({ ...f, weight: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {!editing && (isManager || isHR) && (
              <div className="space-y-1"><Label className="text-xs">{t('goal_level', 'Level')}</Label>
                <select className={sel} value={f.goal_level} onChange={(e) => { setF({ ...f, goal_level: e.target.value, parent_goal_id: '' }); setAssignee(null); setEmpQuery(''); }}>
                  <option value="individual">{t('lvl_individual', 'Individual')}</option>
                  <option value="department">{t('lvl_department', 'Department')}</option>
                  {isHR && <option value="company">{t('lvl_company', 'Company')}</option>}
                </select>
              </div>
            )}
            {!editing && (isManager || isHR) && f.goal_level === 'individual' && (
              <div className="space-y-1 relative">
                <Label className="text-xs">{t('goal_assignee', 'Assign to')}</Label>
                {assignee ? (
                  <div className="flex items-center gap-2 h-9 rounded-md border border-border bg-background px-2 text-sm">
                    <span className="truncate">{assignee.name}</span>
                    <button type="button" className="ml-auto text-muted-foreground hover:text-foreground" onClick={() => { setAssignee(null); setEmpQuery(''); }} aria-label={t('clear', 'Clear')}>×</button>
                  </div>
                ) : (
                  <>
                    <Input value={empQuery} onChange={(e) => setEmpQuery(e.target.value)} placeholder={t('assignee_search', 'Search team members…')} />
                    {empHits.length > 0 && (
                      <div className="absolute z-20 mt-1 w-full rounded-md border border-border bg-popover shadow-md max-h-48 overflow-auto">
                        {empHits.map((h) => (
                          // ⚠️ No login, no goal. Goals are keyed on the AUTH
                          // user id, so an employee record without one cannot
                          // own a goal or be emailed about it. Say so here
                          // rather than accepting the choice and dropping it.
                          <button key={h.id} type="button" disabled={!h.user_id}
                            className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                            title={h.user_id ? undefined : t('assignee_no_login', 'This person has no KaziPeople login yet, so they cannot be given a goal.') as string}
                            onClick={() => { if (!h.user_id) return; setAssignee({ userId: h.user_id, name: h.name }); setEmpHits([]); }}>
                            <span className="truncate">{h.name}</span>
                            {!h.user_id && <span className="text-[10px] text-muted-foreground">{t('no_login', 'no login')}</span>}
                            {h.sublabel && <span className="ml-auto text-xs text-muted-foreground truncate">{h.sublabel}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
                <p className="text-[11px] text-muted-foreground">{t('assignee_hint', 'Leave blank to assign to yourself. Search matches employees in your directory.')}</p>
              </div>
            )}
            <div className="space-y-1"><Label className="text-xs">{t('goal_category', 'Category')}</Label>
              <select className={sel} value={f.category_id || ''} onChange={(e) => setF({ ...f, category_id: e.target.value })}>
                <option value="">{t('cat_default', 'Business (default)')}</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.label}{!c.bonus_eligible && !c.counts_toward_score ? ` · ${t('cat_dev_only', 'never affects pay or score')}` : !c.bonus_eligible ? ` · ${t('cat_no_bonus', 'no bonus')}` : ''}</option>)}
              </select>
              {(() => { const c = categories.find((x) => x.id === f.category_id); return c && !c.bonus_eligible ? <p className="text-[11px] text-muted-foreground">{c.counts_toward_score ? t('cat_hint_score', 'Counts toward the review score, never toward a bonus.') : t('cat_hint_dev', 'Development only — never affects score or pay.')}</p> : null; })()}
            </div>
            {f.goal_level === 'department' && (
              <div className="space-y-1"><Label className="text-xs">{t('goal_department', 'Department')}</Label>
                <select className={sel} value={f.org_unit_id || ''} onChange={(e) => setF({ ...f, org_unit_id: e.target.value })}>
                  <option value="">{t('pick_department', 'Select a department…')}</option>
                  {dialogDepts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
            )}
            {f.goal_level !== 'company' && (
              <div className="space-y-1"><Label className="text-xs">{t('aligns_to', 'Aligns to')}</Label>
                <select className={sel} value={f.parent_goal_id || ''} onChange={(e) => setF({ ...f, parent_goal_id: e.target.value })}>
                  <option value="">{t('no_alignment', 'No alignment')}</option>
                  {orgGoals
                    .filter((og) => f.goal_level === 'department' ? og.goal_level === 'company' : true)
                    .map((og) => <option key={og.id} value={og.id}>{og.goal_level === 'company' ? '🏢 ' : '👥 '}{og.title}</option>)}
                </select>
              </div>
            )}
          </div>
          {numeric ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">{t('target', 'Target')}</Label><Input type="number" value={f.target ?? ''} onChange={(e) => setF({ ...f, target: e.target.value })} /></div>
              <div className="space-y-1"><Label className="text-xs">{t('actual', 'Actual')}</Label><Input type="number" value={f.actual ?? ''} onChange={(e) => setF({ ...f, actual: e.target.value })} placeholder={t('actual_hint', 'exceed target to overachieve')} /></div>
            </div>
          ) : (
            <div className="space-y-1"><Label className="text-xs">{t('progress', 'Progress')} (%)</Label>
              <Input type="number" min={0} value={f.progress ?? 0} onChange={(e) => setF({ ...f, progress: e.target.value })} />
              <p className="text-[11px] text-muted-foreground">{t('over_100_ok', 'You can enter above 100% — overachievement is preserved.')}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            {!editing && <div className="space-y-1"><Label className="text-xs">{t('due', 'Due date')}</Label><Input type="date" value={f.due_date || ''} onChange={(e) => setF({ ...f, due_date: e.target.value })} /></div>}
            <div className="space-y-1"><Label className="text-xs">{t('scoring_cap', 'Score cap %')}</Label><Input type="number" value={f.scoring_cap ?? ''} onChange={(e) => setF({ ...f, scoring_cap: e.target.value })} placeholder={t('optional', 'optional')} /></div>
          </div>
          {editing && (
            <>
              <div className="space-y-1"><Label className="text-xs">{t('status', 'Status')}</Label>
                <select className={sel} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
                  {['not_started', 'on_track', 'at_risk', 'completed', 'paused'].map((s) => <option key={s} value={s}>{human(s)}</option>)}
                </select>
              </div>
              {f.weight_locked && (
                <div className="space-y-1"><Label className="text-xs">{t('change_reason', 'Reason for change')}</Label>
                  <Input value={f.change_reason || ''} onChange={(e) => setF({ ...f, change_reason: e.target.value })} placeholder={t('reason_hint', 'required — weights are locked')} />
                </div>
              )}
              <ConnectedWorkSection goal={goal!} onChanged={onSaved} />
              {versions.length > 0 && (
                <div className="rounded-md border border-border p-2 space-y-1">
                  <div className="text-xs font-medium text-foreground">{t('change_history', 'Change history')}</div>
                  {versions.slice(0, 5).map((v) => (
                    <div key={v.id} className="text-[11px] text-muted-foreground">
                      {human(v.field)}: {v.previous_value || '—'} → {v.new_value || '—'}{v.reason ? ` · ${v.reason}` : ''} · {fmtDate(v.created_at)}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('cancel', 'Cancel')}</Button>
          <Button onClick={save} disabled={busy || (!editing && !f.title)}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('save', 'Save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---- 1:1 meetings -----------------------------------------------------------
const CADENCES = ['none', 'weekly', 'biweekly', 'monthly'];
const fmtDateTime = (d?: string | null) => d ? new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const OneOnOnesTab: React.FC = () => {
  const { t } = useTranslation('performance');
  const [rows, setRows] = useState<api.OneOnOne[]>([]);
  const [loading, setLoading] = useState(true);
  const [scheduling, setScheduling] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const load = () => api.listOneOnOnes().then(setRows).catch(() => setRows([])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  if (loading) return <Loading />;
  const now = Date.now();
  const upcoming = rows.filter((r) => r.status === 'scheduled' && (!r.scheduled_at || new Date(r.scheduled_at).getTime() >= now - 3600e3));
  const past = rows.filter((r) => !upcoming.includes(r));
  // US-06: a still-"scheduled" 1:1 whose time has passed must not read
  // "Scheduled" forever — a meeting whose time is an hour past is treated as
  // Completed for display (the meeting happened). Pure, instant-derived.
  const isPastScheduled = (r: api.OneOnOne) =>
    r.status === 'scheduled' && !!r.scheduled_at && new Date(r.scheduled_at).getTime() < now - 3600e3;
  // The status to SHOW for a 1:1: a past scheduled meeting reads Completed.
  const oneOnOneDisplayStatus = (r: api.OneOnOne) => (isPastScheduled(r) ? 'completed' : r.status);
  const Row = (r: api.OneOnOne) => (
    <button key={r.id} onClick={() => setOpenId(r.id)} className="w-full text-left rounded-lg border border-border bg-card hover:border-orange-300 transition p-3 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="font-medium text-foreground truncate">{r.title || t('one_on_one_with', '1:1 with {{name}}', { name: r.counterpart_name || t('a_colleague', 'a colleague') })}</div>
        <div className="text-xs text-muted-foreground">{fmtDateTime(r.scheduled_at)}{r.cadence !== 'none' ? ` · ${t(`cadence_${r.cadence}`, r.cadence)}` : ''}</div>
      </div>
      <Badge className={`${chip(oneOnOneDisplayStatus(r) === 'completed' ? 'completed' : oneOnOneDisplayStatus(r) === 'cancelled' ? 'at_risk' : 'active')} border-0 capitalize text-[10px]`}>{human(oneOnOneDisplayStatus(r))}</Badge>
    </button>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">{t('one_on_one_intro', 'Recurring 1:1s with a shared agenda, notes, and action items that carry over.')}</p>
        <Button size="sm" onClick={() => setScheduling(true)}><Plus className="mr-1.5 h-4 w-4" />{t('schedule_1on1', 'Schedule 1:1')}</Button>
      </div>
      {rows.length === 0 ? <SectionEmpty text={t('no_1on1', 'No 1:1s yet. Schedule a recurring check-in with your manager or a report.')} cta={{ label: t('schedule_1on1', 'Schedule 1:1'), onClick: () => setScheduling(true) }} /> : (
        <div className="space-y-4">
          {upcoming.length > 0 && <div className="space-y-2"><div className="text-xs font-semibold text-muted-foreground">{t('upcoming', 'Upcoming')}</div>{upcoming.map(Row)}</div>}
          {past.length > 0 && <div className="space-y-2"><div className="text-xs font-semibold text-muted-foreground">{t('past', 'Past')}</div>{past.map(Row)}</div>}
        </div>
      )}
      {scheduling && <ScheduleOneOnOneDialog onClose={() => setScheduling(false)} onSaved={(id) => { setScheduling(false); load(); setOpenId(id); }} />}
      {openId && <OneOnOneDetail id={openId} onClose={() => { setOpenId(null); load(); }} />}
    </div>
  );
};

/** "YYYY-MM-DDTHH:mm" for today at the given local time. */
const localTodayAt = (time: string): string => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${time}`;
};

const ScheduleOneOnOneDialog: React.FC<{ onClose: () => void; onSaved: (id: string) => void }> = ({ onClose, onSaved }) => {
  const { t } = useTranslation('performance');
  const [people, setPeople] = useState<api.FeedbackPerson[]>([]);
  // Default the date to TODAY at 09:00 rather than an empty mm/dd/yyyy
  // placeholder — the field is the one thing every 1:1 needs and it started
  // blank. Built from the local clock, not toISOString(), which is UTC and
  // would offer tomorrow to anybody west of UTC in the evening.
  const [f, setF] = useState({ with_user_id: '', title: '', scheduled_at: localTodayAt('09:00'), cadence: 'weekly' });
  const [carry, setCarry] = useState<api.OneOnOneItem[]>([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.getFeedbackPeople().then(setPeople).catch(() => setPeople([])); }, []);
  useEffect(() => { if (f.with_user_id) api.getOneOnOneOpenActions(f.with_user_id).then(setCarry).catch(() => setCarry([])); else setCarry([]); }, [f.with_user_id]);
  const submit = async () => {
    if (!f.with_user_id) { toast.error(t('pick_subject', 'Pick a colleague')); return; }
    setBusy(true);
    try {
      let browserZone: string | undefined;
      try { browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined; } catch { /* zone-less */ }
      const m = await api.createOneOnOne({ with_user_id: f.with_user_id, title: f.title || undefined, scheduled_at: f.scheduled_at ? new Date(f.scheduled_at).toISOString() : undefined, meeting_timezone: browserZone, cadence: f.cadence });
      toast.success(t('scheduled_1on1', '1:1 scheduled')); onSaved(m.id);
    } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } finally { setBusy(false); }
  };
  const sel = 'w-full h-9 rounded-md border border-border bg-background px-2.5 text-sm';
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{t('schedule_1on1', 'Schedule 1:1')}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div><Label className="text-xs">{t('with', 'With')}</Label>
            <select value={f.with_user_id} onChange={(e) => setF({ ...f, with_user_id: e.target.value })} className={sel}>
              <option value="">{t('select', 'Select…')}</option>
              {people.map((p) => <option key={p.user_id} value={p.user_id}>{p.name}</option>)}
            </select></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">{t('when', 'When')}</Label><DateTimeField value={f.scheduled_at} onChange={(v) => setF({ ...f, scheduled_at: v })} /></div>
            <div><Label className="text-xs">{t('cadence', 'Repeats')}</Label>
              <select value={f.cadence} onChange={(e) => setF({ ...f, cadence: e.target.value })} className={sel}>
                {CADENCES.map((c) => <option key={c} value={c}>{t(`cadence_${c}`, c)}</option>)}
              </select></div>
          </div>
          <div><Label className="text-xs">{t('title_optional', 'Title (optional)')}</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder={t('one_on_one_title_ph', 'Weekly check-in')} /></div>
          {carry.length > 0 && (
            <div className="rounded-md border border-border p-2.5">
              <div className="text-[11px] font-medium text-muted-foreground mb-1">{t('carry_over', 'Open action items to carry over')} ({carry.length})</div>
              {carry.slice(0, 5).map((a) => <div key={a.id} className="text-xs text-foreground truncate">• {a.text}</div>)}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('cancel', 'Cancel')}</Button>
          <Button onClick={submit} disabled={busy}>{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{t('schedule', 'Schedule')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const OneOnOneDetail: React.FC<{ id: string; onClose: () => void }> = ({ id, onClose }) => {
  const { t } = useTranslation('performance');
  const [m, setM] = useState<api.OneOnOne | null>(null);
  const [agenda, setAgenda] = useState('');
  const [action, setAction] = useState('');
  const [shared, setShared] = useState('');
  const [priv, setPriv] = useState('');
  const load = () => api.getOneOnOne(id).then((x) => { setM(x); setShared(x.shared_notes || ''); setPriv(x.my_private_note || ''); }).catch(() => setM(null));
  useEffect(() => { load(); }, [id]);
  if (!m) return null;
  const items = m.items || [];
  const agendaItems = items.filter((i) => i.kind === 'agenda');
  const actionItems = items.filter((i) => i.kind === 'action');

  const addItem = async (kind: 'agenda' | 'action', text: string, reset: () => void) => {
    if (!text.trim()) return;
    try { await api.addOneOnOneItem(id, { kind, text: text.trim() }); reset(); load(); } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); }
  };
  const toggle = async (it: api.OneOnOneItem) => { try { await api.updateOneOnOneItem(id, it.id, { done: !it.done }); load(); } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } };
  const del = async (it: api.OneOnOneItem) => { try { await api.deleteOneOnOneItem(id, it.id); load(); } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } };
  const saveShared = async () => { try { await api.updateOneOnOne(id, { shared_notes: shared }); toast.success(t('saved', 'Saved')); } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } };
  const savePriv = async () => { try { await api.saveOneOnOnePrivateNote(id, priv); toast.success(t('saved', 'Saved')); } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } };
  const setStatus = async (status: string) => { try { await api.updateOneOnOne(id, { status }); load(); } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } };

  const ItemRow = (it: api.OneOnOneItem) => (
    <div key={it.id} className="flex items-center gap-2 text-sm group">
      <input type="checkbox" checked={it.done} onChange={() => toggle(it)} className="h-4 w-4 accent-orange-500" />
      <span className={`flex-1 ${it.done ? 'line-through text-muted-foreground' : 'text-foreground'}`}>{it.text}</span>
      <button onClick={() => del(it)} className="text-muted-foreground hover:text-red-500 opacity-0 group-hover:opacity-100"><Trash2 className="h-3.5 w-3.5" /></button>
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{m.title || t('one_on_one_with', '1:1 with {{name}}', { name: m.counterpart_name || t('a_colleague', 'a colleague') })}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-1 max-h-[66vh] overflow-y-auto">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{fmtDateTime(m.scheduled_at)}{m.cadence !== 'none' ? ` · ${t(`cadence_${m.cadence}`, m.cadence)}` : ''}</span>
            {/* US-06: a past scheduled 1:1 reads Completed (display-derived); the
                stored status only changes if the user actively picks a value. */}
            <select value={m.status === 'scheduled' && m.scheduled_at && new Date(m.scheduled_at).getTime() < Date.now() - 3600e3 ? 'completed' : m.status} onChange={(e) => setStatus(e.target.value)} className="h-7 rounded-md border border-border bg-background px-1.5 text-xs">
              {['scheduled', 'completed', 'cancelled'].map((s) => <option key={s} value={s}>{human(s)}</option>)}
            </select>
          </div>

          <div className="space-y-2">
            <div className="text-xs font-semibold text-muted-foreground">{t('agenda', 'Agenda')}</div>
            {agendaItems.map(ItemRow)}
            <div className="flex gap-2"><Input value={agenda} onChange={(e) => setAgenda(e.target.value)} placeholder={t('add_agenda', 'Add a talking point')} onKeyDown={(e) => e.key === 'Enter' && addItem('agenda', agenda, () => setAgenda(''))} className="h-8" /><Button size="sm" variant="outline" onClick={() => addItem('agenda', agenda, () => setAgenda(''))}><Plus className="h-4 w-4" /></Button></div>
          </div>

          <div className="space-y-2 border-t border-border pt-3">
            <div className="text-xs font-semibold text-muted-foreground">{t('action_items', 'Action items')}</div>
            {actionItems.map(ItemRow)}
            <div className="flex gap-2"><Input value={action} onChange={(e) => setAction(e.target.value)} placeholder={t('add_action', 'Add an action item')} onKeyDown={(e) => e.key === 'Enter' && addItem('action', action, () => setAction(''))} className="h-8" /><Button size="sm" variant="outline" onClick={() => addItem('action', action, () => setAction(''))}><Plus className="h-4 w-4" /></Button></div>
          </div>

          <div className="space-y-1 border-t border-border pt-3">
            <Label className="text-xs">{t('shared_notes', 'Shared notes')} <span className="text-muted-foreground">({t('both_see', 'both of you see these')})</span></Label>
            <textarea value={shared} onChange={(e) => setShared(e.target.value)} onBlur={saveShared} className="w-full min-h-[70px] rounded-md border border-border bg-background px-2.5 py-2 text-sm" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t('private_note', 'Your private note')} <span className="text-muted-foreground">({t('only_you', 'only you')})</span></Label>
            <textarea value={priv} onChange={(e) => setPriv(e.target.value)} onBlur={savePriv} className="w-full min-h-[60px] rounded-md border border-border bg-muted/40 px-2.5 py-2 text-sm" />
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>{t('close', 'Close')}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---- Achievements -----------------------------------------------------------
// The Recognition tab is now the Community feed (feed + reactions + comments +
// composer). See ./RecognitionFeed.
const RecognitionTab: React.FC = () => <RecognitionFeed />;

const AchievementsTab: React.FC = () => {
  const { t } = useTranslation('performance');
  const [rows, setRows] = useState<api.Achievement[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState<any>({});
  const [busy, setBusy] = useState(false);
  const load = () => api.listAchievements().then(setRows).catch(() => setRows([])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.createAchievement({ title: f.title, description: f.description, impact: f.impact, tags: (f.tags || '').split(',').map((x: string) => x.trim()).filter(Boolean) });
      toast.success(t('ach_added', 'Achievement logged')); setOpen(false); setF({}); load();
    } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } finally { setBusy(false); }
  };
  const del = async (id: string) => { try { await api.deleteAchievement(id); load(); } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } };

  if (loading) return <Loading />;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t('ach_intro', 'Your performance journal — log accomplishments throughout the year.')}</p>
        <Button size="sm" onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-1" /> {t('add_achievement', 'Add')}</Button>
      </div>
      {rows.length === 0 ? <Empty text={t('no_achievements', 'No achievements yet. Log your wins as they happen — Dana can help.')} /> : (
        <div className="space-y-3">
          {rows.map((a) => (
            <Card key={a.id}><CardContent className="pt-4 flex items-start justify-between gap-3 group">
              <div className="min-w-0">
                <div className="font-semibold text-foreground flex items-center gap-2"><Trophy className="w-4 h-4 text-orange-500 shrink-0" /> {a.title}</div>
                {a.description && <div className="text-sm text-muted-foreground">{a.description}</div>}
                {a.impact && <div className="text-sm text-orange-700 dark:text-orange-300 mt-0.5">{t('impact', 'Impact')}: {a.impact}</div>}
                <div className="flex flex-wrap gap-1 mt-1">{(a.tags || []).map((x) => <span key={x} className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{x}</span>)}<span className="text-[10px] text-muted-foreground ml-1">{fmtDate(a.entry_date)}</span></div>
              </div>
              <button type="button" onClick={() => del(a.id)} className="text-muted-foreground hover:text-red-500 opacity-0 group-hover:opacity-100"><Trash2 className="w-4 h-4" /></button>
            </CardContent></Card>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('add_achievement', 'Add achievement')}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1"><Label className="text-xs">{t('goal_title', 'Title')}</Label><Input value={f.title || ''} onChange={(e) => setF({ ...f, title: e.target.value })} /></div>
            <div className="space-y-1"><Label className="text-xs">{t('goal_desc', 'Description')}</Label><Input value={f.description || ''} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
            <div className="space-y-1"><Label className="text-xs">{t('impact', 'Impact')}</Label><Input value={f.impact || ''} onChange={(e) => setF({ ...f, impact: e.target.value })} /></div>
            <div className="space-y-1"><Label className="text-xs">{t('tags', 'Tags (comma-separated)')}</Label><Input value={f.tags || ''} onChange={(e) => setF({ ...f, tags: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t('cancel', 'Cancel')}</Button>
            <Button onClick={save} disabled={busy || !f.title}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('save', 'Save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---- Reviews (self) ---------------------------------------------------------
const SELF_SECTIONS = ['Achievements', 'Goal Progress', 'Strengths', 'Development Areas', 'Summary'];

const ReviewsTab: React.FC = () => {
  const { t } = useTranslation('performance');
  const companyName = useAppSelector(selectTenant)?.name ?? '';
  const [reviews, setReviews] = useState<api.Review[]>([]);
  const [cycles, setCycles] = useState<api.Cycle[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  // Written mode: sections from the default template. Goal mode: rate each goal.
  const [sections, setSections] = useState<string[]>(SELF_SECTIONS);
  const [goals, setGoals] = useState<api.Goal[]>([]);
  const [scale, setScale] = useState<api.RatingOption[]>([]);
  const [gRate, setGRate] = useState<Record<string, string>>({});
  const [gNote, setGNote] = useState<Record<string, string>>({});
  const [comps, setComps] = useState<api.CycleCompetency[]>([]);
  const [cRate, setCRate] = useState<Record<string, string>>({});
  const [cNote, setCNote] = useState<Record<string, string>>({});
  const load = () => Promise.all([api.listMyReviews().catch(() => []), api.listCycles().catch(() => [])]).then(([r, c]) => { setReviews(r); setCycles(c); }).finally(() => setLoading(false));
  useEffect(() => {
    load();
    api.listTemplates().then((tl) => { const def = tl.find((x) => x.is_default) || tl[0]; const s = def && Array.isArray(def.sections) ? def.sections.map((x: any) => typeof x === 'string' ? x : (x?.title || x?.name || '')).filter(Boolean) : []; if (s.length) setSections(s); }).catch(() => {});
    api.listGoals().then((g) => setGoals(g.filter((x) => x.status !== 'archived'))).catch(() => {});
    api.getRatingScales().then((sc) => { const d = sc.find((x) => x.is_default) || sc[0]; if (d) setScale([...d.options].sort((a, b) => a.ordinal - b.ordinal)); }).catch(() => {});
  }, []);

  const activeCycle = cycles.find((c) => c.status === 'active') || cycles[0];
  const goalBased = activeCycle?.assessment_style === 'goal_based';
  const rateComp = !!activeCycle?.rate_competencies;
  const current = reviews.find((r) => r.status !== 'finalized');
  const past = reviews.filter((r) => r.status === 'finalized');

  // Prefill saved self ratings so a returning reviewer resumes where they left off.
  useEffect(() => {
    if (!goalBased || !current?.id) return;
    api.listGoalRatings(current.id).then((rs) => {
      const r: Record<string, string> = {}, n: Record<string, string> = {};
      rs.filter((x) => x.author === 'self').forEach((x) => { r[x.goal_id] = String(x.rating); if (x.comment) n[x.goal_id] = x.comment; });
      if (Object.keys(r).length) setGRate(r); if (Object.keys(n).length) setGNote(n);
    }).catch(() => {});
  }, [goalBased, current?.id]);

  // Competency ratings: load the cycle's assigned set + prefill saved self ratings.
  useEffect(() => {
    if (!rateComp || !activeCycle?.id) { setComps([]); return; }
    api.getCycleCompetencies(activeCycle.id).then(setComps).catch(() => setComps([]));
  }, [rateComp, activeCycle?.id]);
  useEffect(() => {
    if (!rateComp || !current?.id) return;
    api.listCompetencyRatings(current.id).then((rs) => {
      const r: Record<string, string> = {}, n: Record<string, string> = {};
      rs.filter((x) => x.author === 'self').forEach((x) => { r[x.competency_id] = String(x.rating); if (x.comment) n[x.competency_id] = x.comment; });
      if (Object.keys(r).length) setCRate(r); if (Object.keys(n).length) setCNote(n);
    }).catch(() => {});
  }, [rateComp, current?.id]);

  const save = async (submit: boolean) => {
    if (!activeCycle) { toast.error(t('no_cycle', 'No active review cycle')); return; }
    setBusy(true);
    try {
      const responses = goalBased
        ? goals.filter((g) => gRate[g.id] || gNote[g.id]?.trim()).map((g) => ({ section: g.id, rating: gRate[g.id] ? Number(gRate[g.id]) : undefined, answer: gNote[g.id] || '' }))
        : sections.filter((s) => form[s]?.trim()).map((s) => ({ section: s, answer: form[s] }));
      const rev = await api.submitSelfReview({ cycle_id: activeCycle.id, submit, responses });
      // Goal-based: persist each self rating to the authoritative per-goal store
      // (drives the manager's side-by-side view + the weighted rollup).
      if (goalBased && rev?.id) {
        await Promise.all(goals.filter((g) => gRate[g.id]).map((g) =>
          api.saveGoalRating(rev.id, g.id, { author: 'self', rating: Number(gRate[g.id]), comment: gNote[g.id]?.trim() || undefined }).catch(() => {})));
      }
      if (rateComp && rev?.id) {
        await Promise.all(comps.filter((c) => cRate[c.competency_id]).map((c) =>
          api.saveCompetencyRating(rev.id, c.competency_id, { author: 'self', rating: Number(cRate[c.competency_id]), comment: cNote[c.competency_id]?.trim() || undefined }).catch(() => {})));
      }
      toast.success(submit ? t('self_submitted', 'Self-review submitted') : t('self_saved', 'Draft saved'));
      load();
    } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } finally { setBusy(false); }
  };

  if (loading) return <Loading />;
  const locked = current?.self_review_status === 'submitted';
  return (
    <div className="space-y-5">
      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-base">{t('current_review', 'Current self-review')}{activeCycle ? ` · ${activeCycle.name}` : ''}</CardTitle>
          <AskDana q="Draft my self-review from my goals and achievements" />
        </CardHeader>
        <CardContent className="space-y-3">
          {current && <div className="text-xs"><Badge className={`${chip(current.self_review_status)} border-0 capitalize`}>{t('self', 'Self')}: {human(current.self_review_status)}</Badge></div>}
          {goalBased ? (
            goals.length === 0 ? <p className="text-sm text-muted-foreground">{t('no_goals_to_rate', 'No goals to rate. Add goals in the Goals tab first.')}</p> :
              goals.map((g, i) => (
                <div key={g.id} className="rounded-lg border border-border p-3 space-y-2">
                  <div className="text-sm font-medium text-foreground flex items-center gap-2 flex-wrap">
                    <span>{t('rate_yourself_goal', 'Rate yourself on goal')} #{i + 1}: {g.title}</span>
                    <span className="text-xs text-muted-foreground">· {t('weight', 'Weight')} {g.weight}%</span>
                    {g.stretch_achieved && <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300 border-0 text-[10px]">🎯 {t('stretch_met', 'Stretch met')}</Badge>}
                  </div>
                  <select disabled={locked} value={gRate[g.id] || ''} onChange={(e) => setGRate({ ...gRate, [g.id]: e.target.value })} className="w-full h-9 rounded-md border border-border bg-background px-2.5 text-sm disabled:opacity-60">
                    <option value="">{t('select_rating', 'Select a rating…')}</option>
                    {scale.map((o) => <option key={o.ordinal} value={o.value}>{o.value} – {o.label}</option>)}
                  </select>
                  <textarea disabled={locked} value={gNote[g.id] || ''} onChange={(e) => setGNote({ ...gNote, [g.id]: e.target.value })} placeholder={t('additional_details', 'Additional details (optional)')} className="w-full min-h-[56px] rounded-md border border-border bg-background px-2.5 py-2 text-sm disabled:opacity-60" />
                </div>
              ))
          ) : sections.map((s) => (
            <div key={s} className="space-y-1">
              <Label className="text-xs">{t(`sec_${s.replace(/ /g, '_').toLowerCase()}`, s)}</Label>
              <textarea
                disabled={locked}
                value={form[s] || ''}
                onChange={(e) => setForm({ ...form, [s]: e.target.value })}
                className="w-full min-h-[64px] rounded-md border border-border bg-background px-2.5 py-2 text-sm disabled:opacity-60"
              />
            </div>
          ))}
          {rateComp && comps.length > 0 && (
            <div className="space-y-2 border-t border-border pt-3">
              <div className="text-xs font-semibold text-muted-foreground">{t('competencies', 'Competencies')}</div>
              {comps.map((c) => (
                <div key={c.competency_id} className="rounded-lg border border-border p-3 space-y-2">
                  <div className="text-sm font-medium text-foreground">{c.name} <span className="text-xs text-muted-foreground">· {t('weight', 'Weight')} {c.weight}%</span></div>
                  <select disabled={locked} value={cRate[c.competency_id] || ''} onChange={(e) => setCRate({ ...cRate, [c.competency_id]: e.target.value })} className="w-full h-9 rounded-md border border-border bg-background px-2.5 text-sm disabled:opacity-60">
                    <option value="">{t('select_rating', 'Select a rating…')}</option>
                    {scale.map((o) => <option key={o.ordinal} value={o.value}>{o.value} – {o.label}</option>)}
                  </select>
                  <textarea disabled={locked} value={cNote[c.competency_id] || ''} onChange={(e) => setCNote({ ...cNote, [c.competency_id]: e.target.value })} placeholder={t('additional_details', 'Additional details (optional)')} className="w-full min-h-[48px] rounded-md border border-border bg-background px-2.5 py-2 text-sm disabled:opacity-60" />
                </div>
              ))}
            </div>
          )}
          {locked ? (
            <p className="text-sm text-muted-foreground">{t('self_locked', 'Your self-review has been submitted.')}</p>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => save(false)} disabled={busy}>{t('save_draft', 'Save draft')}</Button>
              <Button size="sm" onClick={() => save(true)} disabled={busy}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('submit', 'Submit')}</Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">{t('past_reviews', 'Past reviews')}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {past.length === 0 ? <Empty text={t('no_past', 'No finalized reviews yet.')} /> : past.map((r) => {
            const ack = async () => { try { await api.acknowledgeReview(r.id); toast.success(t('acknowledged_ok', 'Review acknowledged')); load(); } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } };
            const dl = () => api.downloadReviewPDF(r.id, `performance-review-${fmtDate(r.finalized_at)}.pdf`, companyName).catch((e: any) => toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))));
            return (
              <div key={r.id} className="flex items-center justify-between gap-2 text-sm border-b border-border pb-1.5 last:border-0">
                <span>{fmtDate(r.finalized_at)}</span>
                <div className="flex items-center gap-2">
                  {/* Finalized ≠ visible: only show the rating once released to the employee. */}
                  {r.released_at
                    ? <span className="font-medium">{t('rating', 'Rating')}: {r.final_rating ?? '—'}</span>
                    : <span className="text-muted-foreground text-xs">{t('not_released', 'Awaiting release')}</span>}
                  {r.released_at && <Button size="sm" variant="outline" onClick={dl}>{t('download_pdf', 'Download PDF')}</Button>}
                  {r.released_at && !r.acknowledged_at && <Button size="sm" variant="outline" onClick={ack}>{t('acknowledge', 'Acknowledge')}</Button>}
                  {r.acknowledged_at && <Badge className="bg-success-soft text-success-strong border-0 text-[10px]">{t('acknowledged', 'Acknowledged')}</Badge>}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <MyDevelopmentPlans />
    </div>
  );
};

// Employee-owned development plans: growth title + typed actions (track P:
// Performance × Learning). Action edits go through the action endpoints; the
// legacy PUT-plan reconcile is kept only as a fallback for id-less actions.
const ACTION_TYPES: api.DevActionType[] = ['manual', 'training', 'goal'];
const actionStatusOf = (a: api.DevAction): api.DevActionStatus => a.status || (a.done ? 'done' : 'open');
const EVIDENCED_CHIP = 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300';

const MyDevelopmentPlans: React.FC = () => {
  const { t } = useTranslation('performance');
  const canAssign = useHasPermission('learning:assign');
  const [plans, setPlans] = useState<api.DevPlan[]>([]);
  const [title, setTitle] = useState('');
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<Record<string, { text: string; type: api.DevActionType; target_date: string }>>({});
  const [assignFor, setAssignFor] = useState<{ plan: api.DevPlan; action: api.DevAction } | null>(null);
  const [evidence, setEvidence] = useState<api.DevEvidence[] | null>(null);
  const fail = (e: any) => toast.error(getApiErrorMessage(e, t('err', 'Something went wrong')));
  const load = () => {
    api.listDevPlans().then(setPlans).catch(() => setPlans([]));
    api.listDevEvidence().then(setEvidence).catch(() => setEvidence([]));
  };
  useEffect(() => { load(); }, []);

  const typeLabel = (ty: api.DevActionType) => ty === 'training' ? t('action_type_training', 'Training') : ty === 'goal' ? t('action_type_goal', 'Goal') : t('action_type_manual', 'Manual');
  const statusLabel = (st: api.DevActionStatus) => st === 'done' ? t('action_status_done', 'Done') : st === 'evidenced' ? t('action_status_evidenced', 'Evidenced') : t('action_status_open', 'Open');
  const refKindLabel = (k: api.DevLearningRefKind) => k === 'assignment' ? t('ref_kind_assignment', 'Assignment') : k === 'program' ? t('ref_kind_program', 'Program') : k === 'session' ? t('ref_kind_session', 'Session') : t('ref_kind_course', 'Course');

  const add = async () => {
    if (!title.trim()) return;
    try { await api.createDevPlan({ title: title.trim(), actions: [] }); setTitle(''); setAdding(false); load(); }
    catch (e: any) { fail(e); }
  };
  // Legacy whole-plan reconcile — only for actions the server never gave an id.
  const legacyPatch = async (p: api.DevPlan, actions: api.DevAction[]) => {
    try { await api.updateDevPlan(p.id, { title: p.title, description: p.description, actions, status: p.status }); load(); }
    catch (e: any) { fail(e); }
  };
  const draftOf = (planId: string) => draft[planId] || { text: '', type: 'manual' as api.DevActionType, target_date: '' };
  const setDraftOf = (planId: string, patch: Partial<{ text: string; type: api.DevActionType; target_date: string }>) =>
    setDraft((s) => ({ ...s, [planId]: { ...draftOf(planId), ...patch } }));
  const addAction = async (p: api.DevPlan) => {
    const d = draftOf(p.id);
    const txt = d.text.trim();
    if (!txt) return;
    try {
      await api.addDevAction(p.id, { title: txt, type: d.type, target_date: d.target_date || undefined });
      setDraft((s) => ({ ...s, [p.id]: { text: '', type: d.type, target_date: '' } }));
      load();
    } catch (e: any) { fail(e); }
  };
  const toggle = async (p: api.DevPlan, a: api.DevAction) => {
    if (!a.id) return legacyPatch(p, p.actions.map((x) => x === a ? { ...x, done: !x.done } : x));
    try { await api.updateDevAction(p.id, a.id, { status: actionStatusOf(a) === 'done' ? 'open' : 'done' }); load(); }
    catch (e: any) { fail(e); }
  };
  const delAction = async (p: api.DevPlan, a: api.DevAction) => {
    if (!a.id) return legacyPatch(p, p.actions.filter((x) => x !== a));
    try { await api.deleteDevAction(p.id, a.id); load(); }
    catch (e: any) { fail(e); }
  };
  const delPlan = async (p: api.DevPlan) => { try { await api.deleteDevPlan(p.id); load(); } catch (e: any) { fail(e); } };
  const sel = 'h-8 rounded-md border border-border bg-background px-2 text-xs';

  return (
    <>
      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-base">{t('dev_plans', 'Development plans')}</CardTitle>
          <div className="flex gap-1.5">
            <Button size="sm" variant="ghost" onClick={() => { window.location.href = '/app/training'; }}>{t('find_course', 'Find a course')}</Button>
            <Button size="sm" variant="outline" onClick={() => setAdding((v) => !v)}><Plus className="w-4 h-4 mr-1" />{t('add', 'Add')}</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {adding && (
            <div className="flex gap-2">
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('dev_plan_title', 'e.g. Grow into a team-lead role')} onKeyDown={(e) => e.key === 'Enter' && add()} />
              <Button size="sm" onClick={add}>{t('save', 'Save')}</Button>
            </div>
          )}
          {plans.length === 0 && !adding ? <Empty text={t('no_dev_plans', 'No development plans yet. Add growth goals to work toward.')} /> : plans.map((p) => {
            const d = draftOf(p.id);
            return (
              <div key={p.id} className="rounded-md border border-border p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-sm text-foreground">{p.title}</div>
                  <button onClick={() => delPlan(p)} className="text-muted-foreground hover:text-red-500" aria-label={t('delete', 'Delete')}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
                {p.actions.map((a, i) => {
                  const st = actionStatusOf(a);
                  const ty = a.type || 'manual';
                  return (
                    <div key={a.id || i} className="flex items-start gap-2 text-sm">
                      <input type="checkbox" checked={st === 'done'} onChange={() => toggle(p, a)} className="h-4 w-4 mt-0.5 accent-orange-500 cursor-pointer" />
                      <div className="flex-1 min-w-0">
                        <span className={st === 'done' ? 'line-through text-muted-foreground' : 'text-foreground'}>{a.text}</span>
                        <div className="flex flex-wrap items-center gap-1 mt-0.5">
                          <Badge className={`${ty === 'training' ? 'bg-info-soft text-info-strong' : ty === 'goal' ? 'bg-[#EDE9FE] text-[#4C1D95] dark:bg-purple-500/15 dark:text-purple-300' : 'bg-[#F0E8DC] text-[#4A5166] dark:bg-white/10 dark:text-slate-300'} border-0 text-[10px]`}>{typeLabel(ty)}</Badge>
                          <Badge
                            className={`${st === 'done' ? 'bg-success-soft text-success-strong' : st === 'evidenced' ? EVIDENCED_CHIP : 'bg-[#F0E8DC] text-[#4A5166] dark:bg-white/10 dark:text-slate-300'} border-0 text-[10px]`}
                            title={st === 'evidenced' ? t('evidenced_hint', 'Learning evidence attached, waiting for you or your manager to close') : undefined}
                          >{statusLabel(st)}</Badge>
                          {a.target_date && <span className="text-[11px] text-muted-foreground">{t('target_date', 'Target')}: {fmtDate(a.target_date)}</span>}
                          {a.learning_ref && <span className="text-[11px] text-muted-foreground">{t('linked_to_learning', 'Linked to Learning')} · {refKindLabel(a.learning_ref.kind)}</span>}
                        </div>
                      </div>
                      {canAssign && a.id && st !== 'done' && (
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs shrink-0" onClick={() => setAssignFor({ plan: p, action: a })}>{t('assign_training', 'Assign training')}</Button>
                      )}
                      <button onClick={() => delAction(p, a)} className="text-muted-foreground hover:text-red-500 mt-1 shrink-0" aria-label={t('delete', 'Delete')}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  );
                })}
                <div className="flex flex-wrap gap-2 items-center">
                  <Input value={d.text} onChange={(e) => setDraftOf(p.id, { text: e.target.value })} placeholder={t('add_action', 'Add an action')} onKeyDown={(e) => e.key === 'Enter' && addAction(p)} className="h-8 flex-1 min-w-[10rem]" />
                  <select value={d.type} onChange={(e) => setDraftOf(p.id, { type: e.target.value as api.DevActionType })} className={sel} aria-label={t('action_type', 'Type')}>
                    {ACTION_TYPES.map((ty) => <option key={ty} value={ty}>{typeLabel(ty)}</option>)}
                  </select>
                  <Input type="date" value={d.target_date} onChange={(e) => setDraftOf(p.id, { target_date: e.target.value })} className="h-8 w-auto text-xs" aria-label={t('target_date', 'Target')} />
                  <Button size="sm" variant="outline" onClick={() => addAction(p)}><Plus className="h-4 w-4" /></Button>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <LearningEvidenceCard evidence={evidence} />

      {assignFor && (
        <AssignTrainingDialog plan={assignFor.plan} action={assignFor.action} onClose={() => setAssignFor(null)} onDone={() => { setAssignFor(null); load(); }} />
      )}
    </>
  );
};

// Assign a course or an upcoming session to a development action (learning:assign).
// The backend validates reach + exactly-one-of course/session; its message is surfaced as-is.
const AssignTrainingDialog: React.FC<{ plan: api.DevPlan; action: api.DevAction; onClose: () => void; onDone: () => void }> = ({ plan, action, onClose, onDone }) => {
  const { t } = useTranslation('performance');
  const [mode, setMode] = useState<'course' | 'session'>('course');
  const [courses, setCourses] = useState<Course[] | null>(null);
  const [sessions, setSessions] = useState<LearningSession[] | null>(null);
  const [courseId, setCourseId] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [due, setDue] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    listCourses().then((c) => setCourses(c.filter((x) => x.active))).catch(() => setCourses([]));
    const from = new Date(); const to = new Date(from.getTime() + 180 * 86400000);
    listSessions(from.toISOString(), to.toISOString())
      .then((s) => setSessions(s.filter((x) => x.status === 'scheduled' || x.status === 'in_progress')))
      .catch(() => setSessions([]));
  }, []);
  const chosen = mode === 'course' ? courseId : sessionId;
  const submit = async () => {
    if (!chosen || !action.id) return;
    setBusy(true);
    try {
      await api.assignDevActionTraining(plan.id, action.id, {
        ...(mode === 'course' ? { course_id: chosen } : { session_id: chosen }),
        due_date: due || undefined, note: note.trim() || undefined,
      });
      toast.success(t('training_assigned', 'Training assigned'));
      onDone();
    } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } finally { setBusy(false); }
  };
  const sel = 'w-full h-9 rounded-md border border-border bg-background px-2.5 text-sm';
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{t('assign_training', 'Assign training')}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <p className="text-xs text-muted-foreground">{t('assign_training_for', 'For action')}: <span className="text-foreground">{action.text}</span></p>
          <div className="flex gap-1.5">
            <Button size="sm" variant={mode === 'course' ? 'default' : 'outline'} onClick={() => setMode('course')}>{t('course', 'Course')}</Button>
            <Button size="sm" variant={mode === 'session' ? 'default' : 'outline'} onClick={() => setMode('session')}>{t('session', 'Session')}</Button>
          </div>
          {mode === 'course' ? (
            <div><Label className="text-xs">{t('course', 'Course')}</Label>
              <select value={courseId} onChange={(e) => setCourseId(e.target.value)} className={sel} disabled={courses === null}>
                <option value="">{courses === null ? t('loading', 'Loading…') : t('pick_course', 'Select a course…')}</option>
                {(courses || []).map((c) => <option key={c.id} value={c.id}>{c.title}{c.provider_name ? ` · ${c.provider_name}` : ''}</option>)}
              </select></div>
          ) : (
            <div><Label className="text-xs">{t('session', 'Session')}</Label>
              <select value={sessionId} onChange={(e) => setSessionId(e.target.value)} className={sel} disabled={sessions === null}>
                <option value="">{sessions === null ? t('loading', 'Loading…') : sessions.length === 0 ? t('no_upcoming_sessions', 'No upcoming sessions') : t('pick_session', 'Select a session…')}</option>
                {(sessions || []).map((s) => <option key={s.id} value={s.id}>{s.title} · {fmtDateTime(s.starts_at)}{s.program_name ? ` · ${s.program_name}` : ''}</option>)}
              </select></div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">{t('due', 'Due date')}</Label><Input type="date" value={due} onChange={(e) => setDue(e.target.value)} /></div>
            <div><Label className="text-xs">{t('note', 'Note')}</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('optional', 'Optional')} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('cancel', 'Cancel')}</Button>
          <Button onClick={submit} disabled={busy || !chosen}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t('assign', 'Assign')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Evidence from Learning: read-only timeline (completions, verified practice,
// certifications, readiness). Evidence never changes goals or ratings on its own.
const LearningEvidenceCard: React.FC<{ evidence: api.DevEvidence[] | null }> = ({ evidence }) => {
  const { t } = useTranslation('performance');
  const sourceLabel = (s: api.DevEvidenceSourceType) =>
    s === 'training_completion' ? t('ev_training_completion', 'Training completed')
      : s === 'training_verified' ? t('ev_training_verified', 'Training verified')
        : s === 'practice_completed' ? t('ev_practice_completed', 'Practice completed')
          : s === 'certification_issued' ? t('ev_certification_issued', 'Certification issued')
            : t('ev_readiness_ready', 'Readiness reached');
  const rows = useMemo(() => [...(evidence || [])].sort((a, b) => (b.occurred_at || '').localeCompare(a.occurred_at || '')), [evidence]);
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">{t('learning_evidence', 'Evidence from Learning')}</CardTitle></CardHeader>
      <CardContent>
        {evidence === null ? <div className="h-10 rounded bg-muted animate-pulse" />
          : rows.length === 0 ? <Empty text={t('learning_evidence_empty', 'Completed training, validated practice and certifications will appear here as evidence. They never change goals or ratings on their own.')} />
            : <ul className="space-y-3">{rows.map((ev) => (
              <li key={ev.id} className="border-l-2 border-border pl-3 text-sm">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="font-medium text-foreground">{ev.title}</span>
                  {ev.verified && <Badge className="bg-success-soft text-success-strong border-0 text-[10px]">{t('verified', 'Verified')}</Badge>}
                  <Badge className="bg-[#F0E8DC] text-[#4A5166] dark:bg-white/10 dark:text-slate-300 border-0 text-[10px]">{sourceLabel(ev.source_type)}</Badge>
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  {fmtDateTime(ev.occurred_at)}
                  {ev.action_title && <> · {t('evidence_for_action', 'Action')}: {ev.action_title}</>}
                </div>
              </li>
            ))}</ul>}
      </CardContent>
    </Card>
  );
};

// ---- Team Reviews (manager/HR) ---------------------------------------------
const TeamReviewsTab: React.FC<{ isHR: boolean }> = ({ isHR }) => {
  const { t } = useTranslation('performance');
  const companyName = useAppSelector(selectTenant)?.name ?? '';
  const [rows, setRows] = useState<api.Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState<api.Review | null>(null);
  const [wizard, setWizard] = useState(false);
  const [calibrate, setCalibrate] = useState(false);
  const [pips, setPips] = useState(false);
  const [names, setNames] = useState<Record<string, string>>({});
  // Whether this workspace lets a manager release their own report's review.
  const [managerSelfRelease, setManagerSelfRelease] = useState(false);
  const load = () => api.listTeamReviews().then(setRows).catch(() => setRows([])).finally(() => setLoading(false));
  useEffect(() => {
    load();
    // Resolve employee_id (auth id) → display name from the roster.
    api.getFeedbackPeople().then((ppl) => { const m: Record<string, string> = {}; ppl.forEach((p) => { m[p.user_id] = p.name; }); setNames(m); }).catch(() => {});
    api.getPerfSettings().then((s) => setManagerSelfRelease(!!s.manager_self_release)).catch(() => {});
  }, []);
  const nameOf = (uid: string) => names[uid] || `${t('employee', 'Employee')} ${uid.slice(0, 8)}`;

  if (loading) return <Loading />;
  // The review-cycle wizard is a full-page guided flow, not a modal.
  if (wizard) return <ReviewCycleWizard onClose={() => setWizard(false)} onLaunched={() => { setWizard(false); load(); }} />;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground">{t('team_intro', isHR ? 'All reviews in your company.' : 'Reviews for your direct reports.')}</p>
        {isHR && <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => setPips(true)}>{t('improvement_plans', 'Improvement plans')}</Button>
          <Button size="sm" variant="outline" onClick={() => setCalibrate(true)}>{t('calibrate', 'Calibrate')}</Button>
          <Button size="sm" onClick={() => setWizard(true)}><Plus className="mr-1.5 h-4 w-4" />{t('launch_cycle', 'Create review cycle')}</Button>
        </div>}
      </div>
      {calibrate && <CalibrationDialog onClose={() => setCalibrate(false)} onChanged={load} />}
      {pips && <PIPDialog onClose={() => setPips(false)} />}
      {rows.length === 0 ? <Empty text={t('no_team', 'No reviews to action.')} /> : (
        <div className="space-y-2">
          {rows.map((r) => (
            <Card key={r.id}><CardContent className="pt-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium text-foreground text-sm">{nameOf(r.employee_id)}</div>
                {/* US-10: one canonical status per person per cycle. derived_status
                    is computed server-side by a documented precedence and already
                    reflects acknowledged/released, so a single chip replaces the
                    old self/manager/overall trio that could contradict each other. */}
                <div className="flex gap-1.5 mt-1">
                  <Badge className={`${chip(r.derived_status || r.status)} border-0 capitalize text-[10px]`}>{human(r.derived_status || r.status)}</Badge>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                {/* Release makes a finalized review visible to the employee. */}
                {isHR && r.status === 'finalized' && !r.released_at && (
                  <Button size="sm" onClick={async () => { try { await api.releaseReview(r.id); toast.success(t('released_ok', 'Review released')); load(); } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } }}>{t('release', 'Release')}</Button>
                )}
                {/* Manager self-release (enabled per-workspace): one step that
                    finalizes + releases their own report once they've submitted. */}
                {!isHR && managerSelfRelease && !r.released_at && r.manager_review_status === 'submitted' && (
                  <Button size="sm" onClick={async () => { try { await api.managerReleaseReview(r.id); toast.success(t('released_ok', 'Review released')); load(); } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } }}>{t('release_to_employee', 'Release to employee')}</Button>
                )}
                {/* HR/manager can download the finalized review PDF without
                    impersonating the employee (the employee's own copy lives in
                    their Reviews tab once released). */}
                {r.finalized_at && (
                  <Button size="sm" variant="outline" onClick={() => api.downloadReviewPDF(r.id, `performance-review-${(nameOf(r.employee_id) || 'review').toLowerCase().replace(/\s+/g, '-')}.pdf`, companyName).catch((e: any) => toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))))}>{t('download_pdf_short', 'PDF')}</Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setActive(r)}>{t('review', 'Review')}</Button>
              </div>
            </CardContent></Card>
          ))}
        </div>
      )}
      {active && <ManagerReviewDialog review={active} subjectName={nameOf(active.employee_id)} isHR={isHR} onClose={() => setActive(null)} onSaved={() => { setActive(null); load(); }} />}
    </div>
  );
};

// Transparent weighted-score panel: shows the tenant formula (weights are never
// hidden — a product requirement), lets the manager score each component on the
// rating scale, and computes the overall via the server engine so the displayed
// number always matches what gets persisted. onOverall bubbles the computed score
// up so the holistic rating field can be pre-filled.
const ScoreBreakdownPanel: React.FC<{ reviewId: string; onOverall?: (v: number) => void }> = ({ reviewId, onOverall }) => {
  const { t } = useTranslation('performance');
  const [formula, setFormula] = useState<api.ScoringFormula | null>(null);
  const [scale, setScale] = useState<api.RatingScale | null>(null);
  const [scores, setScores] = useState<Record<string, number | null>>({});
  const [result, setResult] = useState<api.ReviewScore | null>(null);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([
      api.getScoringFormula().catch(() => null),
      api.getRatingScales().then((s) => s.find((x) => x.is_default) || s[0]).catch(() => null),
      api.getReviewScore(reviewId).catch(() => null),
    ]).then(([f, sc, existing]) => {
      setFormula(f); setScale(sc);
      if (existing?.components?.length) {
        const init: Record<string, number | null> = {};
        existing.components.forEach((c) => { init[c.key] = c.raw_score; });
        setScores(init); setResult(existing);
      }
    }).finally(() => setLoaded(true));
  }, [reviewId]);

  // Recompute via the engine whenever an entered score changes (transparent parity).
  useEffect(() => {
    if (!loaded || !formula) return;
    const any = Object.values(scores).some((v) => v != null);
    if (!any) { setResult(null); return; }
    let cancelled = false;
    api.previewReviewScore(reviewId, { scores }).then((r) => { if (!cancelled) { setResult(r); onOverall?.(r.overall); } }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scores, loaded, formula]);

  const min = scale?.options?.[0]?.value ?? 1;
  const max = scale?.options?.length ? scale.options[scale.options.length - 1].value : 5;
  const save = async () => {
    setSaving(true);
    try { const r = await api.saveReviewScore(reviewId, { scores }); setResult(r); onOverall?.(r.overall); toast.success(t('score_saved', 'Score saved')); }
    catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } finally { setSaving(false); }
  };

  if (!loaded) return <div className="flex justify-center py-3"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>;
  if (!formula) return null;
  const lineFor = (key: string) => result?.components?.find((c) => c.key === key);

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2.5">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-muted-foreground">{t('score_breakdown', 'Weighted score')}</div>
        {scale && <span className="text-[11px] text-muted-foreground">{t('scale', 'Scale')}: {scale.name} ({min}–{max})</span>}
      </div>
      <div className="space-y-1.5">
        {formula.components.map((c) => {
          const ln = lineFor(c.key);
          return (
            <div key={c.key} className="grid grid-cols-[1fr_auto_auto] items-center gap-2 text-sm">
              <div className="min-w-0"><span className="truncate">{c.label}</span> <span className="text-xs text-muted-foreground">· {c.weight}%</span></div>
              <Input
                type="number" min={min} max={max} step={0.1} placeholder="—"
                className="h-8 w-20 text-right"
                value={scores[c.key] ?? ''}
                onChange={(e) => setScores((s) => ({ ...s, [c.key]: e.target.value === '' ? null : Number(e.target.value) }))}
              />
              <div className="w-14 text-right text-xs tabular-nums text-muted-foreground">{ln?.present ? ln.weighted.toFixed(2) : '—'}</div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between border-t border-border pt-2">
        <div>
          <div className="text-xs text-muted-foreground">{t('overall_score', 'Overall score')}</div>
          <div className="text-2xl font-semibold tabular-nums">{result ? result.overall.toFixed(formula.rounding_dp) : '—'}</div>
        </div>
        <Button size="sm" variant="outline" onClick={save} disabled={saving || !result}>
          {saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}{t('save_score', 'Save score')}
        </Button>
      </div>
      <p className="text-[11px] leading-snug text-muted-foreground">
        {t('score_note', 'Missing components are {{policy}}. Formula weights are shown in full and pinned to this review.', { policy: formula.missing_handling })}
      </p>
    </div>
  );
};

const ManagerReviewDialog: React.FC<{ review: api.Review; subjectName?: string; isHR: boolean; onClose: () => void; onSaved: () => void }> = ({ review, subjectName, isHR, onClose, onSaved }) => {
  const { t } = useTranslation('performance');
  const [full, setFull] = useState<api.Review | null>(null);
  const [comments, setComments] = useState('');
  const [rating, setRating] = useState<string>('');
  const [busy, setBusy] = useState(false);
  // Goal-based mode: rate each of the subject's goals side-by-side with their self-rating.
  const [goalBased, setGoalBased] = useState(false);
  const [goals, setGoals] = useState<api.Goal[]>([]);
  const [scale, setScale] = useState<api.RatingOption[]>([]);
  const [gRate, setGRate] = useState<Record<string, string>>({});
  const [gNote, setGNote] = useState<Record<string, string>>({});
  const [weights, setWeights] = useState<api.GoalWeightSummary | null>(null);
  // Competency mode (independent of goal_based): rate the cycle's competencies.
  const [rateComp, setRateComp] = useState(false);
  const [comps, setComps] = useState<api.CycleCompetency[]>([]);
  const [cRate, setCRate] = useState<Record<string, string>>({});
  const [cNote, setCNote] = useState<Record<string, string>>({});
  const [selfComp, setSelfComp] = useState<Record<string, api.CompetencyRating>>({});
  useEffect(() => {
    api.getReview(review.id).then((r) => { setFull(r); }).catch(() => setFull(review));
    api.listCycles().then((cs) => { const c = cs.find((x) => x.id === review.cycle_id); setGoalBased(c?.assessment_style === 'goal_based'); setRateComp(!!c?.rate_competencies); }).catch(() => {});
    api.listGoals(review.employee_id).then((g) => setGoals(g.filter((x) => x.status !== 'archived'))).catch(() => {});
    api.getRatingScales().then((sc) => { const d = sc.find((x) => x.is_default) || sc[0]; if (d) setScale([...d.options].sort((a, b) => a.ordinal - b.ordinal)); }).catch(() => {});
    // Prefill any manager ratings already saved so an in-progress review resumes.
    api.listGoalRatings(review.id).then((rs) => {
      const r: Record<string, string> = {}, n: Record<string, string> = {};
      rs.filter((x) => x.author === 'manager').forEach((x) => { r[x.goal_id] = String(x.rating); if (x.comment) n[x.goal_id] = x.comment; });
      if (Object.keys(r).length) setGRate(r); if (Object.keys(n).length) setGNote(n);
    }).catch(() => {});
    // Weights validate ALL the employee's active individual goals (the set this
    // review rates) — NOT cycle-filtered. Goals are authored cycle-less, so a
    // cycle filter read 0% while the on-screen goals total 100%. Matches the
    // server finalize gate (AssertGoalBasedFinalizable, cycle-less summary).
    api.getGoalWeights({ employee_id: review.employee_id }).then(setWeights).catch(() => {});
    // Competencies: the cycle's assigned set + prefill manager ratings + the self side.
    api.getCycleCompetencies(review.cycle_id).then(setComps).catch(() => setComps([]));
    api.listCompetencyRatings(review.id).then((rs) => {
      const r: Record<string, string> = {}, n: Record<string, string> = {}, sm: Record<string, api.CompetencyRating> = {};
      rs.forEach((x) => {
        if (x.author === 'manager') { r[x.competency_id] = String(x.rating); if (x.comment) n[x.competency_id] = x.comment; }
        else if (x.author === 'self') { sm[x.competency_id] = x; }
      });
      if (Object.keys(r).length) setCRate(r); if (Object.keys(n).length) setCNote(n); setSelfComp(sm);
    }).catch(() => {});
  }, [review.id]);

  const selfResp = (full?.responses || []).filter((x) => x.author === 'self');
  const selfByGoal: Record<string, api.ReviewResponse> = {};
  selfResp.forEach((x) => { selfByGoal[x.section] = x; });
  const scaleLabel = (v?: number | null) => v == null ? '' : (scale.find((o) => o.value === v)?.label || String(v));

  // Weighted overall from the manager's per-goal ratings.
  const weightedOverall = useMemo(() => {
    let ws = 0, w = 0;
    goals.forEach((g) => { const r = Number(gRate[g.id]); if (r) { ws += g.weight * r; w += g.weight; } });
    return w > 0 ? ws / w : 0;
  }, [goals, gRate]);
  useEffect(() => { if (goalBased && weightedOverall > 0 && !rating) setRating(weightedOverall.toFixed(2)); }, [goalBased, weightedOverall]); // eslint-disable-line

  const submit = async (sub: boolean) => {
    setBusy(true);
    try {
      const responses = goalBased
        ? goals.filter((g) => gRate[g.id] || gNote[g.id]?.trim()).map((g) => ({ section: g.id, rating: gRate[g.id] ? Number(gRate[g.id]) : undefined, answer: gNote[g.id] || '' }))
        : [{ section: 'Comments', answer: comments }];
      await api.submitManagerReview({ review_id: review.id, submit: sub, final_rating: rating ? Number(rating) : undefined, responses });
      // Goal-based: persist each manager rating to the authoritative store, then
      // let the server derive + pin the weighted goals-component score (so the
      // finalize gate and the frozen snapshot use the same numbers the UI shows).
      if (goalBased) {
        await Promise.all(goals.filter((g) => gRate[g.id]).map((g) =>
          api.saveGoalRating(review.id, g.id, { author: 'manager', rating: Number(gRate[g.id]), comment: gNote[g.id]?.trim() || undefined }).catch(() => {})));
      }
      if (rateComp) {
        await Promise.all(comps.filter((c) => cRate[c.competency_id]).map((c) =>
          api.saveCompetencyRating(review.id, c.competency_id, { author: 'manager', rating: Number(cRate[c.competency_id]), comment: cNote[c.competency_id]?.trim() || undefined }).catch(() => {})));
      }
      // Let the server derive + pin whichever components are rating-driven.
      if (goalBased || rateComp) {
        await api.saveReviewScore(review.id, { scores: {} }).catch(() => {});
      }
      toast.success(sub ? t('mgr_submitted', 'Review submitted') : t('self_saved', 'Draft saved')); onSaved();
    } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } finally { setBusy(false); }
  };
  const finalize = async () => {
    setBusy(true);
    try { await api.finalizeReview({ review_id: review.id, final_rating: rating ? Number(rating) : undefined }); toast.success(t('finalized', 'Review finalized')); onSaved(); }
    catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>{t('manager_review', 'Manager review')}{subjectName ? ` · ${subjectName}` : ''}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1 max-h-[64vh] overflow-y-auto">
          {goalBased ? (
            // Side-by-side per goal: self (left) vs manager (right).
            <>
              {weights && (
                <div className={`flex items-center justify-between rounded-md border px-3 py-2 text-xs ${weights.valid ? 'border-green-300 bg-green-50 dark:bg-green-500/10 text-green-800 dark:text-green-300' : 'border-amber-300 bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-300'}`}>
                  <span>{t('goal_weights_total', 'Goal weights total')}: <span className="font-semibold tabular-nums">{weights.total_weight}%</span></span>
                  <span>{weights.valid ? `✓ ${t('weights_ok', 'ready to finalize')}` : t('weights_must_100', 'must total 100% before finalizing')}</span>
                </div>
              )}
              {goals.length === 0 ? <p className="text-sm text-muted-foreground">{t('no_goals_to_rate', 'No goals to rate.')}</p> :
              goals.map((g, i) => {
                const self = selfByGoal[g.id];
                return (
                  <div key={g.id} className="rounded-lg border border-border p-3">
                    <div className="text-sm font-medium text-foreground mb-2 flex items-center gap-2 flex-wrap">
                      <span>{t('goal', 'Goal')} #{i + 1}: {g.title}</span>
                      <span className="text-xs text-muted-foreground">· {t('weight', 'Weight')} {g.weight}%</span>
                      {g.stretch_achieved && <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300 border-0 text-[10px]">🎯 {t('stretch_met', 'Stretch met')}</Badge>}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Self */}
                      <div>
                        <div className="text-[11px] font-semibold text-muted-foreground mb-1">{t('self_assessment', 'Self assessment')}</div>
                        {full?.self_hidden ? <div className="text-xs text-muted-foreground">🔒 {t('hidden_until_submit', 'Hidden until you submit')}</div>
                          : self ? <div className="text-sm"><span className="font-medium">{self.rating ?? '—'}</span>{self.rating != null ? ` – ${scaleLabel(self.rating)}` : ''}{self.answer ? <div className="text-xs text-muted-foreground mt-0.5">{self.answer}</div> : null}</div>
                            : <div className="text-xs text-muted-foreground">{t('not_rated', 'Not yet rated')}</div>}
                      </div>
                      {/* Manager */}
                      <div className="space-y-1.5">
                        <div className="text-[11px] font-semibold text-muted-foreground">{t('manager_assessment', 'Manager assessment')}</div>
                        <select value={gRate[g.id] || ''} onChange={(e) => setGRate({ ...gRate, [g.id]: e.target.value })} className="w-full h-9 rounded-md border border-border bg-background px-2.5 text-sm">
                          <option value="">{t('select_rating', 'Select a rating…')}</option>
                          {scale.map((o) => <option key={o.ordinal} value={o.value}>{o.value} – {o.label}</option>)}
                        </select>
                        <textarea value={gNote[g.id] || ''} onChange={(e) => setGNote({ ...gNote, [g.id]: e.target.value })} placeholder={t('additional_details', 'Additional details (optional)')} className="w-full min-h-[48px] rounded-md border border-border bg-background px-2.5 py-2 text-sm" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          ) : (
            <>
              <div>
                <div className="text-xs font-semibold text-muted-foreground mb-1">{t('self_review', 'Self-review')}</div>
                {full?.self_hidden ? (
                  <div className="rounded-md border border-border bg-muted/40 p-2.5 text-sm text-muted-foreground flex items-center gap-2">🔒 {t('double_blind_hidden', 'Double-blind: the self-review is hidden until you submit yours. Both will unlock after you submit.')}</div>
                ) : selfResp.length === 0 ? <p className="text-sm text-muted-foreground">{t('no_self', 'No self-review submitted yet.')}</p> :
                  selfResp.map((x) => <div key={x.id} className="text-sm mb-1.5"><span className="font-medium">{x.section}:</span> {x.answer}</div>)}
              </div>
              <div className="border-t border-border pt-2">
                <AskDana q="Help me draft a fair manager review and check it for bias" />
                <div className="space-y-1 mt-2"><Label className="text-xs">{t('comments', 'Comments')}</Label>
                  <textarea value={comments} onChange={(e) => setComments(e.target.value)} className="w-full min-h-[80px] rounded-md border border-border bg-background px-2.5 py-2 text-sm" /></div>
                <div className="mt-3"><ScoreBreakdownPanel reviewId={review.id} onOverall={(v) => { if (!rating) setRating(v.toFixed(2)); }} /></div>
              </div>
            </>
          )}
          {rateComp && comps.length > 0 && (
            <div className="space-y-2 border-t border-border pt-3">
              <div className="text-xs font-semibold text-muted-foreground">{t('competencies', 'Competencies')}</div>
              {comps.map((c) => {
                const self = selfComp[c.competency_id];
                return (
                  <div key={c.competency_id} className="rounded-lg border border-border p-3">
                    <div className="text-sm font-medium text-foreground mb-2">{c.name} <span className="text-xs text-muted-foreground">· {t('weight', 'Weight')} {c.weight}%</span></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <div className="text-[11px] font-semibold text-muted-foreground mb-1">{t('self_assessment', 'Self assessment')}</div>
                        {self ? <div className="text-sm"><span className="font-medium">{self.rating}</span> – {scaleLabel(self.rating)}{self.comment ? <div className="text-xs text-muted-foreground mt-0.5">{self.comment}</div> : null}</div>
                          : <div className="text-xs text-muted-foreground">{t('not_rated', 'Not yet rated')}</div>}
                      </div>
                      <div className="space-y-1.5">
                        <div className="text-[11px] font-semibold text-muted-foreground">{t('manager_assessment', 'Manager assessment')}</div>
                        <select value={cRate[c.competency_id] || ''} onChange={(e) => setCRate({ ...cRate, [c.competency_id]: e.target.value })} className="w-full h-9 rounded-md border border-border bg-background px-2.5 text-sm">
                          <option value="">{t('select_rating', 'Select a rating…')}</option>
                          {scale.map((o) => <option key={o.ordinal} value={o.value}>{o.value} – {o.label}</option>)}
                        </select>
                        <textarea value={cNote[c.competency_id] || ''} onChange={(e) => setCNote({ ...cNote, [c.competency_id]: e.target.value })} placeholder={t('additional_details', 'Additional details (optional)')} className="w-full min-h-[48px] rounded-md border border-border bg-background px-2.5 py-2 text-sm" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="space-y-1 mt-1 max-w-[160px] border-t border-border pt-3"><Label className="text-xs">{t('overall_rating', 'Overall rating (1-5)')}</Label>
            <Input type="number" min={1} max={5} step={0.1} value={rating} onChange={(e) => setRating(e.target.value)} />
            {goalBased && weightedOverall > 0 && <span className="text-[11px] text-muted-foreground">{t('weighted_from_goals', 'weighted from goals')}: {weightedOverall.toFixed(2)}</span>}
          </div>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={onClose}>{t('cancel', 'Cancel')}</Button>
          <Button variant="outline" onClick={() => submit(false)} disabled={busy}>{t('save_draft', 'Save draft')}</Button>
          <Button onClick={() => submit(true)} disabled={busy}>{t('submit', 'Submit')}</Button>
          {isHR && <Button onClick={finalize} disabled={busy} className="bg-green-600 hover:bg-green-700">{t('finalize', 'Finalize')}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---- Performance improvement plans (HR) ------------------------------------
const PIP_STATUS = ['open', 'succeeded', 'failed', 'closed'];
const PIPDialog: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useTranslation('performance');
  const [pips, setPips] = useState<api.PIP[]>([]);
  const [people, setPeople] = useState<api.FeedbackPerson[]>([]);
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState<{ subject: string; reason: string; objectives: string; start_date: string; end_date: string }>({ subject: '', reason: '', objectives: '', start_date: '', end_date: '' });
  const [busy, setBusy] = useState(false);
  const nameOf = (uid: string) => people.find((p) => p.user_id === uid)?.name || uid.slice(0, 8);
  const load = () => api.listPIPs().then(setPips).catch(() => setPips([]));
  useEffect(() => { load(); api.getFeedbackPeople().then(setPeople).catch(() => setPeople([])); }, []);

  const create = async () => {
    if (!f.subject) { toast.error(t('pick_subject', 'Pick an employee')); return; }
    setBusy(true);
    try {
      await api.createPIP({ subject_user_id: f.subject, reason: f.reason || undefined, objectives: f.objectives || undefined, start_date: f.start_date || undefined, end_date: f.end_date || undefined });
      toast.success(t('pip_created', 'Improvement plan created')); setAdding(false); setF({ subject: '', reason: '', objectives: '', start_date: '', end_date: '' }); load();
    } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } finally { setBusy(false); }
  };
  const setStatus = async (p: api.PIP, status: string) => { try { await api.updatePIP(p.id, { status }); load(); } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } };
  const sel = 'w-full h-9 rounded-md border border-border bg-background px-2.5 text-sm';

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{t('improvement_plans', 'Improvement plans')}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1 max-h-[64vh] overflow-y-auto">
          {!adding ? <Button size="sm" onClick={() => setAdding(true)}><Plus className="mr-1.5 h-4 w-4" />{t('new_pip', 'New plan')}</Button> : (
            <div className="rounded-md border border-border p-3 space-y-2">
              <div><Label className="text-xs">{t('employee', 'Employee')}</Label>
                <select value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} className={sel}>
                  <option value="">{t('select', 'Select…')}</option>
                  {people.map((p) => <option key={p.user_id} value={p.user_id}>{p.name}</option>)}
                </select></div>
              <div><Label className="text-xs">{t('pip_reason', 'Concerns')}</Label>
                <textarea value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className="w-full min-h-[50px] rounded-md border border-border bg-background px-2.5 py-2 text-sm" /></div>
              <div><Label className="text-xs">{t('objectives', 'Objectives')}</Label>
                <textarea value={f.objectives} onChange={(e) => setF({ ...f, objectives: e.target.value })} className="w-full min-h-[50px] rounded-md border border-border bg-background px-2.5 py-2 text-sm" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-[11px] text-muted-foreground">{t('start', 'Start')}</Label><Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} /></div>
                <div><Label className="text-[11px] text-muted-foreground">{t('end', 'End')}</Label><Input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} /></div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>{t('cancel', 'Cancel')}</Button>
                <Button size="sm" onClick={create} disabled={busy}>{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{t('create', 'Create')}</Button>
              </div>
            </div>
          )}
          {pips.length === 0 ? <Empty text={t('no_pips', 'No improvement plans.')} /> : pips.map((p) => (
            <Card key={p.id}><CardContent className="pt-4 space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-foreground">{nameOf(p.subject_user_id)}</div>
                <select value={p.status} onChange={(e) => setStatus(p, e.target.value)} className="h-7 rounded-md border border-border bg-background px-1.5 text-xs">
                  {PIP_STATUS.map((s) => <option key={s} value={s}>{human(s)}</option>)}
                </select>
              </div>
              {p.reason && <div className="text-xs text-muted-foreground">{t('pip_reason', 'Concerns')}: {p.reason}</div>}
              {p.objectives && <div className="text-xs text-muted-foreground">{t('objectives', 'Objectives')}: {p.objectives}</div>}
              {(p.start_date || p.end_date) && <div className="text-[11px] text-muted-foreground">{fmtDate(p.start_date)} – {fmtDate(p.end_date)}</div>}
            </CardContent></Card>
          ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>{t('close', 'Close')}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---- Calibration workspace (HR) --------------------------------------------
// Adjust manager ratings across a cohort for consistency. Every change REQUIRES a
// rationale (enforced server-side) and is recorded in an audit trail; the live
// distribution updates as you calibrate.
const CalibrationDialog: React.FC<{ onClose: () => void; onChanged: () => void }> = ({ onClose, onChanged }) => {
  const { t } = useTranslation('performance');
  const [cycles, setCycles] = useState<api.Cycle[]>([]);
  const [cycleId, setCycleId] = useState('');
  const [board, setBoard] = useState<api.CalibrationBoard | null>(null);
  const [changes, setChanges] = useState<api.CalibrationChange[]>([]);
  const [showLog, setShowLog] = useState(false);
  // per-row edit state
  const [edit, setEdit] = useState<Record<string, { rating: string; reason: string }>>({});
  const [busy, setBusy] = useState('');

  useEffect(() => { api.listCycles().then(setCycles).catch(() => setCycles([])); }, []);
  const loadBoard = (id: string) => {
    if (!id) return;
    api.getCalibrationBoard(id).then(setBoard).catch(() => setBoard(null));
    api.getCalibrationChanges(id).then(setChanges).catch(() => setChanges([]));
  };

  const apply = async (row: api.CalibrationRow) => {
    const e = edit[row.review_id];
    if (!e || !e.rating) return;
    if (!e.reason.trim()) { toast.error(t('reason_required', 'A calibration reason is required')); return; }
    setBusy(row.review_id);
    try {
      await api.adjustCalibration(row.review_id, { rating: Number(e.rating), reason: e.reason.trim() });
      toast.success(t('calibrated_ok', 'Rating calibrated'));
      setEdit((s) => { const n = { ...s }; delete n[row.review_id]; return n; });
      loadBoard(cycleId); onChanged();
    } catch (err: any) { toast.error(getApiErrorMessage(err, t('err', 'Something went wrong'))); } finally { setBusy(''); }
  };

  const total = board?.rows.length || 0;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>{t('calibration', 'Calibration')}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1 max-h-[66vh] overflow-y-auto">
          <select value={cycleId} onChange={(e) => { setCycleId(e.target.value); setBoard(null); loadBoard(e.target.value); }}
            className="h-9 rounded-md border border-border bg-background px-2.5 text-sm min-w-[220px]">
            <option value="">{t('select_cycle', 'Select a cycle…')}</option>
            {cycles.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          {board && (
            <>
              {/* live distribution */}
              <div className="rounded-lg border border-border p-3 space-y-1">
                <div className="text-xs font-semibold text-muted-foreground mb-1">{t('an_distribution', 'Rating distribution')}</div>
                {board.distribution.slice().reverse().map((b) => {
                  const pct = total ? Math.round((b.count / total) * 100) : 0;
                  return (
                    <div key={b.bucket} className="flex items-center gap-3 text-sm">
                      <span className="w-4 text-muted-foreground">{b.bucket}★</span>
                      <div className="flex-1"><Progress value={pct} /></div>
                      <span className="w-8 text-right text-muted-foreground">{b.count}</span>
                    </div>
                  );
                })}
              </div>

              {/* cohort */}
              <div className="space-y-1.5">
                {board.rows.length === 0 ? <Empty text={t('no_participants', 'No participants in this cycle.')} /> : board.rows.map((r) => {
                  const e = edit[r.review_id];
                  return (
                    <div key={r.review_id} className="rounded-md border border-border p-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-foreground truncate">{r.name || r.subject_user_id.slice(0, 8)}</div>
                          <div className="text-xs text-muted-foreground">
                            {t('rating', 'Rating')}: {r.final_rating ?? '—'}
                            {r.calibrated_rating != null && <span className="text-orange-600"> · {t('calibrated', 'calibrated')}</span>}
                          </div>
                        </div>
                        {!e ? (
                          <Button size="sm" variant="outline" onClick={() => setEdit((s) => ({ ...s, [r.review_id]: { rating: String(r.final_rating ?? ''), reason: '' } }))}>{t('adjust', 'Adjust')}</Button>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <Input type="number" min={1} max={5} step={0.1} value={e.rating} onChange={(ev) => setEdit((s) => ({ ...s, [r.review_id]: { ...e, rating: ev.target.value } }))} className="h-8 w-16" />
                          </div>
                        )}
                      </div>
                      {e && (
                        <div className="mt-2 flex items-center gap-2">
                          <Input value={e.reason} onChange={(ev) => setEdit((s) => ({ ...s, [r.review_id]: { ...e, reason: ev.target.value } }))} placeholder={t('reason_ph', 'Reason for adjustment (required)')} className="h-8 flex-1" />
                          <Button size="sm" onClick={() => apply(r)} disabled={busy === r.review_id}>{busy === r.review_id && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}{t('apply', 'Apply')}</Button>
                          <Button size="sm" variant="ghost" onClick={() => setEdit((s) => { const n = { ...s }; delete n[r.review_id]; return n; })}>{t('cancel', 'Cancel')}</Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* audit trail */}
              <button className="text-xs text-orange-600 underline" onClick={() => setShowLog((v) => !v)}>
                {showLog ? t('hide_log', 'Hide change log') : t('show_log', 'Show change log')} ({changes.length})
              </button>
              {showLog && (
                <div className="space-y-1 border-t border-border pt-2">
                  {changes.map((c) => (
                    <div key={c.id} className="text-xs text-muted-foreground">
                      <span className="text-foreground">{fmtDate(c.created_at)}</span> · {c.field} {c.previous_value ?? '—'} → {c.new_value ?? '—'} · {c.reason}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={onClose}>{t('close', 'Close')}</Button>
          {board && <Button variant="ghost" onClick={async () => { await api.closeCalibration(cycleId); toast.success(t('session_closed', 'Calibration session closed')); loadBoard(cycleId); }}>{t('close_session', 'Close session')}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---- Review-cycle wizard (HR) — full-page, multi-step ----------------------
// People → Assessments → Schedule → Review & Launch. Reuses the eligibility preview
// (live count), review templates (questions), and cycle due dates. A draft cycle is
// created as soon as it's named, so "Save & finish later" just leaves the draft.
const EMPLOYMENT_TYPES = ['Permanent', 'Contract', 'Probation', 'Intern', 'Part-time', 'Temporary'];
const WIZ_STEPS = [
  { key: 'people', label: 'People' },
  { key: 'assessments', label: 'Assessments' },
  { key: 'schedule', label: 'Schedule' },
  { key: 'review', label: 'Review & Launch' },
];
const isoToday = () => new Date().toISOString().slice(0, 10);
const isoPlus = (days: number) => { const d = new Date(); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10); };
const tplSections = (s: any): string[] => Array.isArray(s) ? s.map((x) => typeof x === 'string' ? x : (x?.title || x?.name || '')).filter(Boolean) : [];
const RECUR_MONTHS: Record<string, number> = { monthly: 1, quarterly: 3, half_yearly: 6, annual: 12 };
// Preview the next few windows by shifting start/end forward by the cadence.
const upcomingWindows = (start: string, end: string, recur: string): [string, string][] => {
  const m = RECUR_MONTHS[recur] || 0;
  if (!m || !start || !end) return [];
  // Local date in, local date out — the old toISOString() slice re-read the
  // LOCAL midnight as a UTC date, shifting every cycle date a day early east
  // of UTC (timezone program 2026-08).
  const shift = (iso: string, times: number) => {
    const d = new Date(iso + 'T00:00:00');
    d.setMonth(d.getMonth() + m * times);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };
  return [1, 2, 3].map((i) => [shift(start, i), shift(end, i)] as [string, string]);
};

const ReviewCycleWizard: React.FC<{ onClose: () => void; onLaunched: () => void }> = ({ onClose, onLaunched }) => {
  const { t } = useTranslation('performance');
  const [step, setStep] = useState('people');
  const [name, setName] = useState('');
  const [scope, setScope] = useState<'all' | 'some'>('all');
  const [filters, setFilters] = useState<api.PopulationFilters>({ departments: [], employment_types: [], min_tenure_days: 0 });
  const [depts, setDepts] = useState<Array<{ id: string; name: string }>>([]);
  // G7: companies (offered when more than one and the list is readable).
  const [companies, setCompanies] = useState<Array<{ id: string; name: string }>>([]);
  useEffect(() => {
    listLegalEntities().then((l) => setCompanies((l || []).map((c) => ({ id: c.id, name: c.name })))).catch(() => setCompanies([]));
  }, []);
  const [templates, setTemplates] = useState<api.Template[]>([]);
  const [templateId, setTemplateId] = useState('');
  const [dates, setDates] = useState({ start_date: isoToday(), end_date: isoPlus(365), self_review_due_date: isoPlus(45), manager_review_due_date: isoPlus(60) });
  const [recurrence, setRecurrence] = useState<string>('none');
  const [doubleBlind, setDoubleBlind] = useState(false);
  const [assessmentStyle, setAssessmentStyle] = useState<'written' | 'goal_based'>('written');
  const [fws, setFws] = useState<api.PerfFramework[]>([]);
  const [frameworkId, setFrameworkId] = useState('');
  const [cycleId, setCycleId] = useState('');
  const [preview, setPreview] = useState<api.PopulationResult | null>(null);
  const [counting, setCounting] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getDepartments().then((r: any) => setDepts((r?.items || []).map((d: any) => ({ id: d.id, name: d.name })))).catch(() => {});
    api.listTemplates().then((tl) => { setTemplates(tl); const def = tl.find((x) => x.is_default) || tl[0]; if (def) setTemplateId(def.id); }).catch(() => {});
    api.listFrameworks().then(setFws).catch(() => setFws([]));
  }, []);

  const pickFramework = (id: string) => {
    setFrameworkId(id);
    const fw = fws.find((f) => f.id === id);
    const rd = fw?.review_defaults || {};
    if (rd.assessment_style === 'goal_based' || rd.assessment_style === 'written') setAssessmentStyle(rd.assessment_style);
    if (typeof rd.double_blind === 'boolean') setDoubleBlind(rd.double_blind);
  };

  const effFilters: api.PopulationFilters = scope === 'all' ? {} : filters;
  const ensureDraft = async (): Promise<string> => {
    if (cycleId) return cycleId;
    if (!name.trim()) return '';
    const c = await api.createCycle({ name: name.trim(), type: 'annual', ...dates, status: 'draft', recurrence: recurrence as any, double_blind: doubleBlind, assessment_style: assessmentStyle, framework_id: frameworkId || undefined } as any);
    setCycleId(c.id); return c.id;
  };
  const refreshCount = async () => {
    if (!name.trim()) { setPreview(null); return; }
    setCounting(true);
    try { const id = await ensureDraft(); if (id) setPreview(await api.previewPopulation(id, effFilters)); }
    catch { /* keep prior */ } finally { setCounting(false); }
  };
  useEffect(() => { const h = setTimeout(refreshCount, 500); return () => clearTimeout(h); /* eslint-disable-next-line */ }, [name, scope, JSON.stringify(filters)]);

  const toggle = (key: 'departments' | 'employment_types' | 'legal_entities', v: string) =>
    setFilters((f) => { const s = new Set(f[key] || []); s.has(v) ? s.delete(v) : s.add(v); return { ...f, [key]: [...s] }; });

  const idx = WIZ_STEPS.findIndex((s) => s.key === step);
  const goNext = async () => {
    if (step === 'people') { if (!name.trim()) { toast.error(t('cycle_name_req', 'Give the cycle a name')); return; } await ensureDraft(); setStep('assessments'); }
    else if (step === 'assessments') setStep('schedule');
    else if (step === 'schedule') { const id = await ensureDraft(); if (id) await api.updateCycle(id, dates as any).catch(() => {}); setStep('review'); }
  };
  const launch = async () => {
    setBusy(true);
    try {
      const id = await ensureDraft(); if (!id) return;
      await api.updateCycle(id, { ...dates, name: name.trim(), recurrence: recurrence as any, double_blind: doubleBlind, assessment_style: assessmentStyle, framework_id: frameworkId || undefined } as any);
      if (assessmentStyle === 'written' && templateId) await api.updateTemplate(templateId, { is_default: true }).catch(() => {});
      const s = await api.launchCycle(id, effFilters);
      toast.success(t('launched_summary', 'Launched: {{n}} reviews created', { n: s.reviews_created }));
      onLaunched();
    } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } finally { setBusy(false); }
  };

  const selectedTpl = templates.find((x) => x.id === templateId);
  const inputCls = 'w-full h-10 rounded-md border border-border bg-background px-3 text-sm';

  return (
    <div className="space-y-4">
      <button onClick={onClose} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">‹ {t('back_to_reviews', 'Back to team reviews')}</button>
      <h2 className="text-2xl font-bold text-charcoal">{t('create_review_cycle', 'Create review cycle')}</h2>

      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-5">
        {/* Stepper */}
        <nav className="space-y-1" aria-label={t('steps', 'Steps')}>
          {WIZ_STEPS.map((s, i) => {
            const done = i < idx, active = i === idx;
            return (
              <button key={s.key} onClick={() => (i <= idx || name.trim()) && setStep(s.key)}
                className={`w-full flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm text-left transition ${active ? 'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-300 font-medium' : 'text-muted-foreground hover:bg-muted'}`}>
                <span className={`h-6 w-6 rounded-full border-2 flex items-center justify-center text-xs shrink-0 ${done ? 'bg-success border-success text-white' : active ? 'bg-orange-600 border-orange-600 text-white' : 'border-border'}`}>{done ? '✓' : i + 1}</span>
                {t(`wiz_${s.key}`, s.label)}
              </button>
            );
          })}
          <div className="pt-2"><Button variant="ghost" size="sm" className="w-full" onClick={onClose}>{t('save_finish_later', 'Save & finish later')}</Button></div>
        </nav>

        {/* Step content */}
        <Card><CardContent className="pt-5 space-y-5 min-h-[360px]">
          {step === 'people' && (
            <div className="space-y-4">
              <div><h3 className="font-semibold text-foreground">{t('people_in_cycle', 'People in your review cycle')}</h3><p className="text-sm text-muted-foreground">{t('people_help', 'A review cycle is a group of employees who share the same schedule and questions.')}</p></div>
              <div className="max-w-md">
                <Label className="text-xs">{t('cycle_name_req_label', 'Review cycle name')} *</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('cycle_name_ph', 'e.g. 2026 Annual Review, Sales Team')} className={inputCls} />
                <p className="text-[11px] text-muted-foreground mt-1">{t('name_visible', 'Visible to included employees.')}</p>
              </div>
              <div>
                <Label className="text-xs">{t('who_included', 'Who should be included?')}</Label>
                <div className="flex flex-col gap-1.5 mt-1">
                  <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="radio" checked={scope === 'all'} onChange={() => setScope('all')} className="accent-orange-500" /> {t('all_employees', 'All employees')}</label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="radio" checked={scope === 'some'} onChange={() => setScope('some')} className="accent-orange-500" /> {t('some_employees', 'Only some employees')}</label>
                </div>
              </div>
              {scope === 'some' && (
                <div className="space-y-3 rounded-lg border border-border p-3">
                  <div className="text-[11px] text-muted-foreground">{t('filter_by', 'Filter by — leave a facet empty to include everyone on it.')}</div>
                  {depts.length > 0 && <div><div className="text-xs font-medium mb-1">{t('departments', 'Departments')}</div><div className="flex flex-wrap gap-1.5">{depts.map((d) => { const on = (filters.departments || []).includes(d.id); return <button key={d.id} onClick={() => toggle('departments', d.id)} className={`text-xs rounded-full border px-2.5 py-1 ${on ? 'bg-primary-500 text-white border-primary-500' : 'border-border'}`}>{d.name}</button>; })}</div></div>}
                  {companies.length > 1 && <div data-testid="population-companies"><div className="text-xs font-medium mb-1">{t('companies', 'Companies')}</div><div className="flex flex-wrap gap-1.5">{companies.map((c) => { const on = (filters.legal_entities || []).includes(c.id); return <button key={c.id} onClick={() => toggle('legal_entities', c.id)} className={`text-xs rounded-full border px-2.5 py-1 ${on ? 'border-info bg-info-soft text-info-strong' : 'border-border'}`}>{c.name}</button>; })}</div><p className="text-[11px] text-muted-foreground mt-1">{t('companies_hint', 'People employed by a chosen company — including a second job there — reviewed by their manager at that company.')}</p></div>}
                  <div><div className="text-xs font-medium mb-1">{t('employment_type', 'Employment type')}</div><div className="flex flex-wrap gap-1.5">{EMPLOYMENT_TYPES.map((et) => { const on = (filters.employment_types || []).includes(et); return <button key={et} onClick={() => toggle('employment_types', et)} className={`text-xs rounded-full border px-2.5 py-1 ${on ? 'bg-primary-500 text-white border-primary-500' : 'border-border'}`}>{et}</button>; })}</div></div>
                  <div className="max-w-[220px]"><Label className="text-xs">{t('min_tenure', 'Minimum tenure (days)')}</Label><Input type="number" min={0} value={filters.min_tenure_days || 0} onChange={(e) => setFilters({ ...filters, min_tenure_days: Number(e.target.value) || 0 })} /></div>
                </div>
              )}
              {/* Live count */}
              <div className="flex items-center gap-2 text-sm">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-success-soft text-success-strong px-3 py-1 font-medium">
                  {counting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}{preview ? preview.included_count : 0} {t('included', 'included')}
                </span>
                {preview && preview.excluded_count > 0 && <span className="text-xs text-muted-foreground">· {preview.excluded_count} {t('excluded', 'excluded')}</span>}
                {!name.trim() && <span className="text-xs text-muted-foreground">{t('name_first', 'Name the cycle to see the count')}</span>}
              </div>
            </div>
          )}

          {step === 'assessments' && (
            <div className="space-y-4">
              <div><h3 className="font-semibold text-foreground">{t('assessments_title', 'Assessment questions')}</h3><p className="text-sm text-muted-foreground">{t('assessments_help', 'Employees complete a self-review; managers assess their reports. Choose how they’re assessed.')}</p></div>
              {fws.length > 0 && (
                <div className="rounded-lg border border-border p-3 space-y-2">
                  <Label className="text-xs">{t('framework', 'Performance framework')}</Label>
                  <select className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm" value={frameworkId} onChange={(e) => pickFramework(e.target.value)}>
                    <option value="">{t('no_framework', 'No framework (legacy behaviour)')}</option>
                    {fws.map((fw) => <option key={fw.id} value={fw.id}>{fw.name}{fw.tenant_id ? '' : ` · ${t('kazipeople_default', 'KaziPeople default')}`}{fw.bonus_linked ? ` · ${t('bonus_linked', 'bonus-linked')}` : ''}</option>)}
                  </select>
                  {(() => { const fw = fws.find((x) => x.id === frameworkId); return fw ? (
                    <p className="text-[11px] text-muted-foreground">
                      {fw.description || ''} {fw.bonus_linked ? t('fw_launch_note', 'Bonus-linked: every participant’s goal weights must total 100% before launch, and the framework is frozen onto the cycle at launch.') : t('fw_freeze_note', 'The framework’s scale/formula/template versions are frozen onto the cycle at launch.')}
                    </p>) : null; })()}
                </div>
              )}
              {/* Assessment style */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button onClick={() => setAssessmentStyle('goal_based')} className={`text-left rounded-lg border p-3 ${assessmentStyle === 'goal_based' ? 'border-orange-400 bg-orange-50 dark:bg-orange-500/10' : 'border-border'}`}>
                  <div className="font-medium text-sm text-foreground">🎯 {t('style_goal', 'Rate each goal')}</div>
                  <div className="text-xs text-muted-foreground">{t('style_goal_help', 'Employees & managers rate each goal on the scale + comment; ratings roll up into the score.')}</div>
                </button>
                <button onClick={() => setAssessmentStyle('written')} className={`text-left rounded-lg border p-3 ${assessmentStyle === 'written' ? 'border-orange-400 bg-orange-50 dark:bg-orange-500/10' : 'border-border'}`}>
                  <div className="font-medium text-sm text-foreground">📝 {t('style_written', 'Written sections')}</div>
                  <div className="text-xs text-muted-foreground">{t('style_written_help', 'Free-text answers to a set of sections (a template).')}</div>
                </button>
              </div>
              {assessmentStyle === 'goal_based' ? (
                <p className="text-sm text-muted-foreground rounded-md border border-border p-3">{t('goal_based_note', 'Each employee rates their own goals on the default rating scale; their manager rates the same goals side-by-side. Manage the scale in Admin → Rating scales.')}</p>
              ) : templates.length === 0 ? <SectionEmpty text={t('no_templates', 'No templates yet. Create one to structure your review forms.')} /> : (
                <div className="space-y-2">
                  {templates.map((tpl) => (
                    <label key={tpl.id} className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${templateId === tpl.id ? 'border-orange-400 bg-orange-50 dark:bg-orange-500/10' : 'border-border'}`}>
                      <input type="radio" checked={templateId === tpl.id} onChange={() => setTemplateId(tpl.id)} className="mt-1 accent-orange-500" />
                      <div className="min-w-0">
                        <div className="font-medium text-sm text-foreground flex items-center gap-2">{tpl.name}{tpl.is_default && <Badge className="bg-success-soft text-success-strong border-0 text-[10px]">{t('default', 'Default')}</Badge>}</div>
                        <div className="text-xs text-muted-foreground">{tplSections(tpl.sections).join(' · ') || '—'}</div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground">{t('edit_templates_hint', 'Manage templates in Admin → Review templates.')}</p>
            </div>
          )}

          {step === 'schedule' && (
            <div className="space-y-4">
              <div><h3 className="font-semibold text-foreground">{t('schedule_title', 'Schedule')}</h3><p className="text-sm text-muted-foreground">{t('schedule_help', 'Set the cycle window and review deadlines. Reminders fire automatically before each due date.')}</p></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
                <div><Label className="text-xs">{t('start', 'Start')}</Label><Input type="date" value={dates.start_date} onChange={(e) => setDates({ ...dates, start_date: e.target.value })} /></div>
                <div><Label className="text-xs">{t('end', 'End')}</Label><Input type="date" value={dates.end_date} onChange={(e) => setDates({ ...dates, end_date: e.target.value })} /></div>
                <div><Label className="text-xs">{t('self_review_due', 'Self-review due')}</Label><Input type="date" value={dates.self_review_due_date} onChange={(e) => setDates({ ...dates, self_review_due_date: e.target.value })} /></div>
                <div><Label className="text-xs">{t('manager_review_due', 'Manager review due')}</Label><Input type="date" value={dates.manager_review_due_date} onChange={(e) => setDates({ ...dates, manager_review_due_date: e.target.value })} /></div>
              </div>

              {/* Recurrence */}
              <div className="border-t border-border pt-3 space-y-2 max-w-xl">
                <div><Label className="text-xs">{t('repeats', 'Repeats')}</Label>
                  <select value={recurrence} onChange={(e) => setRecurrence(e.target.value)} className={inputCls}>
                    {['none', 'monthly', 'quarterly', 'half_yearly', 'annual'].map((r) => <option key={r} value={r}>{t(`recur_${r}`, r)}</option>)}
                  </select></div>
                {recurrence !== 'none' && (
                  <div className="rounded-md border border-border p-2.5">
                    <div className="text-[11px] font-medium text-muted-foreground mb-1">{t('upcoming_windows', 'Upcoming windows')}</div>
                    {upcomingWindows(dates.start_date, dates.end_date, recurrence).map((w, i) => (
                      <div key={i} className="text-xs text-muted-foreground">• {fmtDate(w[0])} – {fmtDate(w[1])}</div>
                    ))}
                    <div className="text-[11px] text-muted-foreground mt-1">{t('and_so_on', 'and so on… (create each from Admin → Cycles → Create next)')}</div>
                  </div>
                )}
              </div>

              {/* Double-blind */}
              <label className="flex items-start gap-2.5 max-w-xl cursor-pointer border-t border-border pt-3">
                <input type="checkbox" checked={doubleBlind} onChange={(e) => setDoubleBlind(e.target.checked)} className="mt-0.5 h-4 w-4 accent-orange-500" />
                <span className="text-sm"><span className="font-medium text-foreground">{t('double_blind', 'Double-blind assessments')}</span>
                  <span className="block text-xs text-muted-foreground">{t('double_blind_help', 'Managers can’t see an employee’s self-review until they’ve submitted their own.')}</span></span>
              </label>
            </div>
          )}

          {step === 'review' && (
            <div className="space-y-4">
              <div><h3 className="font-semibold text-foreground">{t('review_launch', 'Review & launch')}</h3><p className="text-sm text-muted-foreground">{t('review_launch_help', 'Check everything below, then launch. Until you launch, the draft is invisible to employees.')}</p></div>
              {[
                { label: t('wiz_people', 'People'), to: 'people', body: <><div className="font-medium text-sm">{name || '—'}</div><div className="text-xs text-muted-foreground">{preview ? `${preview.included_count} ${t('included', 'included')}` : ''}{scope === 'some' ? ` · ${t('filtered', 'filtered')}` : ` · ${t('all_employees', 'All employees')}`}</div></> },
                { label: t('wiz_assessments', 'Assessments'), to: 'assessments', body: <div className="text-sm">{assessmentStyle === 'goal_based' ? t('style_goal', 'Rate each goal') : (selectedTpl ? `${selectedTpl.name} · ${tplSections(selectedTpl.sections).length} ${t('sections', 'sections')}` : t('default_questions', 'Default questions'))}</div> },
                { label: t('wiz_schedule', 'Schedule'), to: 'schedule', body: <div className="text-xs text-muted-foreground">{fmtDate(dates.start_date)} – {fmtDate(dates.end_date)} · {t('self_due', 'self due')} {fmtDate(dates.self_review_due_date)} · {t('mgr_due', 'mgr due')} {fmtDate(dates.manager_review_due_date)}{recurrence !== 'none' ? ` · ${t(`recur_${recurrence}`, recurrence)}` : ''}{doubleBlind ? ` · ${t('double_blind', 'Double-blind')}` : ''}</div> },
              ].map((sec) => (
                <div key={sec.to} className="rounded-lg border border-border p-3 flex items-start justify-between gap-3">
                  <div className="min-w-0"><div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5">{sec.label}</div>{sec.body}</div>
                  <button onClick={() => setStep(sec.to)} className="text-sm text-orange-600 hover:text-orange-700 shrink-0">{t('change', 'Change')}</button>
                </div>
              ))}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-border pt-4">
            <Button variant="outline" onClick={() => idx > 0 ? setStep(WIZ_STEPS[idx - 1].key) : onClose()}>{idx > 0 ? t('back', 'Back') : t('cancel', 'Cancel')}</Button>
            {step !== 'review'
              ? <Button onClick={goNext}>{t('next', 'Next')}: {t(`wiz_${WIZ_STEPS[idx + 1].key}`, WIZ_STEPS[idx + 1].label)}</Button>
              : <Button onClick={launch} disabled={busy || !preview || preview.included_count === 0} className="bg-green-600 hover:bg-green-700">{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{t('launch_review_cycle', 'Launch review cycle')} ({preview?.included_count || 0})</Button>}
          </div>
        </CardContent></Card>
      </div>
    </div>
  );
};

// ---- Peer / 360 feedback ----------------------------------------------------
const FeedbackTab: React.FC = () => {
  const { t } = useTranslation('performance');
  const [inbox, setInbox] = useState<api.FeedbackRequest[]>([]);
  const [about, setAbout] = useState<api.FeedbackAboutMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [give, setGive] = useState<api.FeedbackRequest | null>(null);
  const [requesting, setRequesting] = useState(false);
  const load = () => Promise.all([
    api.getFeedbackInbox().catch(() => []),
    api.getFeedbackAboutMe().catch(() => null),
  ]).then(([i, a]) => { setInbox(i); setAbout(a); }).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  if (loading) return <Loading />;
  const pending = inbox.filter((r) => r.status === 'pending');
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">{t('feedback_intro', 'Give and request peer feedback. Anonymous feedback stays anonymous.')}</p>
        <Button size="sm" onClick={() => setRequesting(true)}><Plus className="mr-1.5 h-4 w-4" />{t('request_feedback', 'Request feedback')}</Button>
      </div>

      {/* Requests for you */}
      <div className="space-y-2">
        <div className="text-xs font-semibold text-muted-foreground">{t('requests_for_you', 'Requests for you')} ({pending.length})</div>
        {pending.length === 0 ? <Empty text={t('no_requests', 'No feedback requests waiting.')} /> : pending.map((r) => (
          <Card key={r.id}><CardContent className="pt-4 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-medium text-foreground">{t('feedback_on', 'Feedback on')} {r.subject_name || t('a_colleague', 'a colleague')}</div>
              {r.message && <div className="text-xs text-muted-foreground line-clamp-2">{r.message}</div>}
              {r.anonymous && <Badge className="bg-slate-100 text-slate-600 border-0 text-[10px] mt-1">{t('anonymous', 'Anonymous')}</Badge>}
            </div>
            <div className="flex gap-2 shrink-0">
              <Button size="sm" variant="ghost" onClick={async () => { await api.declineFeedback(r.id); load(); }}>{t('decline', 'Decline')}</Button>
              <Button size="sm" variant="outline" onClick={() => setGive(r)}>{t('give', 'Give')}</Button>
            </div>
          </CardContent></Card>
        ))}
      </div>

      {/* About me */}
      <div className="space-y-2">
        <div className="text-xs font-semibold text-muted-foreground">{t('about_me', 'Feedback about you')}</div>
        {!about || about.total_requested === 0 ? <Empty text={t('no_about', 'No feedback yet. Request some above.')} /> : (
          <>
            <div className="text-xs text-muted-foreground">{about.submitted_count}/{about.total_requested} {t('submitted', 'submitted')}</div>
            {about.anon_pending_reveal > 0 && about.items.length === 0 && (
              <Card><CardContent className="pt-4 text-sm text-muted-foreground">
                {t('threshold_wait', 'Anonymous feedback is revealed once at least {{n}} responses arrive (protecting who said what). {{got}} so far.', { n: about.threshold, got: about.anon_pending_reveal })}
              </CardContent></Card>
            )}
            {about.items.map((it, i) => (
              <Card key={i}><CardContent className="pt-4 space-y-1">
                <div className="text-xs font-medium text-foreground">{it.anonymous ? t('anonymous', 'Anonymous') : it.provider_name}
                  {it.rating != null && <span className="text-muted-foreground"> · {t('rating', 'Rating')} {it.rating}</span>}</div>
                {it.strengths && <div className="text-sm"><span className="text-muted-foreground">{t('strengths', 'Strengths')}: </span>{it.strengths}</div>}
                {it.improvements && <div className="text-sm"><span className="text-muted-foreground">{t('improvements', 'To improve')}: </span>{it.improvements}</div>}
              </CardContent></Card>
            ))}
          </>
        )}
      </div>

      {give && <GiveFeedbackDialog request={give} onClose={() => setGive(null)} onSaved={() => { setGive(null); load(); }} />}
      {requesting && <RequestFeedbackDialog onClose={() => setRequesting(false)} onDone={() => { setRequesting(false); load(); }} />}
    </div>
  );
};

const GiveFeedbackDialog: React.FC<{ request: api.FeedbackRequest; onClose: () => void; onSaved: () => void }> = ({ request, onClose, onSaved }) => {
  const { t } = useTranslation('performance');
  const [f, setF] = useState({ strengths: '', improvements: '', rating: '' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.respondFeedback(request.id, { strengths: f.strengths || undefined, improvements: f.improvements || undefined, rating: f.rating ? Number(f.rating) : undefined });
      toast.success(t('feedback_sent', 'Feedback submitted')); onSaved();
    } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{t('give_feedback', 'Give feedback')} · {request.subject_name || t('a_colleague', 'a colleague')}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          {request.anonymous && <p className="text-[11px] text-muted-foreground">{t('will_be_anon', 'This feedback is anonymous — your name will not be shown.')}</p>}
          <div><Label className="text-xs">{t('strengths', 'Strengths')}</Label>
            <textarea value={f.strengths} onChange={(e) => setF({ ...f, strengths: e.target.value })} className="w-full min-h-[70px] rounded-md border border-border bg-background px-2.5 py-2 text-sm" /></div>
          <div><Label className="text-xs">{t('improvements', 'To improve')}</Label>
            <textarea value={f.improvements} onChange={(e) => setF({ ...f, improvements: e.target.value })} className="w-full min-h-[70px] rounded-md border border-border bg-background px-2.5 py-2 text-sm" /></div>
          <div className="max-w-[140px]"><Label className="text-xs">{t('rating_opt', 'Rating (1-5, optional)')}</Label>
            <Input type="number" min={1} max={5} step={0.1} value={f.rating} onChange={(e) => setF({ ...f, rating: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('cancel', 'Cancel')}</Button>
          <Button onClick={submit} disabled={busy || (!f.strengths && !f.improvements)}>{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{t('submit', 'Submit')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RequestFeedbackDialog: React.FC<{ onClose: () => void; onDone: () => void }> = ({ onClose, onDone }) => {
  const { t } = useTranslation('performance');
  const [people, setPeople] = useState<api.FeedbackPerson[]>([]);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState('');
  const [anonymous, setAnonymous] = useState(true);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  useEffect(() => { api.getFeedbackPeople().then(setPeople).catch(() => setPeople([])); }, []);
  const filtered = people.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));
  const toggle = (id: string) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const submit = async () => {
    if (picked.size === 0) return;
    setBusy(true);
    try {
      const r = await api.requestFeedback({ provider_user_ids: [...picked], message: message || undefined, anonymous });
      toast.success(t('requested_n', '{{n}} feedback requests sent', { n: r.created })); onDone();
    } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{t('request_feedback', 'Request feedback')}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <p className="text-[11px] text-muted-foreground">{t('request_hint', 'Pick colleagues to ask for feedback about you.')}</p>
          <Input placeholder={t('search_people', 'Search colleagues…')} value={q} onChange={(e) => setQ(e.target.value)} />
          <div className="max-h-52 overflow-y-auto space-y-1 rounded-md border border-border p-2">
            {filtered.length === 0 ? <p className="text-sm text-muted-foreground p-1">{t('no_people', 'No colleagues found.')}</p> : filtered.map((p) => (
              <label key={p.user_id} className="flex items-center gap-2 text-sm cursor-pointer py-0.5">
                <input type="checkbox" checked={picked.has(p.user_id)} onChange={() => toggle(p.user_id)} className="h-4 w-4 accent-orange-500" />
                <span className="flex-1 text-foreground">{p.name}</span>
                <span className="text-xs text-muted-foreground">{p.position || p.department || ''}</span>
              </label>
            ))}
          </div>
          <div><Label className="text-xs">{t('message', 'Message')} <span className="text-muted-foreground">({t('optional', 'optional')})</span></Label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} className="w-full min-h-[56px] rounded-md border border-border bg-background px-2.5 py-2 text-sm" /></div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} className="h-4 w-4 accent-orange-500" />
            {t('collect_anon', 'Collect anonymously')}
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('cancel', 'Cancel')}</Button>
          <Button onClick={submit} disabled={busy || picked.size === 0}>{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{t('send_requests', 'Send')} ({picked.size})</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---- Compensation (HR/Finance) ---------------------------------------------
// A cycle-scoped workbench: every eligible employee shown with their review outcome
// (the justification), current pay, and comp history — recommend → a DIFFERENT person
// approves (SoD, server-enforced) → the payroll handoff fires only when the cycle is
// comp-linked (a legal gate, default OFF). AI is never involved.
type CompStage = 'none' | 'recommended' | 'approved' | 'rejected' | 'sent';
const stageOf = (r?: api.CompRecommendation): CompStage => {
  if (!r) return 'none';
  if (r.status === 'rejected') return 'rejected';
  if (r.status === 'approved') return r.pushed_to_payroll ? 'sent' : 'approved';
  return 'recommended';
};

// One employee row: review outcome + current pay + recommend/approve inline.
// Tiny per-goal achievement bar (fill-to-target + overachievement badge, matching goals).
const AchvBar: React.FC<{ pct: number }> = ({ pct }) => {
  const met = pct >= 100, over = Math.max(0, Math.round(pct - 100));
  return (
    <div className="flex items-center gap-1.5">
      <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden"><div className={`h-full rounded-full ${met ? 'bg-success' : 'bg-orange-500'}`} style={{ width: `${Math.min(100, pct)}%` }} /></div>
      {over > 0 && <span className="text-[9px] font-semibold text-success">+{over}%</span>}
    </div>
  );
};

const CompCandidateRow: React.FC<{
  cand: api.CompCandidate; rec?: api.CompRecommendation; cycleId: string; onChanged: () => void;
}> = ({ cand, rec, cycleId, onChanged }) => {
  const { t } = useTranslation('performance');
  const [open, setOpen] = useState(false);
  const [payNum, setPayNum] = useState<number | null>(null);
  const [payStr, setPayStr] = useState<string | null>(null);
  const [history, setHistory] = useState<api.CompRecommendation[]>([]);
  const [bonus, setBonus] = useState<api.BonusBreakdown | null>(null);
  const [target, setTarget] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const stage = stageOf(rec);

  const loadBonus = () => api.getBonusBreakdown(cycleId, cand.user_id).then((b) => { setBonus(b); setTarget(String(b.annual_bonus_target || '')); }).catch(() => setBonus(null));
  const expand = async () => {
    const next = !open; setOpen(next);
    if (next && payNum === null) {
      if (cand.employee_id && cand.employee_id !== '00000000-0000-0000-0000-000000000000') {
        getCompensation(cand.employee_id).then((cs) => { const a = cs.find((c) => c.active) || cs[0]; if (a) { setPayNum(Number(a.amount)); setPayStr(formatMoney(Number(a.amount), a.currency)); } else { setPayNum(0); setPayStr('—'); } }).catch(() => { setPayNum(0); setPayStr('—'); });
      } else { setPayNum(0); setPayStr('—'); }
      api.getCompHistory(cand.user_id).then(setHistory).catch(() => setHistory([]));
      loadBonus();
    }
  };
  const saveTarget = async () => {
    setBusy(true);
    try { const b = await api.saveBonusPlan(cycleId, cand.user_id, { annual_bonus_target: Number(target) || 0 }); setBonus(b); toast.success(t('saved', 'Saved')); }
    catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } finally { setBusy(false); }
  };
  const recommendBonus = async () => {
    if (!bonus || bonus.total_bonus <= 0) return;
    setBusy(true);
    try {
      await api.recommendCompensation({ subject_user_id: cand.user_id, cycle_id: cycleId, review_id: cand.review_id || undefined, kind: 'bonus', amount: bonus.total_bonus, currency: bonus.currency, rationale: t('goal_linked_rationale', 'Goal-linked bonus from cycle achievement') });
      toast.success(t('recommended_ok', 'Recommendation created')); onChanged();
    } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } finally { setBusy(false); }
  };
  const decide = async (approve: boolean) => {
    if (!rec) return;
    setBusy(true);
    try { approve ? await api.approveCompensation(rec.id, note) : await api.rejectCompensation(rec.id, note); toast.success(approve ? t('approved', 'Approved') : t('rejected', 'Rejected')); onChanged(); }
    catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } finally { setBusy(false); }
  };

  const stageChip: Record<CompStage, { label: string; cls: string }> = {
    none: { label: t('stage_none', 'Not recommended'), cls: 'bg-slate-100 text-slate-600' },
    recommended: { label: t('stage_recommended', 'Awaiting approval'), cls: 'bg-warning-soft text-warning-strong' },
    approved: { label: t('stage_approved', 'Approved'), cls: 'bg-info-soft text-info-strong' },
    sent: { label: t('stage_sent', 'Sent to payroll'), cls: 'bg-success-soft text-success-strong' },
    rejected: { label: t('stage_rejected', 'Rejected'), cls: 'bg-danger-soft text-danger-strong' },
  };
  const cur = bonus?.currency || 'TZS';
  const ctc = payNum != null && bonus ? payNum + bonus.total_bonus : null;

  return (
    <div className="border-b border-border last:border-0">
      <button onClick={expand} className="w-full flex items-center gap-3 py-2.5 text-left text-sm">
        <span className="flex-1 min-w-0"><span className="font-medium text-foreground truncate">{cand.name || cand.user_id.slice(0, 8)}</span>
          {cand.department && <span className="text-xs text-muted-foreground"> · {cand.department}</span>}</span>
        <span className="text-xs text-muted-foreground tabular-nums w-24 text-right">{cand.final_rating != null ? `★ ${cand.final_rating}` : '—'}{cand.final_score != null ? ` · ${cand.final_score}` : ''}</span>
        <span className={`text-[10px] rounded-full px-2 py-0.5 ${stageChip[stage].cls} w-28 text-center shrink-0`}>{stageChip[stage].label}</span>
        <span className="text-muted-foreground">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="pb-4 pl-1 space-y-3">
          {/* Goal-linked bonus calculator */}
          <div className="rounded-lg border border-border overflow-hidden">
            <div className="bg-muted/40 px-3 py-2 flex flex-wrap items-end justify-between gap-3">
              <div>
                <div className="text-[11px] text-muted-foreground">{t('annual_bonus_target', 'Annual bonus target')}</div>
                <div className="flex items-center gap-1.5">
                  <Input type="number" value={target} onChange={(e) => setTarget(e.target.value)} className="h-8 w-32" placeholder="0" />
                  <Button size="sm" variant="outline" className="h-8" onClick={saveTarget} disabled={busy}>{t('save', 'Save')}</Button>
                </div>
                {bonus?.target_source === 'policy' && (
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    {t('target_from_policy', 'From policy')} {bonus.policy_name ? `“${bonus.policy_name}”` : ''}{bonus.eligible_salary ? ` · ${t('eligible_salary', 'eligible salary')} ${formatMoney(bonus.eligible_salary, cur)}/mo (${t('frozen', 'frozen')})` : ''}
                  </div>
                )}
                {bonus?.target_source === 'manual' && Number(bonus?.annual_bonus_target) > 0 && (
                  <div className="text-[11px] text-muted-foreground mt-0.5">{t('target_manual', 'Manually set — policy runs never overwrite it.')}</div>
                )}
              </div>
              {bonus && (
                <div className="flex gap-4 text-right">
                  <div><div className="text-[11px] text-muted-foreground">{t('quarterly', 'Quarterly')}</div><div className="text-sm font-medium tabular-nums">{formatMoney(bonus.quarterly_bonus, cur)}</div></div>
                  <div><div className="text-[11px] text-muted-foreground">{t('this_cycle_pot', 'This cycle')}</div><div className="text-sm font-medium tabular-nums">{formatMoney(bonus.cycle_pot, cur)}</div></div>
                </div>
              )}
            </div>

            {/* goals → earned */}
            <div className="divide-y divide-border">
              {!bonus ? <div className="p-3 text-sm text-muted-foreground">…</div>
                : bonus.goals.length === 0 ? <div className="p-3 text-sm text-muted-foreground">{t('no_bonus_goals', 'No weighted goals in this cycle to allocate the bonus across.')}</div>
                  : bonus.goals.map((g) => (
                    <div key={g.goal_id} className="px-3 py-2">
                      <div className="flex items-center justify-between gap-3 text-sm">
                        <span className="flex-1 min-w-0 truncate font-medium text-foreground">{g.title}</span>
                        <span className="w-14 text-right text-xs text-muted-foreground">{g.weight}%</span>
                        <span className="w-14 text-right text-xs tabular-nums">{Math.round(g.achievement)}%</span>
                        <span className="w-24 text-right text-xs text-muted-foreground tabular-nums">{formatMoney(g.targeted, cur)}</span>
                        <span className="w-24 text-right text-sm font-semibold text-success tabular-nums">{formatMoney(g.earned, cur)}</span>
                      </div>
                      <div className="mt-1"><AchvBar pct={g.achievement} /></div>
                    </div>
                  ))}
            </div>
            <div className="flex items-center gap-3 px-3 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground border-t border-border">
              <span className="flex-1">{t('goal', 'Goal')}</span><span className="w-14 text-right">{t('weight', 'Weight')}</span><span className="w-14 text-right">{t('achieved', 'Achieved')}</span><span className="w-24 text-right">{t('targeted', 'Targeted')}</span><span className="w-24 text-right">{t('earned', 'Earned')}</span>
            </div>
          </div>

          {/* Calculated bonus + total CTC */}
          {bonus && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-lg border border-success bg-success-soft p-3">
                <div className="text-[11px] text-muted-foreground">{t('total_bonus', 'Total bonus')}</div>
                <div className="text-2xl font-bold text-success tabular-nums">{formatMoney(bonus.total_bonus, cur)}</div>
                <div className="text-[11px] text-muted-foreground">{t('earned_payout', 'Earned bonus payout')}</div>
              </div>
              <div className="rounded-lg border border-border p-3">
                <div className="text-[11px] text-muted-foreground">{t('total_ctc', 'Total annual CTC')}</div>
                <div className="text-2xl font-bold tabular-nums">{ctc != null ? formatMoney(ctc, cur) : '—'}</div>
                <div className="text-[11px] text-muted-foreground">{t('base_plus_bonus', 'Base salary + bonus')}{payStr ? ` · ${t('base', 'base')} ${payStr}` : ''}</div>
              </div>
            </div>
          )}

          {/* Recommendation: staged workflow OR recommend the computed bonus */}
          {rec ? (
            <div className="rounded-md border border-border p-2.5 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="capitalize">{rec.kind}: {rec.kind === 'bonus' && rec.amount != null ? formatMoney(rec.amount, rec.currency) : (rec.percent != null ? `${rec.percent}%` : '—')}{rec.period ? ` · ${rec.period}` : ''}</span>
                <span className={`text-[10px] rounded-full px-2 py-0.5 ${stageChip[stage].cls}`}>{stageChip[stage].label}</span>
              </div>
              {rec.rationale && <div className="text-xs text-muted-foreground">{rec.rationale}</div>}
              {rec.paid_at && <div className="text-[11px] text-success-strong">✓ {t('paid_by_run', 'Paid — consumed by a locked payroll run')}</div>}
              {rec.reversed_at && <div className="text-[11px] text-red-500">{t('reversed', 'Reversed')}: {rec.reverse_reason}</div>}
              {rec.pushed_to_payroll && !rec.reversed_at && (
                <div className="flex items-center gap-2">
                  <Input value={revReason} onChange={(e) => setRevReason(e.target.value)} placeholder={t('reverse_reason', 'Reason to reverse…')} className="h-7 flex-1 text-xs" />
                  <Button size="sm" variant="ghost" className="h-7 text-red-500" disabled={busy || !revReason.trim()} onClick={async () => {
                    setBusy(true);
                    try { await api.reverseAward(rec.id, revReason.trim()); toast.success(t('reversed_ok', 'Reversal sent to payroll')); onChanged(); }
                    catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); }
                    finally { setBusy(false); }
                  }}>{t('reverse', 'Reverse')}</Button>
                </div>
              )}
              {stage === 'recommended' && (
                <div className="flex items-center gap-2">
                  <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('decision_note', 'Decision note (optional)')} className="h-8 flex-1" />
                  <Button size="sm" variant="ghost" onClick={() => decide(false)} disabled={busy}>{t('reject', 'Reject')}</Button>
                  <Button size="sm" onClick={() => decide(true)} disabled={busy} className="bg-green-600 hover:bg-green-700">{t('approve', 'Approve')}</Button>
                </div>
              )}
            </div>
          ) : (
            <Button size="sm" onClick={recommendBonus} disabled={busy || !bonus || bonus.total_bonus <= 0}>
              {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{t('recommend_bonus', 'Recommend this bonus')} {bonus && bonus.total_bonus > 0 ? `(${formatMoney(bonus.total_bonus, cur)})` : ''}
            </Button>
          )}

          {history.length > 0 && (
            <div>
              <div className="text-[11px] font-medium text-muted-foreground mb-1">{t('comp_history', 'Compensation history')}</div>
              {history.map((h) => (
                <div key={h.id} className="text-xs text-muted-foreground flex justify-between">
                  <span className="capitalize">{h.kind}: {h.kind === 'bonus' && h.amount != null ? formatMoney(h.amount, h.currency) : (h.percent != null ? `${h.percent}%` : '—')}</span>
                  <span>{h.status}{h.pushed_to_payroll ? ' · sent' : ''} · {fmtDate(h.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CompensationTab: React.FC = () => {
  const { t } = useTranslation('performance');
  const [cycles, setCycles] = useState<api.Cycle[]>([]);
  const [cycleId, setCycleId] = useState('');
  const [linked, setLinked] = useState(false);
  const [cands, setCands] = useState<api.CompCandidate[]>([]);
  const [recs, setRecs] = useState<api.CompRecommendation[]>([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  useEffect(() => { api.listCycles().then(setCycles).catch(() => setCycles([])); }, []);

  const load = (id: string) => {
    setLoading(true);
    setLinked(!!cycles.find((x) => x.id === id)?.comp_linked);
    Promise.all([api.getCompCandidates(id).catch(() => []), api.listCompensation(id).catch(() => [])])
      .then(([c, r]) => { setCands(c); setRecs(r); }).finally(() => setLoading(false));
  };
  const pick = (id: string) => { setCycleId(id); if (id) load(id); else { setCands([]); setRecs([]); } };
  const toggleLinked = async () => {
    try { const r = await api.setCompLinked(cycleId, !linked); setLinked(r.comp_linked); toast.success(t('saved', 'Saved')); }
    catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); }
  };

  // Latest recommendation per subject (for the row), + budget rollup.
  const recBySubject = useMemo(() => {
    const m: Record<string, api.CompRecommendation> = {};
    for (const r of recs) if (!m[r.subject_user_id]) m[r.subject_user_id] = r; // recs are newest-first
    return m;
  }, [recs]);
  const budget = useMemo(() => {
    const sum = (pred: (r: api.CompRecommendation) => boolean) =>
      recs.filter((r) => r.kind === 'bonus' && pred(r)).reduce((s, r) => s + (r.amount || 0), 0);
    return {
      recommendedN: recs.filter((r) => r.status === 'recommended').length,
      approved: sum((r) => r.status === 'approved'),
      sent: sum((r) => r.pushed_to_payroll),
      pending: sum((r) => r.status === 'recommended'),
    };
  }, [recs]);

  const rows = cands.filter((c) => (c.name || '').toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <select value={cycleId} onChange={(e) => pick(e.target.value)} className="h-9 rounded-md border border-border bg-background px-2.5 text-sm min-w-[220px]">
          <option value="">{t('select_cycle', 'Select a cycle…')}</option>
          {cycles.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {cycleId && <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('search_people', 'Search…')} className="h-9 max-w-[200px]" />}
        {cycleId && (
          <Button size="sm" variant="outline" className="h-9" onClick={async () => {
            try {
              const s2 = await api.resolveBonusTargets(cycleId);
              toast.success(t('targets_resolved', '{{n}} target(s) set from policy · {{k}} skipped', { n: s2.resolved, k: s2.skipped } as any));
              load(cycleId);
            } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); }
          }}>{t('resolve_targets', 'Resolve targets from policy')}</Button>
        )}
      </div>

      {!cycleId ? <Empty text={t('comp_pick_cycle', 'Pick a review cycle to review its team and their compensation.')} /> : loading ? <Loading /> : (
        <>
          <PoolsCard cycleId={cycleId} onProposed={() => load(cycleId)} />
          <MeritProposalsCard />
          {/* Budget rollup */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label={t('pending_approval', 'Awaiting approval')} value={budget.recommendedN} sub={budget.pending ? formatMoney(budget.pending) : undefined} />
            <StatCard label={t('approved_total', 'Approved')} value={formatMoney(budget.approved)} />
            <StatCard label={t('sent_total', 'Sent to payroll')} value={formatMoney(budget.sent)} />
            <StatCard label={t('people', 'People')} value={cands.length} />
          </div>

          {/* Legal gate — clear about what it does */}
          <Card className={linked ? 'border-success' : 'border-warning'}>
            <CardContent className="pt-4 flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium text-foreground flex items-center gap-1.5">{t('payroll_link', 'Payroll link')}
                  <span className={`text-[10px] rounded-full px-2 py-0.5 ${linked ? 'bg-success-soft text-success-strong' : 'bg-warning-soft text-warning-strong'}`}>{linked ? t('on', 'On') : t('legal_hold', 'Legal hold')}</span></div>
                <div className="text-xs text-muted-foreground">{linked ? t('link_on', 'Approved bonuses in this cycle are sent to payroll.') : t('link_off', 'Legal hold — approvals are recorded but NOT sent to payroll.')}</div>
              </div>
              <Button size="sm" variant={linked ? 'outline' : 'default'} onClick={toggleLinked}>{linked ? t('disable', 'Disable') : t('enable', 'Enable')}</Button>
            </CardContent>
          </Card>

          {/* Roster — each person with review outcome + comp, sorted highest-rated first */}
          <Card>
            <CardContent className="pt-3">
              <div className="flex items-center gap-3 pb-1.5 text-[11px] text-muted-foreground border-b border-border">
                <span className="flex-1">{t('employee', 'Employee')}</span>
                <span className="w-24 text-right">{t('review_outcome', 'Review')}</span>
                <span className="w-28 text-center">{t('status', 'Status')}</span><span className="w-4" />
              </div>
              {rows.length === 0 ? <Empty text={t('no_candidates', 'No one to review in this cycle.')} /> :
                rows.map((c) => <CompCandidateRow key={c.user_id} cand={c} rec={recBySubject[c.user_id]} cycleId={cycleId} onChanged={() => load(cycleId)} />)}
            </CardContent>
          </Card>

          <BonusReportCard cycleId={cycleId} />
        </>
      )}
    </div>
  );
};

// Rendered, printable, exportable bonus report (replaces the raw CSV dump). Preview on
// screen first (our theme), then Export CSV / Print (→ Save as PDF). No hardcoded data.
const BonusReportCard: React.FC<{ cycleId: string }> = ({ cycleId }) => {
  const { t } = useTranslation('performance');
  const [report, setReport] = useState<api.BonusReport | null>(null);
  const [busy, setBusy] = useState(false);
  const generate = async () => {
    setBusy(true);
    try { setReport(await api.getBonusReport(cycleId)); }
    catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } finally { setBusy(false); }
  };
  const cur = report?.currency || 'TZS';
  const exportCsv = () => {
    if (!report) return;
    const header = ['Name', 'Title', 'Reports to', 'Target bonus', 'Earned bonus', 'Percent achieved'];
    const lines = report.rows.map((r) => [r.name, r.title, r.manager_name, r.target_bonus, r.earned_bonus, r.percent_achieved]
      .map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','));
    const totals = ['Total', '', '', report.total_target, report.total_earned, report.total_percent].map((c) => `"${c}"`).join(',');
    const csv = [header.join(','), ...lines, totals].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `bonus-${report.cycle_name.replace(/\s+/g, '-')}.csv`; a.click(); URL.revokeObjectURL(url);
  };
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between pb-2">
        <CardTitle className="text-base">{t('bonus_report', 'Bonus report')}</CardTitle>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={generate} disabled={busy}>{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{t('preview', 'Preview')}</Button>
          <Button size="sm" variant="ghost" onClick={exportCsv} disabled={!report}>{t('export_csv', 'Export CSV')}</Button>
          <Button size="sm" variant="ghost" onClick={() => window.print()} disabled={!report}>{t('print_pdf', 'Print / PDF')}</Button>
        </div>
      </CardHeader>
      <CardContent>
        {!report ? <p className="text-sm text-muted-foreground">{t('bonus_report_hint', 'Preview the goal-linked bonus for everyone in this cycle — target vs earned + % achieved — then export or print.')}</p> : (
          <div className="space-y-2">
            <div className="text-sm font-medium">{report.cycle_name} · <span className="text-muted-foreground">{report.rows.length} {t('people', 'people')}</span></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-muted-foreground border-b border-border">
                  <th className="py-1.5 pr-3">{t('name', 'Name')}</th><th className="pr-3">{t('title', 'Title')}</th><th className="pr-3">{t('reports_to', 'Reports to')}</th>
                  <th className="pr-3 text-right">{t('target_bonus', 'Target bonus')}</th><th className="pr-3 text-right">{t('earned', 'Earned')}</th><th className="text-right">{t('achieved', 'Achieved')}</th>
                </tr></thead>
                <tbody>
                  {report.rows.map((r, i) => (
                    <tr key={i} className="border-b border-border">
                      <td className="py-1.5 pr-3">{r.name}</td>
                      <td className="pr-3 text-muted-foreground">{r.title || '—'}</td>
                      <td className="pr-3 text-muted-foreground">{r.manager_name || '—'}</td>
                      <td className="pr-3 text-right tabular-nums">{formatMoney(r.target_bonus, cur)}</td>
                      <td className="pr-3 text-right tabular-nums text-success">{formatMoney(r.earned_bonus, cur)}</td>
                      <td className="text-right tabular-nums">{r.percent_achieved}%</td>
                    </tr>
                  ))}
                  <tr className="font-semibold border-t-2 border-border">
                    <td className="py-1.5 pr-3">{t('total', 'Total')}</td><td /><td />
                    <td className="pr-3 text-right tabular-nums">{formatMoney(report.total_target, cur)}</td>
                    <td className="pr-3 text-right tabular-nums text-success">{formatMoney(report.total_earned, cur)}</td>
                    <td className="text-right tabular-nums">{report.total_percent}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ---- Analytics (HR/exec) ----------------------------------------------------
const AnalyticsTab: React.FC = () => {
  const { t } = useTranslation('performance');
  const [reviews, setReviews] = useState<api.Review[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.listTeamReviews().then(setReviews).catch(() => setReviews([])).finally(() => setLoading(false)); }, []);
  const stats = useMemo(() => {
    const total = reviews.length;
    const finalized = reviews.filter((r) => r.status === 'finalized').length;
    const rated = reviews.filter((r) => r.final_rating != null);
    const avg = rated.length ? (rated.reduce((s, r) => s + (r.final_rating || 0), 0) / rated.length) : 0;
    const dist: Record<string, number> = { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0 };
    rated.forEach((r) => { const b = String(Math.min(5, Math.max(1, Math.round(r.final_rating || 0)))); dist[b]++; });
    return { total, finalized, avg, dist };
  }, [reviews]);

  if (loading) return <Loading />;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard label={t('an_completion', 'Review completion')} value={`${stats.total ? Math.round((stats.finalized / stats.total) * 100) : 0}%`} sub={`${stats.finalized}/${stats.total}`} />
        <StatCard label={t('an_avg_rating', 'Average rating')} value={stats.avg ? stats.avg.toFixed(1) : '—'} />
        <StatCard label={t('an_reviews', 'Reviews')} value={stats.total} />
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base">{t('an_distribution', 'Rating distribution')}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {['5', '4', '3', '2', '1'].map((k) => {
            const n = stats.dist[k]; const pct = stats.total ? Math.round((n / stats.total) * 100) : 0;
            return (
              <div key={k} className="flex items-center gap-3 text-sm">
                <span className="w-4 text-muted-foreground">{k}★</span>
                <div className="flex-1"><Progress value={pct} /></div>
                <span className="w-8 text-right text-muted-foreground">{n}</span>
              </div>
            );
          })}
        </CardContent>
      </Card>
      <PerformanceTrendCard />
      <CycleReportCard />
      <p className="text-sm text-muted-foreground"><AskDana q="Which teams have low goal completion, and who may need development support?" /></p>
    </div>
  );
};

// Cross-cycle trend + per-department breakdown (advanced analytics).
const PerformanceTrendCard: React.FC = () => {
  const { t } = useTranslation('performance');
  const [trend, setTrend] = useState<api.CycleTrend[]>([]);
  const [deptCycle, setDeptCycle] = useState('');
  const [depts, setDepts] = useState<api.DeptBreakdown[]>([]);
  useEffect(() => { api.getPerformanceOverview().then((tr) => { setTrend(tr); if (tr[0]) { setDeptCycle(tr[0].cycle_id); } }).catch(() => setTrend([])); }, []);
  useEffect(() => { if (deptCycle) api.getCycleDepartments(deptCycle).then(setDepts).catch(() => setDepts([])); }, [deptCycle]);

  if (trend.length === 0) return null;
  const maxRating = 5;
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{t('trend', 'Trend across cycles')}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {/* Avg rating + completion per cycle (oldest → newest left to right) */}
        <div className="space-y-2">
          {trend.slice().reverse().map((c) => (
            <div key={c.cycle_id} className="flex items-center gap-3 text-sm">
              <span className="w-28 truncate text-muted-foreground" title={c.cycle_name}>{c.cycle_name}</span>
              <div className="flex-1 flex items-center gap-2">
                <div className="flex-1"><Progress value={((c.avg_final_rating ?? 0) / maxRating) * 100} /></div>
                <span className="w-10 text-right tabular-nums">{c.avg_final_rating != null ? c.avg_final_rating.toFixed(1) : '—'}</span>
              </div>
              <span className="w-12 text-right text-xs text-muted-foreground">{c.completion_pct}%</span>
            </div>
          ))}
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
            <span className="w-28" /> <span className="flex-1">{t('avg_rating', 'Avg rating')} (0–5)</span> <span className="w-12 text-right">{t('an_completion', 'Completion')}</span>
          </div>
        </div>

        {/* Department breakdown for a chosen cycle */}
        <div className="border-t border-border pt-3 space-y-2">
          <select value={deptCycle} onChange={(e) => setDeptCycle(e.target.value)} className="h-8 rounded-md border border-border bg-background px-2 text-sm">
            {trend.map((c) => <option key={c.cycle_id} value={c.cycle_id}>{c.cycle_name}</option>)}
          </select>
          {depts.length === 0 ? <p className="text-sm text-muted-foreground">{t('no_dept_data', 'No department data yet.')}</p> : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-muted-foreground border-b border-border">
                <th className="py-1">{t('department', 'Department')}</th><th className="text-right">{t('participants', 'People')}</th>
                <th className="text-right">{t('an_completion', 'Completion')}</th><th className="text-right">{t('avg_rating', 'Avg rating')}</th>
              </tr></thead>
              <tbody>
                {depts.map((d) => (
                  <tr key={d.department} className="border-b border-border">
                    <td className="py-1">{d.department}</td>
                    <td className="text-right tabular-nums">{d.participants}</td>
                    <td className="text-right tabular-nums">{d.completion_pct}%</td>
                    <td className="text-right tabular-nums">{d.avg_rating != null ? d.avg_rating.toFixed(1) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Cycle report with a mandatory on-screen PREVIEW before any download (product rule:
// never download a report without an optional preview). Export builds CSV from the
// same data that's on screen, and Print uses the rendered preview.
const CycleReportCard: React.FC = () => {
  const { t } = useTranslation('performance');
  const [cycles, setCycles] = useState<api.Cycle[]>([]);
  const [cycleId, setCycleId] = useState('');
  const [report, setReport] = useState<api.CycleReport | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.listCycles().then(setCycles).catch(() => setCycles([])); }, []);

  const generate = async () => {
    if (!cycleId) return;
    setBusy(true);
    try { setReport(await api.getCycleReport(cycleId)); }
    catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } finally { setBusy(false); }
  };

  const exportCsv = () => {
    if (!report) return;
    const header = ['Name', 'Status', 'Self', 'Manager', 'Final rating', 'Final score'];
    const lines = report.rows.map((r) => [
      r.name || r.employee_id, r.status, r.self_status, r.manager_status,
      r.final_rating ?? '', r.final_score ?? '',
    ].map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','));
    const csv = [header.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `performance-${report.cycle_name.replace(/\s+/g, '-')}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const s = report?.summary;
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{t('cycle_report', 'Cycle report')}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <select value={cycleId} onChange={(e) => { setCycleId(e.target.value); setReport(null); }}
            className="h-9 rounded-md border border-border bg-background px-2.5 text-sm min-w-[200px]">
            <option value="">{t('select_cycle', 'Select a cycle…')}</option>
            {cycles.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={generate} disabled={!cycleId || busy}>
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{t('preview', 'Preview')}
          </Button>
          {/* Export is only available AFTER a preview is on screen. */}
          <Button size="sm" variant="ghost" onClick={exportCsv} disabled={!report}>{t('export_csv', 'Export CSV')}</Button>
          <Button size="sm" variant="ghost" onClick={() => window.print()} disabled={!report}>{t('print', 'Print')}</Button>
        </div>

        {report && s && (
          <div className="space-y-3 border-t border-border pt-3">
            <div className="text-sm font-medium text-foreground">{report.cycle_name} · <span className="capitalize text-muted-foreground">{human(report.status)}</span></div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard label={t('participants', 'Participants')} value={s.participants} />
              <StatCard label={t('an_completion', 'Completion')} value={`${s.completion_pct}%`} sub={`${s.finalized}/${s.participants}`} />
              <StatCard label={t('avg_score', 'Avg score')} value={s.avg_final_score != null ? s.avg_final_score.toFixed(2) : '—'} />
              <StatCard label={t('an_avg_rating', 'Avg rating')} value={s.avg_final_rating != null ? s.avg_final_rating.toFixed(1) : '—'} />
            </div>
            <div className="space-y-1">
              {report.rating_distribution.slice().reverse().map((b) => {
                const pct = s.participants ? Math.round((b.count / s.participants) * 100) : 0;
                return (
                  <div key={b.bucket} className="flex items-center gap-3 text-sm">
                    <span className="w-4 text-muted-foreground">{b.bucket}★</span>
                    <div className="flex-1"><Progress value={pct} /></div>
                    <span className="w-8 text-right text-muted-foreground">{b.count}</span>
                  </div>
                );
              })}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-muted-foreground border-b border-border">
                  <th className="py-1.5 pr-3">{t('name', 'Name')}</th><th className="pr-3">{t('status', 'Status')}</th>
                  <th className="pr-3">{t('self', 'Self')}</th><th className="pr-3">{t('manager', 'Manager')}</th>
                  <th className="pr-3 text-right">{t('rating', 'Rating')}</th><th className="text-right">{t('score', 'Score')}</th>
                </tr></thead>
                <tbody>
                  {report.rows.map((r) => (
                    <tr key={r.employee_id} className="border-b border-border">
                      <td className="py-1.5 pr-3">{r.name || r.employee_id.slice(0, 8)}</td>
                      <td className="pr-3 capitalize text-muted-foreground">{human(r.status)}</td>
                      <td className="pr-3 capitalize text-muted-foreground">{human(r.self_status)}</td>
                      <td className="pr-3 capitalize text-muted-foreground">{human(r.manager_status)}</td>
                      <td className="pr-3 text-right tabular-nums">{r.final_rating ?? '—'}</td>
                      <td className="text-right tabular-nums">{r.final_score ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ---- Administration (HR/owner) ----------------------------------------------
// Wires the already-built config backends (cycles, rating scales, scoring formula,
// review templates) to a UI so admins can actually manage them — instead of cycle
// creation being welded to the launch wizard and the rest being unreachable.
const ADMIN_SECTIONS = [
  { key: 'cycles', label: 'Cycles' },
  { key: 'scales', label: 'Rating scales' },
  { key: 'formula', label: 'Scoring formula' },
  { key: 'competencies', label: 'Competencies' },
  { key: 'templates', label: 'Review templates' },
  { key: 'categories', label: 'Goal categories' },
  { key: 'recognition_values', label: 'Recognition values' },
  { key: 'frameworks', label: 'Frameworks' },
  { key: 'bonus_policies', label: 'Bonus policies' },
  { key: 'settings', label: 'Settings' },
];
const CYCLE_TYPES = ['annual', 'half_year', 'quarter', 'probation', 'custom'];
const CYCLE_STATUSES = ['draft', 'active', 'in_review', 'closed', 'cancelled'];

const AdminTab: React.FC = () => {
  const { t } = useTranslation('performance');
  const [section, setSection] = useState('cycles');
  return (
    <div className="flex flex-col md:flex-row gap-4">
      {/* secondary left-rail nav within Admin */}
      <nav className="md:w-48 shrink-0 flex md:flex-col gap-1 overflow-x-auto" aria-label={t('admin_nav', 'Administration')}>
        {ADMIN_SECTIONS.map((s) => (
          <button key={s.key} onClick={() => setSection(s.key)}
            className={`text-left text-sm rounded-md px-3 py-2 whitespace-nowrap transition ${section === s.key ? 'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-300 font-medium' : 'text-muted-foreground hover:bg-muted'}`}>
            {t(`admin_${s.key}`, s.label)}
          </button>
        ))}
      </nav>
      <div className="flex-1 min-w-0">
        {section === 'cycles' && <CyclesAdmin />}
        {section === 'scales' && <ScalesAdmin />}
        {section === 'formula' && <FormulaAdmin />}
        {section === 'competencies' && <CompetenciesAdmin />}
        {section === 'templates' && <TemplatesAdmin />}
        {section === 'categories' && <CategoriesAdmin />}
        {section === 'recognition_values' && <RecognitionValuesAdmin />}
        {section === 'frameworks' && <FrameworksAdmin />}
        {section === 'bonus_policies' && <BonusPoliciesAdmin />}
        {section === 'settings' && <SettingsAdmin />}
      </div>
    </div>
  );
};

// Competency library + per-cycle assignment. HR authors competencies once, then
// assigns a weighted set to a cycle (rate_competencies must be on for that cycle).
const CompetenciesAdmin: React.FC = () => {
  const { t } = useTranslation('performance');
  const [lib, setLib] = useState<api.Competency[]>([]);
  const [cycles, setCycles] = useState<api.Cycle[]>([]);
  const [cycleId, setCycleId] = useState('');
  const [assigned, setAssigned] = useState<api.CycleCompetency[]>([]);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [busy, setBusy] = useState(false);
  const [frameworks, setFrameworks] = useState<workforce.Framework[]>([]);
  const [fwId, setFwId] = useState('');
  const loadLib = () => api.listCompetencies().then(setLib).catch(() => setLib([]));
  useEffect(() => {
    loadLib();
    api.listCycles().then(setCycles).catch(() => setCycles([]));
    workforce.listFrameworks().then(setFrameworks).catch(() => setFrameworks([]));
  }, []);

  const importFromFramework = async () => {
    if (!fwId) return;
    setBusy(true);
    try {
      const detail = await workforce.getFramework(fwId);
      const rows = (detail.definition.competencies || []).map((c) => ({ key: c.key, name: c.label || c.key, description: c.description }));
      if (rows.length === 0) { toast.error(t('no_fw_competencies', 'That framework defines no competencies.')); return; }
      const r = await api.importCompetencies(rows);
      toast.success(t('imported_n', '{{imported}} imported, {{skipped}} already present', { imported: r.imported, skipped: r.skipped }));
      loadLib();
    } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } finally { setBusy(false); }
  };
  useEffect(() => { if (cycleId) api.getCycleCompetencies(cycleId).then(setAssigned).catch(() => setAssigned([])); else setAssigned([]); }, [cycleId]);

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { await api.createCompetency({ name: name.trim(), category: category.trim() || undefined }); setName(''); setCategory(''); loadLib(); toast.success(t('saved', 'Saved')); }
    catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } finally { setBusy(false); }
  };
  const toggle = async (c: api.Competency) => { try { await api.updateCompetency(c.id, { active: !c.active }); loadLib(); } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } };

  const inSet = (id: string) => assigned.some((a) => a.competency_id === id);
  const setWeight = (id: string, w: number) => setAssigned((prev) => prev.map((a) => a.competency_id === id ? { ...a, weight: w } : a));
  const addToCycle = (c: api.Competency) => setAssigned((prev) => [...prev, { competency_id: c.id, name: c.name, weight: 0 }]);
  const removeFromCycle = (id: string) => setAssigned((prev) => prev.filter((a) => a.competency_id !== id));
  const totalWeight = assigned.reduce((s, a) => s + (a.weight || 0), 0);
  const saveAssign = async () => {
    setBusy(true);
    try { const r = await api.setCycleCompetencies(cycleId, assigned.map((a) => ({ competency_id: a.competency_id, weight: a.weight }))); setAssigned(r); toast.success(t('assign_saved', 'Cycle competencies saved')); }
    catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader><CardTitle className="text-base">{t('admin_competencies', 'Competencies')}</CardTitle>
          <p className="text-xs text-muted-foreground">{t('competencies_help', 'Reusable competencies rated on the default scale. Assign a weighted set to a cycle (enable “rate competencies” on the cycle) and their manager ratings roll into the competencies score.')}</p></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1"><Label className="text-xs">{t('name', 'Name')}</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 w-56" placeholder={t('competency_eg', 'e.g. Communication')} /></div>
            <div className="space-y-1"><Label className="text-xs">{t('category', 'Category')}</Label><Input value={category} onChange={(e) => setCategory(e.target.value)} className="h-9 w-40" placeholder={t('optional', 'optional')} /></div>
            <Button size="sm" onClick={add} disabled={busy || !name.trim()}><Plus className="mr-1.5 h-4 w-4" />{t('add', 'Add')}</Button>
          </div>
          {frameworks.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border p-2.5">
              <span className="text-xs text-muted-foreground">{t('import_from_fw', 'Import from a workforce framework:')}</span>
              <select value={fwId} onChange={(e) => setFwId(e.target.value)} className="h-8 rounded-md border border-border bg-background px-2 text-sm">
                <option value="">{t('select_framework', 'Select a framework…')}</option>
                {frameworks.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
              <Button size="sm" variant="outline" onClick={importFromFramework} disabled={busy || !fwId}>{t('import', 'Import')}</Button>
            </div>
          )}
          <div className="divide-y divide-border rounded-md border border-border">
            {lib.length === 0 ? <div className="p-3 text-sm text-muted-foreground">{t('no_competencies', 'No competencies yet.')}</div> :
              lib.map((c) => (
                <div key={c.id} className="flex items-center justify-between px-3 py-2 text-sm">
                  <div className="flex items-center gap-2 min-w-0"><span className={`truncate ${c.active ? '' : 'text-muted-foreground line-through'}`}>{c.name}</span>
                    {c.category && <Badge className="bg-muted text-muted-foreground border-0 text-[10px]">{c.category}</Badge>}
                    {c.source === 'workforce_import' && <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300 border-0 text-[10px]">{t('imported', 'imported')}</Badge>}</div>
                  <Button size="sm" variant="ghost" onClick={() => toggle(c)}>{c.active ? t('deactivate', 'Deactivate') : t('reactivate', 'Reactivate')}</Button>
                </div>
              ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">{t('assign_to_cycle', 'Assign to a cycle')}</CardTitle>
          <p className="text-xs text-muted-foreground">{t('assign_help', 'Weights are relative within the competencies component and must total 100% before a review can be finalized.')}</p></CardHeader>
        <CardContent className="space-y-3">
          <select value={cycleId} onChange={(e) => setCycleId(e.target.value)} className="h-9 w-full max-w-sm rounded-md border border-border bg-background px-2.5 text-sm">
            <option value="">{t('select_cycle', 'Select a cycle…')}</option>
            {cycles.map((c) => <option key={c.id} value={c.id}>{c.name}{c.rate_competencies ? '' : ` (${t('rating_off', 'rating off')})`}</option>)}
          </select>
          {cycleId && (
            <>
              {assigned.length > 0 && (
                <div className="space-y-1.5">
                  {assigned.map((a) => (
                    <div key={a.competency_id} className="grid grid-cols-[1fr_auto_auto] items-center gap-2 text-sm">
                      <span className="truncate">{a.name}</span>
                      <Input type="number" min={0} max={100} value={a.weight} onChange={(e) => setWeight(a.competency_id, Number(e.target.value))} className="h-8 w-20 text-right" />
                      <Button size="sm" variant="ghost" onClick={() => removeFromCycle(a.competency_id)}>{t('remove', 'Remove')}</Button>
                    </div>
                  ))}
                  <div className={`flex items-center justify-between rounded-md border px-3 py-2 text-xs ${totalWeight === 100 ? 'border-green-300 bg-green-50 dark:bg-green-500/10 text-green-800 dark:text-green-300' : 'border-amber-300 bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-300'}`}>
                    <span>{t('total', 'Total')}: <span className="font-semibold tabular-nums">{totalWeight}%</span></span>
                    <span>{totalWeight === 100 ? `✓ ${t('ready', 'ready')}` : t('must_100', 'must total 100%')}</span>
                  </div>
                </div>
              )}
              <div className="flex flex-wrap gap-1.5">
                {lib.filter((c) => c.active && !inSet(c.id)).map((c) => (
                  <button key={c.id} onClick={() => addToCycle(c)} className="rounded-full border border-border px-2.5 py-1 text-xs hover:bg-muted">+ {c.name}</button>
                ))}
              </div>
              <Button size="sm" onClick={saveAssign} disabled={busy}>{t('save', 'Save')}</Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const CyclesAdmin: React.FC = () => {
  const { t } = useTranslation('performance');
  const [rows, setRows] = useState<api.Cycle[]>([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState<api.Cycle | 'new' | null>(null);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const [busy, setBusy] = useState('');
  const load = () => api.listCycles().then(setRows).catch(() => setRows([])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const setStatus = async (c: api.Cycle, status: string) => { try { await api.updateCycle(c.id, { status } as any); toast.success(t('saved', 'Saved')); load(); } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } };
  const del = async (c: api.Cycle) => {
    setBusy(c.id);
    try { await api.deleteCycle(c.id); toast.success(t('cycle_deleted', 'Cycle deleted')); setConfirmDel(null); load(); }
    catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } finally { setBusy(''); }
  };
  if (loading) return <Loading />;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div><CardTitle className="text-base">{t('admin_cycles', 'Cycles')}</CardTitle><p className="text-xs text-muted-foreground">{t('cycles_help', 'Create and manage review cycles and their deadlines.')}</p></div>
        <Button size="sm" onClick={() => setEdit('new')}><Plus className="mr-1.5 h-4 w-4" />{t('new_cycle', 'New cycle')}</Button>
      </div>
      {rows.length === 0 ? <SectionEmpty text={t('no_cycles', 'No cycles yet. Create one to start goal-setting and reviews.')} cta={{ label: t('new_cycle', 'New cycle'), onClick: () => setEdit('new') }} /> : (
        <div className="space-y-2">
          {rows.map((c) => (
            <Card key={c.id}><CardContent className="pt-3.5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-foreground flex items-center gap-1.5 flex-wrap">{c.name} <span className="text-xs text-muted-foreground capitalize">· {c.type}</span>
                    {c.recurrence && c.recurrence !== 'none' && <Badge className="bg-info-soft text-info-strong border-0 text-[10px]">↻ {t(`recur_${c.recurrence}`, c.recurrence)}</Badge>}
                    {c.double_blind && <Badge className="bg-slate-100 text-slate-600 border-0 text-[10px]">🔒 {t('double_blind', 'Double-blind')}</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground">{c.start_date ? `${fmtDate(c.start_date)} – ${fmtDate(c.end_date)}` : '—'}{c.self_review_due_date ? ` · ${t('self_due', 'self due')} ${fmtDate(c.self_review_due_date)}` : ''}</div>
                </div>
                <div className="flex items-center gap-2">
                  {c.recurrence && c.recurrence !== 'none' && <Button size="sm" variant="ghost" onClick={async () => { try { await api.nextCycle(c.id); toast.success(t('next_created', 'Next cycle created (draft)')); load(); } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } }}>{t('create_next', 'Create next')}</Button>}
                  <select value={c.status} onChange={(e) => setStatus(c, e.target.value)} className="h-8 rounded-md border border-border bg-background px-1.5 text-xs">
                    {CYCLE_STATUSES.map((s) => <option key={s} value={s}>{human(s)}</option>)}
                  </select>
                  <Button size="sm" variant="outline" onClick={() => setEdit(c)}>{t('edit', 'Edit')}</Button>
                  <button onClick={() => setConfirmDel(confirmDel === c.id ? null : c.id)} aria-label={t('delete', 'Delete')} className="text-muted-foreground hover:text-red-500 p-1.5"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              {/* Inline confirm — delete is irreversible + cascades reviews. Prefer Cancel for real data. */}
              {confirmDel === c.id && (
                <div className="mt-3 rounded-md border border-danger bg-danger-soft p-2.5 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs text-danger-strong">{t('delete_cycle_warn', 'Permanently delete this cycle and all its reviews? This cannot be undone. To keep the data, set the status to Cancelled instead.')}</span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setConfirmDel(null)}>{t('cancel', 'Cancel')}</Button>
                    <Button size="sm" onClick={() => del(c)} disabled={busy === c.id} className="bg-rose-600 hover:bg-rose-700 text-white">{busy === c.id && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}{t('delete_permanently', 'Delete permanently')}</Button>
                  </div>
                </div>
              )}
            </CardContent></Card>
          ))}
        </div>
      )}
      {edit && <CycleEditDialog cycle={edit === 'new' ? null : edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
};

const CycleEditDialog: React.FC<{ cycle: api.Cycle | null; onClose: () => void; onSaved: () => void }> = ({ cycle, onClose, onSaved }) => {
  const { t } = useTranslation('performance');
  const [f, setF] = useState<any>(cycle
    ? { name: cycle.name, type: cycle.type, start_date: cycle.start_date, end_date: cycle.end_date, self_review_due_date: cycle.self_review_due_date, manager_review_due_date: cycle.manager_review_due_date, rate_competencies: cycle.rate_competencies }
    : { name: '', type: 'annual', start_date: '', end_date: '', self_review_due_date: '', manager_review_due_date: '', rate_competencies: false });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.name || !f.start_date || !f.end_date) { toast.error(t('cycle_fields_req', 'Name and dates are required')); return; }
    setBusy(true);
    try {
      const body = { name: f.name, type: f.type, start_date: f.start_date, end_date: f.end_date, self_review_due_date: f.self_review_due_date || undefined, manager_review_due_date: f.manager_review_due_date || undefined, rate_competencies: !!f.rate_competencies };
      cycle ? await api.updateCycle(cycle.id, body as any) : await api.createCycle({ ...body, status: 'draft' } as any);
      toast.success(t('saved', 'Saved')); onSaved();
    } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } finally { setBusy(false); }
  };
  const sel = 'w-full h-9 rounded-md border border-border bg-background px-2.5 text-sm';
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{cycle ? t('edit_cycle', 'Edit cycle') : t('new_cycle', 'New cycle')}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div><Label className="text-xs">{t('cycle_name', 'Cycle name')}</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="2026 Annual Review" /></div>
          <div><Label className="text-xs">{t('kind', 'Type')}</Label><select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className={sel}>{CYCLE_TYPES.map((x) => <option key={x} value={x}>{human(x)}</option>)}</select></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">{t('start', 'Start')}</Label><Input type="date" value={f.start_date || ''} onChange={(e) => setF({ ...f, start_date: e.target.value })} /></div>
            <div><Label className="text-xs">{t('end', 'End')}</Label><Input type="date" value={f.end_date || ''} onChange={(e) => setF({ ...f, end_date: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">{t('self_review_due', 'Self-review due')}</Label><Input type="date" value={f.self_review_due_date || ''} onChange={(e) => setF({ ...f, self_review_due_date: e.target.value })} /></div>
            <div><Label className="text-xs">{t('manager_review_due', 'Manager review due')}</Label><Input type="date" value={f.manager_review_due_date || ''} onChange={(e) => setF({ ...f, manager_review_due_date: e.target.value })} /></div>
          </div>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!f.rate_competencies} onChange={(e) => setF({ ...f, rate_competencies: e.target.checked })} className="mt-0.5 h-4 w-4 accent-orange-500" />
            <span>{t('rate_competencies', 'Rate competencies')}<span className="block text-xs text-muted-foreground">{t('rate_competencies_help', 'Collect per-competency ratings (assign the set in Admin → Competencies).')}</span></span>
          </label>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>{t('cancel', 'Cancel')}</Button><Button onClick={save} disabled={busy}>{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{t('save', 'Save')}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ScalesAdmin: React.FC = () => {
  const { t } = useTranslation('performance');
  const [scales, setScales] = useState<api.RatingScale[]>([]);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [opts, setOpts] = useState<api.RatingOption[]>([{ value: 1, label: '', ordinal: 1 }, { value: 2, label: '', ordinal: 2 }, { value: 3, label: '', ordinal: 3 }]);
  const [busy, setBusy] = useState(false);
  const load = () => api.getRatingScales().then(setScales).catch(() => setScales([]));
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!name.trim() || opts.some((o) => !o.label.trim())) { toast.error(t('scale_incomplete', 'Give the scale a name and label every point')); return; }
    setBusy(true);
    try { await api.createRatingScale({ name: name.trim(), is_default: scales.length === 0, options: opts.map((o, i) => ({ ...o, ordinal: i + 1 })) }); toast.success(t('saved', 'Saved')); setAdding(false); setName(''); load(); }
    catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div><CardTitle className="text-base">{t('admin_scales', 'Rating scales')}</CardTitle><p className="text-xs text-muted-foreground">{t('scales_help', 'The rating points reviews are scored on. Scales are versioned — a change creates a new version.')}</p></div>
        <Button size="sm" onClick={() => setAdding((v) => !v)}><Plus className="mr-1.5 h-4 w-4" />{t('new_scale', 'New scale')}</Button>
      </div>
      {adding && (
        <Card><CardContent className="pt-4 space-y-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('scale_name', 'Scale name (e.g. 5-point)')} />
          {opts.map((o, i) => (
            <div key={i} className="flex gap-2 items-center">
              <Input type="number" value={o.value} onChange={(e) => setOpts(opts.map((x, j) => j === i ? { ...x, value: Number(e.target.value) } : x))} className="w-20" />
              <Input value={o.label} onChange={(e) => setOpts(opts.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} placeholder={t('point_label', 'Label (e.g. Meets)')} className="flex-1" />
              <button onClick={() => setOpts(opts.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setOpts([...opts, { value: opts.length + 1, label: '', ordinal: opts.length + 1 }])}>{t('add_point', 'Add point')}</Button>
            <Button size="sm" onClick={save} disabled={busy}>{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{t('save', 'Save scale')}</Button>
          </div>
        </CardContent></Card>
      )}
      {scales.map((s) => (
        <Card key={s.id || s.name}><CardContent className="pt-3.5">
          <div className="flex items-center gap-2"><span className="font-medium text-foreground">{s.name}</span>{s.is_default && <Badge className="bg-success-soft text-success-strong border-0 text-[10px]">{t('default', 'Default')}</Badge>}{s.version ? <span className="text-xs text-muted-foreground">v{s.version}</span> : null}</div>
          <div className="flex flex-wrap gap-1.5 mt-1.5">{s.options.map((o) => <span key={o.ordinal} className="text-xs rounded-full bg-muted px-2 py-0.5">{o.value} · {o.label}</span>)}</div>
        </CardContent></Card>
      ))}
    </div>
  );
};

const MISSING_HANDLING = ['redistribute', 'zero', 'exclude'];
const FormulaAdmin: React.FC = () => {
  const { t } = useTranslation('performance');
  const [f, setF] = useState<api.ScoringFormula | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.getScoringFormula().then(setF).catch(() => setF(null)); }, []);
  if (!f) return <Loading />;
  const total = f.components.reduce((s, c) => s + (c.weight || 0), 0);
  const setW = (i: number, w: number) => setF({ ...f, components: f.components.map((c, j) => j === i ? { ...c, weight: w } : c) });
  const save = async () => {
    if (total !== 100) { toast.error(t('weights_100', 'Component weights must total 100%')); return; }
    setBusy(true);
    try { const r = await api.saveScoringFormula(f); setF(r); toast.success(t('saved', 'Saved')); }
    catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } finally { setBusy(false); }
  };
  const sel = 'h-9 rounded-md border border-border bg-background px-2.5 text-sm';
  return (
    <div className="space-y-3">
      <div><CardTitle className="text-base">{t('admin_formula', 'Scoring formula')}</CardTitle><p className="text-xs text-muted-foreground">{t('formula_help', 'How a review’s components roll up into one weighted score. Weights must total 100%.')}</p></div>
      <Card><CardContent className="pt-4 space-y-2">
        {f.components.map((c, i) => (
          <div key={c.key} className="flex items-center gap-3">
            <span className="flex-1 text-sm">{c.label}</span>
            <Input type="number" value={c.weight} onChange={(e) => setW(i, Number(e.target.value))} className="w-24" />
            <span className="text-xs text-muted-foreground w-4">%</span>
          </div>
        ))}
        <div className={`text-xs font-medium ${total === 100 ? 'text-success' : 'text-warning'}`}>{t('total', 'Total')}: {total}% {total === 100 ? '✓' : t('must_total_100', 'must total 100%')}</div>
        <div className="flex flex-wrap items-center gap-3 border-t border-border pt-2">
          <div><Label className="text-xs">{t('rounding', 'Rounding (dp)')}</Label><Input type="number" min={0} max={4} value={f.rounding_dp} onChange={(e) => setF({ ...f, rounding_dp: Number(e.target.value) })} className="w-20" /></div>
          <div><Label className="text-xs">{t('missing_handling', 'Missing components')}</Label>
            <select value={f.missing_handling} onChange={(e) => setF({ ...f, missing_handling: e.target.value as any })} className={sel}>{MISSING_HANDLING.map((m) => <option key={m} value={m}>{t(`missing_${m}`, m)}</option>)}</select></div>
          <Button size="sm" className="ml-auto self-end" onClick={save} disabled={busy}>{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{t('save', 'Save')}</Button>
        </div>
      </CardContent></Card>
    </div>
  );
};

const TemplatesAdmin: React.FC = () => {
  const { t } = useTranslation('performance');
  const [rows, setRows] = useState<api.Template[]>([]);
  const [edit, setEdit] = useState<api.Template | 'new' | null>(null);
  const load = () => api.listTemplates().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const del = async (id: string) => { try { await api.deleteTemplate(id); load(); } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } };
  const asList = (s: any): string[] => Array.isArray(s) ? s.map((x) => typeof x === 'string' ? x : (x?.title || x?.name || JSON.stringify(x))) : [];
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div><CardTitle className="text-base">{t('admin_templates', 'Review templates')}</CardTitle><p className="text-xs text-muted-foreground">{t('templates_help', 'The sections a review form is built from.')}</p></div>
        <Button size="sm" onClick={() => setEdit('new')}><Plus className="mr-1.5 h-4 w-4" />{t('new_template', 'New template')}</Button>
      </div>
      {rows.length === 0 ? <SectionEmpty text={t('no_templates', 'No templates yet. Create one to structure your review forms.')} cta={{ label: t('new_template', 'New template'), onClick: () => setEdit('new') }} /> : rows.map((tpl) => (
        <Card key={tpl.id}><CardContent className="pt-3.5 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-medium text-foreground flex items-center gap-2">{tpl.name}{tpl.is_default && <Badge className="bg-success-soft text-success-strong border-0 text-[10px]">{t('default', 'Default')}</Badge>}{(tpl.version ?? 1) > 1 && <Badge variant="outline" className="text-[10px]">v{tpl.version}</Badge>}</div>
            <div className="text-xs text-muted-foreground">{asList(tpl.sections).join(' · ') || '—'}</div>
            <div className="text-[11px] text-muted-foreground/80 mt-0.5">{t('template_versioning_hint', 'Editing the sections saves a new version; older versions are kept for running cycles.')}</div>
          </div>
          <div className="flex gap-1.5 shrink-0">
            <Button size="sm" variant="outline" onClick={() => setEdit(tpl)}>{t('edit', 'Edit')}</Button>
            <button onClick={() => del(tpl.id)} className="text-muted-foreground hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
          </div>
        </CardContent></Card>
      ))}
      {edit && <TemplateEditDialog tpl={edit === 'new' ? null : edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
};

const TemplateEditDialog: React.FC<{ tpl: api.Template | null; onClose: () => void; onSaved: () => void }> = ({ tpl, onClose, onSaved }) => {
  const { t } = useTranslation('performance');
  const asList = (s: any): string[] => Array.isArray(s) ? s.map((x) => typeof x === 'string' ? x : (x?.title || x?.name || '')) : [];
  const [name, setName] = useState(tpl?.name || '');
  const [sections, setSections] = useState((tpl ? asList(tpl.sections) : ['Achievements', 'Goal Progress', 'Strengths', 'Development Areas', 'Summary']).join('\n'));
  const [isDefault, setIsDefault] = useState(!!tpl?.is_default);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim()) { toast.error(t('name_required', 'Name is required')); return; }
    setBusy(true);
    const body = { name: name.trim(), sections: sections.split('\n').map((s) => s.trim()).filter(Boolean), is_default: isDefault };
    try { tpl ? await api.updateTemplate(tpl.id, body) : await api.createTemplate(body); toast.success(t('saved', 'Saved')); onSaved(); }
    catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{tpl ? t('edit_template', 'Edit template') : t('new_template', 'New template')}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div><Label className="text-xs">{t('template_name', 'Template name')}</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div><Label className="text-xs">{t('sections', 'Sections')} <span className="text-muted-foreground">({t('one_per_line', 'one per line')})</span></Label>
            <textarea value={sections} onChange={(e) => setSections(e.target.value)} className="w-full min-h-[120px] rounded-md border border-border bg-background px-2.5 py-2 text-sm" /></div>
          <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} className="h-4 w-4 accent-orange-500" />{t('make_default', 'Make this the default')}</label>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>{t('cancel', 'Cancel')}</Button><Button onClick={save} disabled={busy}>{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{t('save', 'Save')}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SettingsAdmin: React.FC = () => {
  const { t } = useTranslation('performance');
  const [scale, setScale] = useState<api.RatingScale | null>(null);
  const [formula, setFormula] = useState<api.ScoringFormula | null>(null);
  const [tplCount, setTplCount] = useState<number | null>(null);
  const [managerSelfRelease, setManagerSelfRelease] = useState(false);
  const [savingMSR, setSavingMSR] = useState(false);
  useEffect(() => {
    api.getRatingScales().then((s) => setScale(s.find((x) => x.is_default) || s[0] || null)).catch(() => {});
    api.getScoringFormula().then(setFormula).catch(() => {});
    api.listTemplates().then((r) => setTplCount(r.length)).catch(() => setTplCount(0));
    api.getPerfSettings().then((s) => setManagerSelfRelease(!!s.manager_self_release)).catch(() => {});
  }, []);
  const toggleMSR = async (next: boolean) => {
    setManagerSelfRelease(next); // optimistic
    setSavingMSR(true);
    try {
      await api.updatePerfSettings({ manager_self_release: next });
      toast.success(t('saved', 'Saved'));
    } catch (e: any) {
      setManagerSelfRelease(!next); // revert
      toast.error(getApiErrorMessage(e, t('err', 'Something went wrong')));
    } finally { setSavingMSR(false); }
  };
  return (
    <div className="space-y-3">
      <div><CardTitle className="text-base">{t('admin_settings', 'Settings')}</CardTitle><p className="text-xs text-muted-foreground">{t('settings_help', 'The active configuration used by reviews in this company.')}</p></div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Metric label={t('default_scale', 'Default rating scale')} value={<span className="text-sm">{scale ? scale.name : '—'}</span>} sub={scale ? `${scale.options.length} ${t('points', 'points')}` : undefined} />
        <Metric label={t('scoring_formula', 'Scoring formula')} value={<span className="text-sm">{formula ? `${formula.components.length} ${t('components', 'components')}` : '—'}</span>} sub={formula ? `${formula.rounding_dp}dp · ${formula.missing_handling}` : undefined} />
        <Metric label={t('review_templates', 'Review templates')} value={tplCount ?? '—'} />
      </div>
      {/* Release policy: who can make a finalized review visible to the employee. */}
      <label className="flex items-start gap-3 rounded-lg border border-border p-3 cursor-pointer">
        <input type="checkbox" className="mt-0.5 h-4 w-4 accent-orange-500" checked={managerSelfRelease}
          disabled={savingMSR} onChange={(e) => toggleMSR(e.target.checked)} />
        <span>
          <span className="block text-sm font-medium text-foreground">{t('msr_title', 'Let managers release their own reports’ reviews')}</span>
          <span className="block text-xs text-muted-foreground">{t('msr_help', 'When on, a manager can finalize and release a direct report’s review in one step, without waiting for HR. When off (default), only HR releases reviews — use this if you calibrate before releasing.')}</span>
        </span>
      </label>
      <p className="text-xs text-muted-foreground">{t('settings_note', 'Payroll linkage for compensation is set per cycle on the Compensation tab.')}</p>
    </div>
  );
};

// ---- shared -----------------------------------------------------------------
const Loading = () => <div className="flex items-center gap-2 text-muted-foreground p-8"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
const Empty: React.FC<{ text: string }> = ({ text }) => <div className="text-sm text-muted-foreground py-8 text-center">{text}</div>;

export default PerformancePage;


// --- Phase 1 admin: goal categories -----------------------------------------
const CategoriesAdmin: React.FC = () => {
  const { t } = useTranslation('performance');
  const [rows, setRows] = useState<api.GoalCategory[]>([]);
  const [draft, setDraft] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.listGoalCategories().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const save = async () => {
    setBusy(true);
    try {
      if (draft.id) await api.updateGoalCategory(draft.id, draft);
      else await api.createGoalCategory({ ...draft, key: (draft.key || draft.label || '').toLowerCase().replace(/[^a-z0-9]+/g, '_') });
      toast.success(t('saved', 'Saved')); setDraft(null); load();
    } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); }
    finally { setBusy(false); }
  };
  const purpose = (c: api.GoalCategory) => c.bonus_eligible && c.counts_toward_score ? t('cat_p_both', 'Score + bonus')
    : c.counts_toward_score ? t('cat_p_score', 'Score only') : c.bonus_eligible ? t('cat_p_bonus', 'Bonus only') : t('cat_p_dev', 'Development only');
  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div><CardTitle className="text-base">{t('admin_categories', 'Goal categories')}</CardTitle>
          <p className="text-xs text-muted-foreground">{t('categories_help', 'What a goal means commercially: whether it feeds the review score and/or the bonus. Development goals never move money.')}</p></div>
        <Button size="sm" onClick={() => setDraft({ label: '', counts_toward_score: true, bonus_eligible: false })}><Plus className="mr-1.5 h-4 w-4" />{t('new_category', 'New category')}</Button>
      </div>
      {rows.map((c) => (
        <Card key={c.id}><CardContent className="pt-3.5 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-medium text-foreground flex items-center gap-2">{c.label}
              {!c.tenant_id && <Badge variant="outline" className="text-[10px]">{t('kazipeople_default', 'KaziPeople default')}</Badge>}
              <Badge className={`border-0 text-[10px] ${c.bonus_eligible ? 'bg-success-soft text-success-strong' : 'bg-muted text-muted-foreground'}`}>{purpose(c)}</Badge>
            </div>
            {c.description && <div className="text-xs text-muted-foreground">{c.description}</div>}
          </div>
          {c.tenant_id && <div className="flex gap-1.5 shrink-0">
            <Button size="sm" variant="outline" onClick={() => setDraft({ ...c })}>{t('edit', 'Edit')}</Button>
            <button onClick={async () => { try { await api.deleteGoalCategory(c.id); load(); } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); } }} className="text-muted-foreground hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
          </div>}
        </CardContent></Card>
      ))}
      {draft && (
        <Card><CardContent className="pt-4 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">{t('label', 'Label')}</Label><Input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} /></div>
            <div className="space-y-1"><Label className="text-xs">{t('description', 'Description')}</Label><Input value={draft.description || ''} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></div>
          </div>
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" className="accent-orange-500" checked={!!draft.counts_toward_score} onChange={(e) => setDraft({ ...draft, counts_toward_score: e.target.checked })} />{t('cat_score_flag', 'Counts toward review score')}</label>
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" className="accent-orange-500" checked={!!draft.bonus_eligible} onChange={(e) => setDraft({ ...draft, bonus_eligible: e.target.checked })} />{t('cat_bonus_flag', 'Bonus-eligible')}</label>
          </div>
          <div className="flex gap-2 justify-end"><Button variant="outline" size="sm" onClick={() => setDraft(null)}>{t('cancel', 'Cancel')}</Button><Button size="sm" onClick={save} disabled={busy || !draft.label}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t('save', 'Save')}</Button></div>
        </CardContent></Card>
      )}
    </div>
  );
};

// Recognition Values — the tenant-configurable "What for?" catalogue shown when
// giving recognition. Reuses the recognition_category storage; up to 12 active.
const RecognitionValuesAdmin: React.FC = () => {
  const { t } = useTranslation('performance');
  const [rows, setRows] = useState<api.RecognitionValue[]>([]);
  const [draft, setDraft] = useState<{ id?: string; label: string; description?: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const load = () => api.listRecognitionValues().then(setRows).catch(() => setRows([])).finally(() => setLoaded(true));
  useEffect(() => { load(); }, []);

  const live = rows.filter((v) => !v.archived);
  const archived = rows.filter((v) => v.archived);
  const activeCount = rows.filter((v) => v.active && !v.archived).length;
  const atLimit = activeCount >= api.MAX_ACTIVE_RECOGNITION_VALUES;

  const guard = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try { await fn(); load(); }
    catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); }
    finally { setBusy(false); }
  };
  const save = () => guard(async () => {
    if (draft!.id) await api.updateRecognitionValue(draft!.id, { label: draft!.label, description: draft!.description });
    else await api.createRecognitionValue({ label: draft!.label.trim(), description: draft!.description });
    toast.success(t('saved', 'Saved')); setDraft(null);
  });
  const setActive = (v: api.RecognitionValue, active: boolean) =>
    guard(() => api.updateRecognitionValue(v.id, { active }));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= live.length) return;
    const order = live.map((v) => v.id);
    [order[i], order[j]] = [order[j], order[i]];
    guard(() => api.reorderRecognitionValues(order));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <CardTitle className="text-base">{t('admin_recognition_values', 'Recognition values')}</CardTitle>
          <p className="text-xs text-muted-foreground">{t('recognition_values_help', 'Define the values employees can recognize each other for. These appear when giving recognition.')}</p>
          {loaded && <p className="mt-1 text-xs font-medium text-muted-foreground">{t('recognition_values_count', '{{active}} of {{max}} active values', { active: activeCount, max: api.MAX_ACTIVE_RECOGNITION_VALUES })}</p>}
        </div>
        <Button size="sm" disabled={busy} onClick={() => setDraft({ label: '', description: '' })}>
          <Plus className="mr-1.5 h-4 w-4" />{t('new_recognition_value', 'Add value')}
        </Button>
      </div>

      {loaded && live.length === 0 && !draft && (
        <Card><CardContent className="pt-4 text-sm text-muted-foreground">
          {t('recognition_values_empty', 'No active recognition values. Add or restore a value so employees can give recognition.')}
        </CardContent></Card>
      )}

      {live.map((v, i) => (
        <Card key={v.id}><CardContent className="pt-3.5 flex items-center gap-3">
          <div className="flex flex-col shrink-0">
            <button aria-label={t('move_up', 'Move up')} disabled={busy || i === 0} onClick={() => move(i, -1)}
              className="text-muted-foreground hover:text-foreground disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
            <button aria-label={t('move_down', 'Move down')} disabled={busy || i === live.length - 1} onClick={() => move(i, 1)}
              className="text-muted-foreground hover:text-foreground disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-medium text-foreground flex items-center gap-2">{v.label}
              <Badge className={`border-0 text-[10px] ${v.active ? 'bg-success-soft text-success-strong' : 'bg-muted text-muted-foreground'}`}>{v.active ? t('active', 'Active') : t('inactive', 'Inactive')}</Badge>
            </div>
            {v.description && <div className="text-xs text-muted-foreground">{v.description}</div>}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setDraft({ id: v.id, label: v.label, description: v.description || '' })}>{t('edit', 'Edit')}</Button>
            <Button size="sm" variant="outline" disabled={busy || (!v.active && atLimit)} onClick={() => setActive(v, !v.active)}>
              {v.active ? t('deactivate', 'Deactivate') : t('activate', 'Activate')}
            </Button>
            <button aria-label={t('archive', 'Archive')} title={t('archive', 'Archive')} disabled={busy} onClick={() => guard(() => api.archiveRecognitionValue(v.id))}
              className="text-muted-foreground hover:text-foreground disabled:opacity-40"><Archive className="h-4 w-4" /></button>
            <button aria-label={t('delete', 'Delete')} title={v.in_use ? t('recognition_value_used', 'Used in recognition — archive instead of deleting') : t('delete', 'Delete')}
              disabled={busy || v.in_use} onClick={() => guard(() => api.deleteRecognitionValue(v.id))}
              className="text-muted-foreground hover:text-red-500 disabled:opacity-30 disabled:hover:text-muted-foreground"><Trash2 className="h-4 w-4" /></button>
          </div>
        </CardContent></Card>
      ))}

      {draft && (
        <Card><CardContent className="pt-4 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">{t('label', 'Name')}</Label>
              <Input value={draft.label} maxLength={60} onChange={(e) => setDraft({ ...draft, label: e.target.value })} placeholder={t('recognition_value_name_ph', 'e.g. Customer First')} /></div>
            <div className="space-y-1"><Label className="text-xs">{t('description', 'Description')}</Label>
              <Input value={draft.description || ''} maxLength={200} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder={t('recognition_value_desc_ph', 'Optional — what this value means')} /></div>
          </div>
          {!draft.id && atLimit && <p className="text-xs text-orange-600 dark:text-orange-400">{t('recognition_values_max', 'You can have up to 12 active recognition values. Deactivate or archive one before adding another.')}</p>}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => setDraft(null)}>{t('cancel', 'Cancel')}</Button>
            <Button size="sm" onClick={save} disabled={busy || !draft.label.trim() || (!draft.id && atLimit)}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t('save', 'Save')}</Button>
          </div>
        </CardContent></Card>
      )}

      {archived.length > 0 && (
        <div className="pt-2">
          <div className="text-xs font-semibold text-muted-foreground mb-2">{t('archived', 'Archived')}</div>
          <div className="space-y-2">
            {archived.map((v) => (
              <Card key={v.id}><CardContent className="pt-3 flex items-center justify-between gap-3">
                <div className="min-w-0"><div className="text-sm text-muted-foreground truncate">{v.label}</div></div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => guard(() => api.restoreRecognitionValue(v.id))}>
                    <ArchiveRestore className="mr-1.5 h-3.5 w-3.5" />{t('restore', 'Restore')}
                  </Button>
                  {!v.in_use && <button aria-label={t('delete', 'Delete')} disabled={busy} onClick={() => guard(() => api.deleteRecognitionValue(v.id))}
                    className="text-muted-foreground hover:text-red-500 disabled:opacity-30"><Trash2 className="h-4 w-4" /></button>}
                </div>
              </CardContent></Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// --- Phase 1 admin: framework bundles ---------------------------------------
const FrameworksAdmin: React.FC = () => {
  const { t } = useTranslation('performance');
  const [rows, setRows] = useState<api.PerfFramework[]>([]);
  const [scales, setScales] = useState<any[]>([]);
  const [templates, setTemplates] = useState<api.Template[]>([]);
  const [draft, setDraft] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.listFrameworks().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); api.getRatingScales().then(setScales).catch(() => {}); api.listTemplates().then(setTemplates).catch(() => {}); }, []);
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...draft, weight_policy: { require_sum_100: true, enforce_at: draft.bonus_linked ? 'launch' : (draft.enforce_at || 'finalize') } };
      if (draft.id) await api.updateFramework(draft.id, body);
      else await api.createFramework(body);
      toast.success(t('saved', 'Saved')); setDraft(null); load();
    } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); }
    finally { setBusy(false); }
  };
  const sel = 'w-full h-9 rounded-md border border-border bg-background px-2 text-sm';
  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div><CardTitle className="text-base">{t('admin_frameworks', 'Performance frameworks')}</CardTitle>
          <p className="text-xs text-muted-foreground">{t('frameworks_help', 'A named bundle of scale, formula, template and weight rules. Launching a cycle freezes its framework so running cycles never change under it.')}</p></div>
        <Button size="sm" onClick={() => setDraft({ name: '', bonus_linked: false, enforce_at: 'finalize', review_defaults: {} })}><Plus className="mr-1.5 h-4 w-4" />{t('new_framework', 'New framework')}</Button>
      </div>
      {rows.map((fw) => (
        <Card key={fw.id}><CardContent className="pt-3.5 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-medium text-foreground flex items-center gap-2">{fw.name}
              {!fw.tenant_id && <Badge variant="outline" className="text-[10px]">{t('kazipeople_default', 'KaziPeople default')}</Badge>}
              {fw.bonus_linked && <Badge className="bg-success-soft text-success-strong border-0 text-[10px]">{t('bonus_linked', 'bonus-linked')}</Badge>}
            </div>
            <div className="text-xs text-muted-foreground">{fw.description || ''} · {t('weights', 'Weights')}: {(fw.weight_policy?.enforce_at === 'launch' || fw.bonus_linked) ? t('wp_launch', '100% required at launch') : t('wp_finalize', '100% checked at finalize')}</div>
          </div>
          <div className="flex gap-1.5 shrink-0">
            {fw.tenant_id
              ? <Button size="sm" variant="outline" onClick={() => setDraft({ ...fw, enforce_at: fw.weight_policy?.enforce_at || 'finalize' })}>{t('edit', 'Edit')}</Button>
              : <Button size="sm" variant="outline" onClick={() => setDraft({ name: `${fw.name} (copy)`, description: fw.description, bonus_linked: fw.bonus_linked, enforce_at: fw.weight_policy?.enforce_at || 'finalize', scale_id: fw.scale_id, formula_id: fw.formula_id, template_id: fw.template_id, review_defaults: fw.review_defaults })}>{t('copy', 'Copy')}</Button>}
          </div>
        </CardContent></Card>
      ))}
      {draft && (
        <Card><CardContent className="pt-4 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">{t('name', 'Name')}</Label><Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></div>
            <div className="space-y-1"><Label className="text-xs">{t('description', 'Description')}</Label><Input value={draft.description || ''} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></div>
            <div className="space-y-1"><Label className="text-xs">{t('rating_scale', 'Rating scale')}</Label>
              <select className={sel} value={draft.scale_id || ''} onChange={(e) => setDraft({ ...draft, scale_id: e.target.value || null })}>
                <option value="">{t('builtin_default', 'Built-in default')}</option>
                {scales.map((sc: any) => <option key={sc.id} value={sc.id}>{sc.name} v{sc.version}</option>)}
              </select></div>
            <div className="space-y-1"><Label className="text-xs">{t('template', 'Review template')}</Label>
              <select className={sel} value={draft.template_id || ''} onChange={(e) => setDraft({ ...draft, template_id: e.target.value || null })}>
                <option value="">{t('tenant_default', 'Tenant default')}</option>
                {templates.map((tp) => <option key={tp.id} value={tp.id}>{tp.name}{(tp.version ?? 1) > 1 ? ` v${tp.version}` : ''}</option>)}
              </select></div>
          </div>
          <div className="flex flex-wrap gap-4 text-sm items-center">
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" className="accent-orange-500" checked={!!draft.bonus_linked} onChange={(e) => setDraft({ ...draft, bonus_linked: e.target.checked })} />{t('fw_bonus_flag', 'Bonus-linked (forces 100% weights at launch)')}</label>
            {!draft.bonus_linked && (
              <label className="flex items-center gap-2">{t('wp_enforce', 'Enforce 100% weights at')}
                <select className="h-8 rounded-md border border-border bg-background px-2 text-sm" value={draft.enforce_at} onChange={(e) => setDraft({ ...draft, enforce_at: e.target.value })}>
                  <option value="finalize">{t('wp_at_finalize', 'finalize')}</option>
                  <option value="launch">{t('wp_at_launch', 'launch')}</option>
                </select>
              </label>
            )}
          </div>
          <div className="flex gap-2 justify-end"><Button variant="outline" size="sm" onClick={() => setDraft(null)}>{t('cancel', 'Cancel')}</Button><Button size="sm" onClick={save} disabled={busy || !draft.name}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t('save', 'Save')}</Button></div>
        </CardContent></Card>
      )}
    </div>
  );
};


// --- Phase 2 admin: target-bonus policies ------------------------------------
const BonusPoliciesAdmin: React.FC = () => {
  const { t } = useTranslation('performance');
  const [rows, setRows] = useState<api.BonusPolicy[]>([]);
  const [depts, setDepts] = useState<Array<{ id: string; name: string }>>([]);
  const [draft, setDraft] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.listBonusPolicies().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); getDepartments().then((r: any) => setDepts((r?.items || []).map((d: any) => ({ id: d.id, name: d.name })))).catch(() => {}); }, []);
  const save = async () => {
    setBusy(true);
    try {
      const body: any = {
        name: draft.name, method: draft.method,
        percent: draft.method === 'percent_of_salary' ? Number(draft.percent) : undefined,
        amount: draft.method === 'fixed_amount' ? Number(draft.amount) : undefined,
        department_id: draft.department_id || undefined,
        employment_type: draft.employment_type || undefined,
        career_level: draft.career_level || undefined,
        band_table: draft.method === 'band_table' && draft.bands ? draft.bands.split('\n').map((l: string) => { const [lv, pc] = l.split(':'); return { career_level: (lv || '').trim(), percent: Number(pc) || 0 }; }).filter((b: any) => b.career_level) : undefined,
        payout_curve: draft.curve === 'stepped' ? { type: 'stepped', steps: [{ min: 0, multiplier: 0 }, { min: Number(draft.threshold) || 80, multiplier: 0.5 }, { min: 100, multiplier: 1 }] } : undefined,
      };
      await api.createBonusPolicy(body);
      toast.success(t('saved_draft', 'Saved as draft — activate it to apply')); setDraft(null); load();
    } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); }
    finally { setBusy(false); }
  };
  const act = async (p: api.BonusPolicy, to: 'activate' | 'retire') => {
    try { to === 'activate' ? await api.activateBonusPolicy(p.id) : await api.retireBonusPolicy(p.id); load(); }
    catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); }
  };
  const sel = 'w-full h-9 rounded-md border border-border bg-background px-2 text-sm';
  const methodLabel = (m: string) => m === 'percent_of_salary' ? t('bp_pct', '% of salary') : m === 'fixed_amount' ? t('bp_fixed', 'Fixed amount') : m === 'band_table' ? t('bp_band', 'Career-level bands') : t('bp_manual', 'Manual only');
  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div><CardTitle className="text-base">{t('admin_bonus_policies', 'Target bonus policies')}</CardTitle>
          <p className="text-xs text-muted-foreground">{t('bonus_policies_help', 'How each person’s bonus opportunity is derived (e.g. 10% of salary). Resolving a cycle freezes the fired policy + eligible salary onto each participant; running cycles never change when you publish a new version.')}</p></div>
        <Button size="sm" onClick={() => setDraft({ name: '', method: 'percent_of_salary', percent: 10, curve: 'linear', threshold: 80 })}><Plus className="mr-1.5 h-4 w-4" />{t('new_policy', 'New policy')}</Button>
      </div>
      {rows.map((p) => (
        <Card key={p.id}><CardContent className="pt-3.5 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-medium text-foreground flex items-center gap-2">{p.name} <span className="text-xs text-muted-foreground">v{p.version}</span>
              {!p.tenant_id && <Badge variant="outline" className="text-[10px]">{t('kazipeople_default', 'KaziPeople default')}</Badge>}
              <Badge className={`border-0 text-[10px] ${p.status === 'active' ? 'bg-success-soft text-success-strong' : 'bg-muted text-muted-foreground'}`}>{p.status}</Badge>
            </div>
            <div className="text-xs text-muted-foreground">
              {methodLabel(p.method)}{p.method === 'percent_of_salary' && p.percent ? ` · ${p.percent}%` : ''}{p.method === 'fixed_amount' && p.amount ? ` · ${formatMoney(p.amount, p.currency)}` : ''}
              {p.department_id ? ` · ${t('dept_scoped', 'department-scoped')}` : ''}{p.career_level ? ` · ${p.career_level}` : ''}{p.employment_type ? ` · ${p.employment_type}` : ''}
              {p.payout_curve?.type === 'stepped' ? ` · ${t('stepped_curve', 'stepped payout curve')}` : ''}
            </div>
          </div>
          {p.tenant_id && <div className="flex gap-1.5 shrink-0">
            {p.status === 'draft' && <Button size="sm" onClick={() => act(p, 'activate')}>{t('activate', 'Activate')}</Button>}
            {p.status === 'active' && <Button size="sm" variant="outline" onClick={() => act(p, 'retire')}>{t('retire', 'Retire')}</Button>}
          </div>}
        </CardContent></Card>
      ))}
      {draft && (
        <Card><CardContent className="pt-4 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">{t('name', 'Name')}</Label><Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder={t('bp_name_ph', 'e.g. Company-wide 10%')} /></div>
            <div className="space-y-1"><Label className="text-xs">{t('bp_method', 'Method')}</Label>
              <select className={sel} value={draft.method} onChange={(e) => setDraft({ ...draft, method: e.target.value })}>
                <option value="percent_of_salary">{t('bp_pct', '% of salary')}</option>
                <option value="fixed_amount">{t('bp_fixed', 'Fixed amount')}</option>
                <option value="band_table">{t('bp_band', 'Career-level bands')}</option>
                <option value="manual_only">{t('bp_manual', 'Manual only')}</option>
              </select></div>
            {draft.method === 'percent_of_salary' && <div className="space-y-1"><Label className="text-xs">{t('bp_percent', 'Percent of annual salary')}</Label><Input type="number" value={draft.percent} onChange={(e) => setDraft({ ...draft, percent: e.target.value })} /></div>}
            {draft.method === 'fixed_amount' && <div className="space-y-1"><Label className="text-xs">{t('bp_amount', 'Annual amount (TZS)')}</Label><Input type="number" value={draft.amount || ''} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} /></div>}
            <div className="space-y-1"><Label className="text-xs">{t('bp_dept', 'Department (optional)')}</Label>
              <select className={sel} value={draft.department_id || ''} onChange={(e) => setDraft({ ...draft, department_id: e.target.value })}>
                <option value="">{t('any', 'Any')}</option>
                {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select></div>
            <div className="space-y-1"><Label className="text-xs">{t('bp_career', 'Career level (optional)')}</Label><Input value={draft.career_level || ''} onChange={(e) => setDraft({ ...draft, career_level: e.target.value })} placeholder="L4 / Band C" /></div>
          </div>
          {draft.method === 'band_table' && (
            <div className="space-y-1"><Label className="text-xs">{t('bp_bands', 'Bands — one per line as level:percent')}</Label>
              <textarea className="w-full rounded-md border border-border bg-background p-2 text-sm min-h-[80px]" value={draft.bands || ''} onChange={(e) => setDraft({ ...draft, bands: e.target.value })} placeholder={'L3:8\nL4:12\nL5:15'} /></div>
          )}
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <label className="flex items-center gap-2">{t('bp_curve', 'Payout curve')}
              <select className="h-8 rounded-md border border-border bg-background px-2 text-sm" value={draft.curve} onChange={(e) => setDraft({ ...draft, curve: e.target.value })}>
                <option value="linear">{t('bp_linear', 'Linear (attainment pays itself)')}</option>
                <option value="stepped">{t('bp_stepped', 'Stepped (below threshold pays 0, half to 100%, full above)')}</option>
              </select>
            </label>
            {draft.curve === 'stepped' && <label className="flex items-center gap-2">{t('bp_threshold', 'Threshold %')}<Input type="number" className="h-8 w-20" value={draft.threshold} onChange={(e) => setDraft({ ...draft, threshold: e.target.value })} /></label>}
          </div>
          <div className="flex gap-2 justify-end"><Button variant="outline" size="sm" onClick={() => setDraft(null)}>{t('cancel', 'Cancel')}</Button><Button size="sm" onClick={save} disabled={busy || !draft.name}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t('save_draft', 'Save draft')}</Button></div>
        </CardContent></Card>
      )}
    </div>
  );
};


// --- Phase 3: bonus pools card ------------------------------------------------
const PoolsCard: React.FC<{ cycleId: string; onProposed: () => void }> = ({ cycleId, onProposed }) => {
  const { t } = useTranslation('performance');
  const [pools, setPools] = useState<api.BonusPool[]>([]);
  const [draft, setDraft] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.listBonusPools(cycleId).then(setPools).catch(() => setPools([]));
  useEffect(() => { load(); }, [cycleId]);
  return (
    <Card><CardContent className="pt-4 space-y-2.5">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-medium text-sm text-foreground">{t('bonus_pools', 'Bonus pools')}</div>
          <p className="text-[11px] text-muted-foreground">{t('pools_help', 'A pool constrains money — it never changes performance. Proposing scales everyone’s computed bonus down pro-rata when the pool can’t fund the total; every proposal still needs a different approver.')}</p>
        </div>
        {!draft && <Button size="sm" variant="outline" onClick={() => setDraft({ name: '', funding_amount: '' })}>{t('new_pool', 'New pool')}</Button>}
      </div>
      {pools.map((p) => (
        <div key={p.id} className="rounded-md border border-border p-2.5 flex flex-wrap items-center justify-between gap-2 text-sm">
          <div className="min-w-0">
            <span className="font-medium">{p.name}</span>
            <span className="text-xs text-muted-foreground"> · {t('funding', 'funding')} {formatMoney(p.funding_amount, p.currency)} · {t('proposed', 'proposed')} {formatMoney(p.proposed_total, p.currency)} · {t('approved', 'approved')} {formatMoney(p.approved_total, p.currency)}</span>
          </div>
          <Button size="sm" disabled={busy || p.status === 'closed'} onClick={async () => {
            setBusy(true);
            try {
              const r = await api.proposeFromPool(p.id);
              toast.success(t('pool_proposed', '{{n}} proposal(s) created · scale ×{{s}}', { n: r.proposed, s: r.scale_factor } as any));
              load(); onProposed();
            } catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); }
            finally { setBusy(false); }
          }}>{t('propose_from_pool', 'Propose from pool')}</Button>
        </div>
      ))}
      {draft && (
        <div className="rounded-md border border-border p-2.5 flex flex-wrap items-end gap-2">
          <div className="space-y-1"><Label className="text-xs">{t('name', 'Name')}</Label><Input className="h-8" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></div>
          <div className="space-y-1"><Label className="text-xs">{t('funding_amount', 'Funding (TZS)')}</Label><Input className="h-8 w-36" type="number" value={draft.funding_amount} onChange={(e) => setDraft({ ...draft, funding_amount: e.target.value })} /></div>
          <Button size="sm" disabled={busy || !draft.name || !Number(draft.funding_amount)} onClick={async () => {
            setBusy(true);
            try { await api.createBonusPool(cycleId, { name: draft.name, funding_amount: Number(draft.funding_amount) }); setDraft(null); load(); }
            catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); }
            finally { setBusy(false); }
          }}>{t('create', 'Create')}</Button>
          <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>{t('cancel', 'Cancel')}</Button>
        </div>
      )}
    </CardContent></Card>
  );
};

// --- Phase 3: merit proposals awaiting payroll confirmation (D-6) -------------
const MeritProposalsCard: React.FC = () => {
  const { t } = useTranslation('performance');
  const [items, setItems] = useState<api.MeritProposal[]>([]);
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);
  const load = () => api.listMeritProposals().then(setItems).catch((e: any) => { if (e?.response?.status === 403) setDenied(true); setItems([]); });
  useEffect(() => { load(); }, []);
  const pending = items.filter((m) => m.status === 'pending');
  if (denied || pending.length === 0) return null;
  return (
    <Card><CardContent className="pt-4 space-y-2.5">
      <div>
        <div className="font-medium text-sm text-foreground">{t('merit_proposals', 'Merit increases awaiting payroll confirmation')}</div>
        <p className="text-[11px] text-muted-foreground">{t('merit_help', 'Approved merit lands as a PROPOSED salary revision — payroll confirms or rejects it; nothing changes automatically.')}</p>
      </div>
      {pending.map((m) => (
        <div key={m.id} className="rounded-md border border-border p-2.5 flex flex-wrap items-center justify-between gap-2 text-sm">
          <div className="min-w-0 text-xs text-muted-foreground">
            <span className="text-sm font-medium text-foreground">{m.percent != null ? `+${m.percent}%` : m.amount != null ? `+${formatMoney(m.amount, m.currency || 'TZS')}` : '—'}</span>
            {m.current_base ? <> · {t('from', 'from')} {formatMoney(Number(m.current_base), m.currency || 'TZS')} → {formatMoney(Number(m.proposed_base || 0), m.currency || 'TZS')}/mo</> : null}
            {' · '}{t('effective', 'effective')} {fmtDate(m.effective_from)}
          </div>
          <div className="flex gap-1.5">
            <Button size="sm" variant="ghost" disabled={busy} onClick={async () => { setBusy(true); try { await api.decideMeritProposal(m.id, false); load(); } finally { setBusy(false); } }}>{t('reject', 'Reject')}</Button>
            <Button size="sm" className="bg-green-600 hover:bg-green-700" disabled={busy} onClick={async () => {
              setBusy(true);
              try { await api.decideMeritProposal(m.id, true); toast.success(t('merit_confirmed', 'Salary revision applied (effective-dated)')); load(); }
              catch (e: any) { toast.error(getApiErrorMessage(e, t('err', 'Something went wrong'))); }
              finally { setBusy(false); }
            }}>{t('confirm', 'Confirm')}</Button>
          </div>
        </div>
      ))}
    </CardContent></Card>
  );
};
